import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle as ViewToggleControl } from '@/components/ui/ViewToggle';
/*
 * The contacts manager (owner 2026-10-16: "convert [the email button] to a full
 * contacts edit and manage modal"). It replaced `CompanyContactsMailModal` on
 * this page; the per-contact email button lives inside it. The people glyph is
 * the shared catalog one, so the table lane and the phone card cannot drift.
 */
import { CompanyContactsManagerModal } from '@/components/companies/contacts-manager/CompanyContactsManagerModal';
import { UsersIcon } from '@/components/catalog/icons';
/*
 * The account-wide LAST NOTE — anyone's human note on the company or on its
 * deals, quotes and invoices, served on every list row as `accountLastNote`.
 *
 * ⚠️ **NOT `latestNote`, which is VIEWER-SCOPED** (the reader's own notes) and
 * is still served beside it. The two answer different questions; the cells and
 * the headings come from one module so the wording cannot drift between this
 * page and the UP dashboard, which renders the same two columns.
 */
import {
  AccountLastNoteCell,
  AccountLastNoteDateCell,
} from '@/components/companies/AccountLastNoteCell';
import {
  ACCOUNT_LAST_NOTE_AT_LABEL,
  ACCOUNT_LAST_NOTE_LABEL,
} from '@/components/companies/account-last-note';
/*
 * The account-wide LATEST TASK and the date it was added — the twins of the
 * last-note pair above, served on every list row as `accountLastTask`. Both
 * cells open the page's one tasks-and-notes sheet for the company.
 */
import {
  AccountLastTaskCell,
  AccountLastTaskDateCell,
  OpenTasksButton,
} from '@/components/companies/AccountLastTaskCell';
import {
  ACCOUNT_LAST_TASK_AT_LABEL,
  ACCOUNT_LAST_TASK_LABEL,
} from '@/components/companies/account-last-task';
import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
/*
 * The two name labels (owner 2026-10-25): Company name is the display name;
 * Billing name is the Xero Contact Name. One spelling across the app.
 */
import { BILLING_NAME_LABEL, COMPANY_NAME_LABEL } from '@/lib/company-names';
import { OpenNotesButton } from '@/components/companies/OpenNotesButton';
import {
  ApprovalStatusBadge,
  ResendApprovalControl,
} from '@/components/companies/ResendApprovalControl';
import {
  InlineRegionsField,
  type InlineSetSaveResult,
} from '@/components/companies/InlineRegionsField';
import {
  refusedRegionIds,
  // Aliased: this page's own local `regionOptions` builds the Regions FUNNEL's
  // list (includes retired regions on purpose — a value on screen you can't
  // filter for is a silent gap), while this one builds the PICKER's, where a
  // retired row is only ever the value already held. The two must never swap.
  regionOptions as regionPickerOptions,
  retiredRegionNames,
} from '@/components/companies/company-master-options';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
/*
 * Account lead history (owner 2026-10-25): a lead change asks "Effective from"
 * first. One mount per page; see `requestStaffChange`.
 */
import { AccountLeadChangeDialog } from '@/components/companies/AccountLeadChangeDialog';
import { accountLeadErrorMessage } from '@/lib/account-lead-history';
/*
 * A separate statement from the `RecordCreateMenu` import below — a different
 * module, even though the two are used together at all three "+" mounts.
 */
import {
  CompanyProjectLinesFlow,
  type CompanyProjectLinesRequest,
} from '@/components/records/CompanyProjectLinesFlow';
import {
  COMPANY_CREATE_TARGETS,
  type CompanyCreateTarget,
  CreateForCompanyMenu,
} from '@/components/records/RecordCreateMenu';
import { ActivityDrawer, ActivityIcon } from '@/components/tasks/ActivityDrawer';
// Not re-exported by the `@/components/ui` barrel, same as `ProjectInlineCells`
// imports it — the primitive is reached by path.
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { ApiError, api } from '@/lib/api';
import { saveBlob } from '@/lib/download';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
import {
  useColumnLayout,
  useFrozenOffsets,
  type ColumnLayoutController,
} from '@/lib/use-column-layout';
import {
  companyStageLabel,
  companyStageOptions,
  useCompanyStages,
} from '@/lib/use-company-stages';
/*
 * The Stage status master (owner 2026-10-23): statuses hang off a STAGE, so a
 * picker offers only the active statuses of the row's current stage. A stage
 * change clears the status server-side; never match a status by name.
 */
import { useCompanyStageStatuses } from '@/lib/use-company-stage-statuses';
import { useStageStatusChange } from '@/lib/use-stage-status-change';
/*
 * Per-stage STATUS columns (owner 2026-10-25): one `{Stage} status` column per
 * active stage with active statuses, ids `stageStatus:<stageId>`. They replaced
 * the single Stage status column; only the company's OWN stage's column is
 * editable, and every column's funnel edits its slice of the one shared
 * `?stageStatusId=` list.
 */
import {
  buildStageStatusColumns,
  columnSelection,
  legacyHiddenStageStatusIds,
  parseStageStatusColumnId,
  replaceColumnSelection,
  type StageStatusColumn,
} from '@/lib/stage-status-columns';
import { StageStatusColumnCell } from '@/components/companies/StageStatusColumnCell';
/*
 * The company-source master — where an account came from.
 *
 * ⚠️ **The same three names, and the same split, as `use-company-stages`
 * directly above**, because it is the same shape of column: a nullable
 * `MasterRef` on the row, a foreign key on the write, one master read per page.
 * `companySourceOptions` is the PICKER's list (active rows plus whatever this
 * row holds); the FUNNEL's list is `sourceFilterOptions` below and is
 * deliberately not narrowed — a filter and a picker have opposite rules about a
 * retired row.
 */
import {
  companySourceLabel,
  companySourceOptions,
  useCompanySources,
} from '@/lib/use-company-sources';
/*
 * The company-TYPE master — what kind of organisation the account is.
 *
 * ⚠️ **The same three names and the same split as the two masters above**, and
 * deliberately NOT a second reading of the source master: a source is where an
 * account arrived from, a type is what the counterparty IS. `companyTypeOptions`
 * is the PICKER's list (active rows plus whatever this row holds); the FUNNEL's
 * list is `companyTypeFilterOptions` below and is deliberately not narrowed — a
 * filter and a picker have opposite rules about a retired row.
 */
import {
  companyTypeLabel,
  companyTypeOptions,
  useCompanyTypes,
} from '@/lib/use-company-types';
import { companyNotesConsequence } from '@/lib/company-delete';
/*
 * The Project-of-interest column's WORDING and its option list, both imported
 * rather than derived here.
 *
 * ⚠️ **`companyProjectsLabel` is the collapsed summary and `projectOfInterestOptions`
 * owns the cycle narrowing.** A second copy of either on this page would agree
 * the day it was written and drift on the first correction — and what would
 * drift is which projects a picker OFFERS, where the failure is a project
 * silently missing from a dropdown rather than anything that errors.
 */
import {
  PROJECTS_OF_INTEREST_LABEL,
  companyProjectsLabel,
  companyProjectsPills,
  companyProjectsTitle,
  projectOfInterestOptions,
  type SelectableProject,
} from '@/lib/company-projects';
import {
  CREDIT_BALANCE_LABEL,
  CREDIT_BALANCE_NOT_VISIBLE,
  balanceLabel,
  balancePills,
  balanceTitle,
  creditHeldOf,
} from '@/lib/credit-notes';
/*
 * Products purchased / Projects participated in — the WORDING, the distinct
 * list and the per-cycle title, all imported. `purchaseHistoryOf` folds an
 * older server's absent key to `null` ONCE; nothing here coalesces to `[]`,
 * because *not reported* and *bought nothing* are different facts.
 */
import {
  PRODUCTS_PURCHASED_LABEL,
  PROJECTS_PARTICIPATED_LABEL,
  purchaseHistoryOf,
  purchasedItemsOf,
} from '@/lib/company-purchases';
/*
 * The Products purchased / Projects participated in cell — the highlighted
 * cycle's items plus a by-year view.
 */
import { PurchaseHistoryCell } from '@/components/companies/PurchaseHistoryCell';
import { cx } from '@/lib/cx';
import { formatDateOnly, joinList, pluralise } from '@/lib/format';
import { preferencesStore } from '@/lib/preferences-store';
import { useDefaultAccountLeadFilter } from '@/lib/use-default-account-lead';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
import { useMediaQuery, useRowWindow } from '@/lib/use-row-window';
import { denialReason } from '@/lib/permissions';
import type {
  Company,
  CompanyCreditBalance,
  CompanyListParams,
  CompanyListSort,
  Country,
  IdListFilter,
  KanbanQueryParams,
  ListOrder,
  ListSort,
  MasterRef,
  Paginated,
  ReferenceRecord,
  UserSummary,
} from '@/lib/types';
import { viewerZone } from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
/*
 * The cycle calendar split into CURRENT and PREVIOUS id sets, for the
 * Project-of-interest picker's narrowing.
 *
 * ⚠️ **The hook, not `cycleScopeIdIndex` over a local `useCycleOptions`.** It
 * already memoises, and it already resolves the four ways the question cannot be
 * answered — `loading`, `failed`, `hasMore` and an empty master — to
 * `UNKNOWN_CYCLE_SCOPES` rather than to an empty set. `hasMore` is the one a
 * hand-rolled memo drops: a cycle absent from a truncated first page is not
 * proven closed, and filing it under `previous` on that evidence hides every
 * project in it from a picker whose default pill is `current`.
 */
/*
 * The pills' own vocabulary — ⛔ **imported, never restated.** `Current` here
 * and `Current` in `projectOfInterestOptions`' pinned-project hint have to be
 * the same word for the same set of cycles, or the picker labels a pinned row
 * `Previous` while a pill spelled differently claims to be showing it. There is
 * no second notion of "current" in this file and there must not be one.
 */
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  type CycleScopeIdIndex,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { useCycleScopeIds } from '@/lib/use-line-project';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useProjectOptions } from '@/lib/use-project-options';
import { useReferenceOptions } from '@/lib/use-reference-options';
import { useUserOptions } from '@/lib/use-user-options';
import {
  USER_GROUP_SLUGS,
  outsideGroupMarker,
  type StaffNarrowing,
} from '@/lib/user-groups';
import { useTableFilters, type ColumnSpec, type TableFilters } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  getFilterMemory,
  isFilterMemoryReady,
  isFilterMemoryWritable,
  setFilterMemory,
  stateMemoryKey,
} from '@/lib/filter-memory/store';
import { stateMemoryPath } from '@/lib/filter-memory';
import {
  Avatar,
  Button,
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  ListPager,
  PageHeader,
  Panel,
  PanelHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  ValuePills,
  type ColumnFilterOption,
  type FilterableTHProps,
  type FrozenSide,
  type SegmentedControlOption,
} from '@/components/ui';

const PER_PAGE = 20;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

// `cardsPerColumn` is 1..100, default 20. Left at the default: there is no
// per-column "load more" endpoint, so raising it only makes the first paint
// heavier, and column headers count every match either way.
const BOARD_CARDS_PER_COLUMN = 20;

// The board is not "the companies list, sideways" — its columns are the four
// account-lifecycle stages, and dragging a card is `PATCH /companies/{id}/stage`,
// the same reclassification the Stage select performs. The choice lives in the
// URL so either view is a real link.
//
// THE TABLE IS THE DEFAULT, because the PER-RECORD ACTIONS LIVE THERE. Tasks
// and notes, the "+" create menu, Edit and Delete are row actions with no
// board equivalent beyond `cardAction`, so opening on the board hid all of
// them behind a toggle — how the "+" menu was reported missing.
//
// An absent `view` key or anything but the exact value `board` means the
// table — a pasted `?view=kanban` is a typo, not a 404.
type CompaniesView = 'table' | 'board';

/**
 * What this page remembers against the account, spelled as query keys.
 *
 * ⚠️ **A key belongs here only if the page renders a control that can CLEAR
 * it.** A remembered filter with no visible off switch is a list that is
 * quietly narrowed every time somebody arrives, and the reader has no way to
 * discover why — the worst outcome available, because a short list looks like
 * a small dataset rather than like a filter.
 *
 * ⚠️ **`page` is never in either list.** It is not a description of which rows,
 * and seeding somebody onto page 7 of a set they have not seen reads as a
 * broken pager.
 */
// ✅ `accountLeadId` joined `stageId` on 2026-09-12, when the Account lead
// funnel moved onto the URL. A key may only be listed here once the page really
// READS it from the query string — anything else saves cleanly, reloads on the
// next arrival and is ignored by every request.
//
// ✅ **`clientSuccessManagerId`, `countryId` and `regionId` joined them the same
// day**, when the remaining three server-scoped funnels moved onto the URL —
// see `urlColumnFilters`. That is now every filter this page can express, so
// the note below about a filter surface narrower than the controls is spent.
//
// ⚠️ **This is `stageId` plus every member of `SERVER_FILTER_PARAMS`, written
// out rather than derived** — that list is declared further down the file, so
// spreading it here would read it before it exists. **Adding a server param
// there means adding its key here**, and `urlColumnFilters` is where the
// compiler will say so.
// ✅ **`sourceId` joined them on 2026-09-14**, in that order: the funnel's
// `value`/`onChange` were routed through `sourceFilterIds` / `setSourceFilter`
// FIRST, and only then was the key listed here. Listing it first would have
// saved a filter the hook held and no request read — one that persists across
// arrivals, draws its funnel with ticks, and narrows nothing.
const SAVED_FILTER_KEYS = [
  'stageId',
  'accountLeadId',
  'clientSuccessManagerId',
  'countryId',
  'regionId',
  'sourceId',
  // ✅ **`companyTypeId` joined them the same way, and in the same order** —
  // `companyTypeFilterIds` / `setCompanyTypeFilter` and the `urlColumnFilters`
  // row went in FIRST, and only then this entry. The ordering is recorded twice
  // now because it is the one step here whose mistake is invisible: a key saved
  // before anything reads it is a filter that persists across arrivals, draws
  // its funnel with ticks, and narrows nothing.
  'companyTypeId',
  // The two cycle filters on Projects participated in / Products purchased
  // (2026-10-09), added after their `urlColumnFilters` rows for the same reason.
  'participatedCycleId',
  'purchasedCycleId',
  // Stage status (2026-10-23), added after its `urlColumnFilters` row.
  'stageStatusId',
] as const;
/**
 * No joint meaning with the filters, so merged PER KEY rather than
 * all-or-nothing: a link naming a stage must not also impose the sender's
 * choice of table-versus-board on the recipient.
 */
const SAVED_VIEW_KEYS = ['view'] as const;
type SavedCompaniesKey = (typeof SAVED_FILTER_KEYS)[number] | (typeof SAVED_VIEW_KEYS)[number];

const VIEW_OPTIONS: readonly { value: CompaniesView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'board', label: 'Board' },
];

// The kanban query schema is `.strict()` and built PER BOARD, so a key
// belonging to another board is a 422 naming it, not a silent no-op —
// `boardParams` below is assembled key by key, NEVER by spreading this page's
// `/companies`-shaped filter state.
//
// The companies board takes exactly what `/companies` itself takes:
// `accountLeadId`, `clientSuccessManagerId`, `facultyId`, `regionId`,
// `stageId`, `search`, plus `cardsPerColumn` — and on this board alone the id
// filters accept a comma-separated LIST, as the list endpoint does. This page
// has a control for five: the Stage column funnel (in the URL), search, and
// three server-scoped column funnels. `facultyId` has no column here, so it is
// never set.
//
// ⚠️ `ownerId` is a SEVENTH key the board still accepts and this app no longer
// sends — it went with the owner surface on 2026-08-31, and it is off
// `KanbanQueryParams` entirely, so no board here can express it.
//
// `card.company` is always null on this board (the card IS the company), and
// so are `amount`/`currency`/`totalAmount` — a company carries no money.

/**
 * Two buttons, not a Select — this switches the view rather than filtering,
 * and `aria-pressed` states which is on with no listbox to open. Sits in
 * `PageHeader`'s actions, which wraps; `PanelHeader`'s is `shrink-0` and would
 * push the panel sideways instead.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: CompaniesView;
  onChange: (next: CompaniesView) => void;
}) {
  return (
    <ViewToggleControl
      label="Company view"
      value={view}
      options={[...VIEW_OPTIONS]}
      onChange={onChange}
    />
  );
}

/**
 * The sorts the API actually supports — anything else silently falls back.
 *
 * ⚠️ **Typed against `CompanyListSort`, not `ListSort`** — `/companies` sorts by
 * four keys where `/contacts` sorts by three, and the widened union is declared
 * once for exactly this. It ADMITS `country` and `openTask` as spellings
 * without offering them: `sortChoices` narrows this list to the keys some
 * column really declares a `sortKey` for, so an option nothing can drive is
 * dropped rather than sitting in the control doing nothing — which is what
 * "Recently updated" was for a week.
 *
 * ⚠️ **Source is offered HERE as well as on the column header, and that is not
 * decoration.** Below `md` the column funnels live in the `FilterSheet` and
 * this select is the only sort control on screen, so a sort offered on the
 * desktop header alone would be a desktop-only feature — indistinguishable, on
 * a phone, from one that was never built.
 */
const SORT_OPTIONS: { value: `${CompanyListSort}:${ListOrder}`; label: string }[] = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'updatedAt:desc', label: 'Recently updated' },
  { value: 'updatedAt:asc', label: 'Least recently updated' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  // ⚠️ **Ascending opens on the companies with NO source recorded**, which is
  // most of them: `source_id` is nullable, MariaDB sorts NULLs first, and the
  // server deliberately applies no COALESCE — a sentinel would not move those
  // rows out of the way, it would file them under a source they did not come
  // from. The labels say A–Z rather than "Source first" for that reason.
  { value: 'source:asc', label: 'Source A–Z' },
  { value: 'source:desc', label: 'Source Z–A' },
  // The same NULLs-first caveat as Source, for the same reason: `company_type_id`
  // is nullable and the server applies no COALESCE, so ascending opens on the
  // companies nobody has classified. A–Z rather than "Type first" for that
  // reason. ⚠️ **`companyType`, not `companyTypeId`** — the sort names the
  // master's NAME column and the filter names the foreign key; the wrong one is
  // a 422 naming the key.
  { value: 'companyType:asc', label: 'Type A–Z' },
  { value: 'companyType:desc', label: 'Type Z–A' },
  // ⚠️ **NOT the NULLs-first caveat above, and deliberately the opposite.** The
  // server sorts a company with NO human note LAST in BOTH directions (an
  // explicit `IS NULL` term ahead of the date), so either direction opens on
  // accounts that have a note. The labels can therefore name the order plainly.
  // ⚠️ The date is the account's, not the reader's — sorting by it is allowed
  // for every reader even where the note's text is not visible to them.
  { value: 'lastNoteAt:desc', label: 'Last note, newest first' },
  { value: 'lastNoteAt:asc', label: 'Last note, oldest first' },
  // The same shape as the last-note pair, for the account's latest task.
  { value: 'lastTaskAt:desc', label: 'Last task, newest first' },
  { value: 'lastTaskAt:asc', label: 'Last task, oldest first' },
  // 2026-10-09 (backend `32057ed`): every funnel column sorts on the server.
  // Person and master columns order by the NAME, with no sentinel for a blank,
  // so ascending opens on the unset rows — the Source caveat above.
  { value: 'stage:asc', label: 'Stage A–Z' },
  { value: 'stage:desc', label: 'Stage Z–A' },
  { value: 'country:asc', label: 'Country A–Z' },
  { value: 'country:desc', label: 'Country Z–A' },
  { value: 'accountLead:asc', label: 'Account lead A–Z' },
  { value: 'accountLead:desc', label: 'Account lead Z–A' },
  { value: 'clientSuccessManager:asc', label: 'Client success manager A–Z' },
  { value: 'clientSuccessManager:desc', label: 'Client success manager Z–A' },
  // Set-valued columns order by their COUNT. Projects participated in and
  // Products purchased count only the cycles their own funnel names, when set.
  { value: 'regions:desc', label: 'Most regions' },
  { value: 'regions:asc', label: 'Fewest regions' },
  { value: 'projectsParticipated:desc', label: 'Most participating projects' },
  { value: 'projectsParticipated:asc', label: 'Fewest participating projects' },
  { value: 'productsPurchased:desc', label: 'Most products purchased' },
  { value: 'productsPurchased:asc', label: 'Fewest products purchased' },
];

/**
 * The only three the API sorts on — offering a fourth would 422.
 *
 * ⚠️ **`createdAt` stays here even though the Created COLUMN is gone** — it is
 * the default sort and what "Newest/Oldest first" select. Showing a column and
 * ordering by a field are two different decisions.
 */
function isListSort(value: string): value is ListSort {
  return value === 'name' || value === 'createdAt' || value === 'updatedAt';
}

/**
 * The same question for `/companies`, which sorts by one key more.
 *
 * ⚠️ **`source`, NOT `sourceId`** — the filter is `?sourceId=` and the sort is
 * `?sort=source`, one word apart, and the wrong one is a 422 naming the key
 * rather than a silent no-op. Exactly the `country` / `countryId` pair.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, for `country` only: "⛔ **`country` and
 * `openTask` are members of `CompanyListSort` and are deliberately NOT admitted
 * here.** The server sorts by both; this PAGE declares a `sortKey` for
 * neither."* The Country column declares `sortKey: 'country'` now (owner: every
 * funnel column sorts), so it is admitted. **The rule survives and is why
 * `openTask` and `faculty` are still absent**: no column here declares either,
 * and the predicate describes what this page can drive, not what the endpoint
 * can do.
 */
const PAGE_SORT_KEYS: ReadonlySet<string> = new Set([
  'source',
  'companyType',
  'lastNoteAt',
  'lastTaskAt',
  'stage',
  'country',
  'accountLead',
  'clientSuccessManager',
  'regions',
  'projectsParticipated',
  'productsPurchased',
] satisfies CompanyListSort[]);

function isCompanySortKey(value: string): value is CompanyListSort {
  return isListSort(value) || PAGE_SORT_KEYS.has(value);
}

/**
 * Column specs that exist ONLY to carry a `sortKey` — no header, no cell, no
 * funnel, no entry in the mobile filter sheet. Must stay out of `filterKeys`,
 * the one place column keys reach `FilterSheet`.
 */
const SORT_ONLY_COLUMN_KEYS = new Set(['createdAt', 'updatedAt']);

/**
 * The companies table's filter-memory suffix before it had a `memoryId`
 * (until 2026-10-25): `table-` + the djb2 hash of the then-static column keys.
 * Read once to carry existing memory across; see the carry-over effect next
 * to `useTableFilters`.
 */
const LEGACY_TABLE_MEMORY_SUFFIX = 'table-127syka';

/**
 * The `/companies` list params this table's column funnels drive — real query
 * params on BOTH the list and the board, spelled the same, so a `scope:
 * 'server'` column filters both through one id.
 *
 * ⚠️ **They take a COMMA-SEPARATED LIST, and are the only params on this API
 * that do.** `?accountLeadId=1,2` parses as a list on `/companies` and
 * `/companies/kanban` only — every other board/list 422s "expected number,
 * received NaN". That is why these columns carry `multi: true` and why
 * `IdListFilter` exists; neither may be copied onto another page.
 *
 * ⚠️ **CORRECTED 2026-08-31 — the sentence above said "real query params on
 * BOTH the list and the board", and `countryId` is the exception that makes it
 * no longer true of every member.** It is a LIST parameter only; the companies
 * kanban schema is `.strict()` and 422s on it. That is why `boardParams` below
 * is still written out key by key rather than spreading `listFilters`, and why
 * the board renders a notice when a country filter is set. **Read this list as
 * "the params the funnels drive", not as "the params both surfaces accept".**
 */
/*
 * ⚠️ `ownerId` was the FIRST member and was removed on 2026-08-31 with the rest
 * of the owner surface. `/companies` and `/companies/kanban` both still accept
 * it — no column ever drove it here, so nothing on screen changes; what goes is
 * the ability for a stale URL to narrow the list by a person this app no longer
 * renders anywhere.
 */
/**
 * A comma-separated id list out of the query string, or `[]`.
 *
 * ⚠️ **ONE copy for Stage and Account lead**, because the two rules below are
 * easy to get subtly different and both failures are silent.
 *
 * ⛔ **A JUNK MEMBER DROPS THE WHOLE VALUE rather than being skipped.** A
 * half-applied filter is worse than an unapplied one: `?stageId=1,abc` narrowing
 * to stage 1 is a list the reader never asked for, shown under a funnel drawn
 * with one tick. The server agrees — its schema is
 * `z.coerce.number().int().positive()` and it 422s the whole request — so this
 * mirrors the refusal rather than inventing a client-side narrowing.
 *
 * Duplicates are collapsed; the server does the same.
 */
function idListFromQuery(query: string, key: string): number[] {
  const raw = new URLSearchParams(query).get(key);
  if (raw === null || raw === '') return [];
  const ids = raw.split(',').map((part) => Number(part));
  if (ids.some((id) => !Number.isInteger(id) || id <= 0)) return [];
  return Array.from(new Set(ids));
}

/**
 * The same list back into the query string.
 *
 * ⚠️ **An EMPTY list DELETES the key and never writes `?key=`.** An empty value
 * is a 422 on these `.strict()` schemas, not "no filter" — and it would also be
 * saved as one by `useSavedFilters`, so a cleared funnel would come back as a
 * broken request on the next arrival.
 */
function writeIdList(query: string, key: string, next: readonly number[]): URLSearchParams {
  const params = new URLSearchParams(query);
  if (next.length === 0) params.delete(key);
  else params.set(key, next.join(','));
  return params;
}

const SERVER_FILTER_PARAMS = [
  'accountLeadId',
  'clientSuccessManagerId',
  'regionId',
  // ⚠️ LIST ONLY. See the correction above before adding it to `boardParams`.
  'countryId',
  // ⚠️ LIST ONLY as well, and for a stronger reason than `countryId`'s: the
  // companies kanban does not declare `sourceId` at all, so whether it parses
  // one is UNMEASURED rather than known-to-422. `BOARD_UNSUPPORTED_PARAMS`
  // carries it for that reason — an unmeasured key sent to a `.strict()` board
  // schema is a 422 that takes the whole board out.
  'sourceId',
  // ⚠️ LIST ONLY, on the same unmeasured footing as `sourceId` directly above:
  // `companyTypeId` is not declared on `KanbanQueryParams` either, so
  // `BOARD_UNSUPPORTED_PARAMS` carries it too.
  'companyTypeId',
  // ⚠️ LIST ONLY, on the same unmeasured footing: neither cycle filter is
  // declared on `KanbanQueryParams` (backend `32057ed`, 2026-10-09).
  'participatedCycleId',
  'purchasedCycleId',
  // ⚠️ *SUPERSEDED 2026-10-25: "LIST ONLY, on the same unmeasured footing".*
  // The companies kanban registry declares `stageStatusId` (kind 'ids', backend
  // shipped server-first), so `boardParams` sends it too — see there.
  'stageStatusId',
] as const;

type ServerFilterParam = (typeof SERVER_FILTER_PARAMS)[number];

/**
 * Every query key a column funnel on this page writes — the four server params
 * plus Stage, which filters through `stageId` but is spelled into the request by
 * hand rather than through `listFilters`.
 *
 * ⛔ **It exists so that "clear every column filter" is ONE write to the query
 * string.** Each setter below builds its `URLSearchParams` from the `query`
 * string captured at render, so clearing five filters with five calls would have
 * five writers racing one stale snapshot — the last would restore the four keys
 * its predecessors had just deleted, leaving a table that reads as filtered
 * after a Clear that reported success. Same shape as the saved-filter store's
 * recursive-merge hazard, one layer up.
 *
 * Derived from `SERVER_FILTER_PARAMS`, so a fifth server filter is cleared by
 * the entry somebody adds there.
 */
const URL_FILTER_KEYS: readonly string[] = ['stageId', ...SERVER_FILTER_PARAMS];

/**
 * One funnel whose ticked ids live in the query string: what it holds, and the
 * writer that puts it back.
 *
 * ⚠️ **`emptiness` is deliberately absent.** That half stays in
 * `useTableFilters` on every one of these columns, because it is page-scoped —
 * no endpoint here exposes an is-null filter, so `?accountLeadId=` cannot ask
 * for "nobody leads this account". A control with two homes is the price of a
 * deep link, and writing both on every change is what keeps them from
 * disagreeing.
 */
type UrlColumnFilter = {
  readonly ids: readonly number[];
  readonly set: (next: readonly number[]) => void;
};

/**
 * Of those, the ones the companies KANBAN does not accept.
 *
 * ⚠️ **A member here is a `422` on `/companies/kanban`, not a no-op** — the
 * board schema is `.strict()` and built per board. So `boardParams` must not
 * send it, and board view must SAY the filter is not applied: a filtered table
 * and an unfiltered board under one heading is the "it looks like it worked"
 * failure, and here it would show companies the table had excluded.
 *
 * `countryId` was the first member, and `sourceId` is the second — which is
 * what the set shape above was written for. The two are here for DIFFERENT
 * reasons and the difference matters if anybody ever measures them: `countryId`
 * is known to 422 on the board, while `sourceId` is simply **not declared on
 * `KanbanQueryParams`**, so whether the board parses it has never been
 * measured. Unmeasured is treated as unsupported, because the failure of
 * guessing wrong is a 422 that empties the whole board rather than one filter
 * quietly not applying. Measure before promoting either.
 */
// Typed `ReadonlySet<string>` so it can be asked about a `ColumnSpec.param`,
// which is a plain `string`. The literal is checked against `ServerFilterParam`
// on the way in, so a typo is still a build error rather than a member that
// silently matches nothing.
const BOARD_UNSUPPORTED_PARAMS: ReadonlySet<string> = new Set<ServerFilterParam>([
  'countryId',
  'sourceId',
  // The third, on `sourceId`'s footing rather than `countryId`'s: not declared
  // on `KanbanQueryParams`, so whether the board parses it is UNMEASURED.
  // Unmeasured is treated as unsupported here — being wrong that way costs one
  // filter quietly not applying on the board, where being wrong the other way
  // is a 422 that empties the whole board. Measure before promoting it.
  'companyTypeId',
  // The two cycle filters, on the same unmeasured footing.
  'participatedCycleId',
  'purchasedCycleId',
  // ⚠️ *SUPERSEDED 2026-10-25: `stageStatusId` sat here "on the same
  // unmeasured footing".* The companies board declares it now, so the status
  // funnels narrow the board exactly as they narrow the table.
]);

/**
 * Every option, plus any selected value the list does not contain — a ticked
 * value with no matching row would otherwise render no checkbox, leaving the
 * column stuck reading as filtered with no way out but "Clear column filters".
 * The id is a poor label, and the honest one: this list does not know the name.
 */
function withSelectedValues(
  options: ColumnFilterOption[],
  selected: readonly string[],
): ColumnFilterOption[] {
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((value) => !known.has(value));
  return missing.length === 0
    ? options
    : [...options, ...missing.map((value) => ({ value, label: `#${value}` }))];
}

/**
 * One validated id, a validated comma-separated list of them, or `undefined`.
 *
 * A `multi` column hands back `"3,7"` as text; reading that with `Number()`
 * produces NaN and drops the filter — the funnel still shows the column as
 * filtered while the request carries nothing. Every member is checked here.
 *
 * A single value comes back as a NUMBER, since the board banner's owner lookup
 * compares it against `row.owner.id`.
 */
function idFilterValue(raw: string | undefined): IdListFilter | undefined {
  if (raw === undefined || raw === '') return undefined;
  const ids = raw.split(',').map((part) => Number(part));
  // `Number('')` is 0 and `Number('x')` is NaN, so a stray comma or a
  // non-numeric member fails this rather than reaching the API as a 422.
  if (ids.some((id) => !Number.isInteger(id) || id <= 0)) return undefined;
  const [first] = ids;
  if (first === undefined) return undefined;
  return ids.length === 1 ? first : ids.join(',');
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

// The same paths `FutureBillablesTrackerPage`'s export button inlines.
function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}

/**
 * The server's refusal when an export is over its cap is the useful sentence
 * (it says how far to narrow), so it is shown as sent. Anything else falls back
 * through the usual specificity order.
 */
const COMPANY_EXPORT_TOO_LARGE_CODE = 'COMPANY_EXPORT_TOO_LARGE';

function exportFailureMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === COMPANY_EXPORT_TOO_LARGE_CODE) return caught.message;
    return caught.formMessage ?? caught.message;
  }
  return caught instanceof Error ? caught.message : 'The export could not be produced.';
}

/**
 * The three records that are raised *against* a company, in the order the
 * business raises them. Each is gated on its own module — creating a deal and
 * creating an invoice are separate permissions, and a set may hold one without
 * the other, so this is never a single `canCreate('deals')` for all three.
 */
/*
 * ⚠️ **The row "+" menu and its URL convention BOTH live in
 * `@/components/records/RecordCreateMenu` now** — the portal, the `z-[60]`
 * layer rung, the keyboard semantics and the flip handling were extracted
 * verbatim, together with the `?companyId=<id>&new=1` convention itself, so
 * that this list, its board and the Companies card on
 * `/university-partnerships` cannot offer different records or word the control
 * differently. `CREATE_TARGETS` moved with it as `COMPANY_CREATE_TARGETS`;
 * the per-module `ability.canCreate` filter stays here, because what is
 * OFFERED is this page's decision.
 */

// `OpenNotesButton` — the last-note cells' way into the notes sheet — lives in
// `@/components/companies/OpenNotesButton`, shared with the sales tracker.

function OwnerCell({ owner }: { owner: Company['owner'] }) {
  if (!owner) return <span className="text-xs text-fg-subtle">Unassigned</span>;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar name={owner.name} size="sm" />
      <span className="truncate text-sm text-fg-secondary">{owner.name}</span>
    </div>
  );
}

/**
 * The two person columns are one column model. The value is the READ key; the
 * write key is derived from it in `changeStaff`, by hand, so a typo is a
 * compile error.
 */
type StaffColumn = 'accountLead' | 'clientSuccessManager';

/** What each column is called, in one place, so a label and a message agree. */
const STAFF_LABELS: Record<StaffColumn, string> = {
  accountLead: 'Account lead',
  clientSuccessManager: 'Client success manager',
};

/**
 * Narrows the plain `string` a `headerFor(key)` call carries to the two person
 * columns, which share one option list and one loading flag.
 *
 * Derived from `STAFF_LABELS` rather than written as `key === 'accountLead' ||
 * …`, so a third person column is covered by the entry somebody adds there
 * instead of by remembering this line — the same reason `filterKeys` is derived
 * from `columns` rather than hand-listed.
 *
 * `Object.hasOwn`, not `in`: `'toString' in STAFF_LABELS` is true, and a key
 * that walks the prototype chain into `StaffColumn` would index the record as
 * `undefined` and be read as a column with no options.
 */
function isStaffColumn(key: string): key is StaffColumn {
  return Object.hasOwn(STAFF_LABELS, key);
}

/**
 * The staff options for one row's picker. Two things a plain `users.map` does
 * not do:
 *
 *  1. **Injects the person the row already holds** when the loaded page does
 *     not carry them. Without it the select falls back to its empty option and
 *     the cell reads **"Unassigned"** over a company that is assigned — the
 *     next person to touch the column then overwrites a real assignment in
 *     ignorance of it. Same defence as `CompanyFormModal` and the detail page.
 *  2. **Disambiguates duplicate names only when there are duplicates.** A
 *     native `<option>` has no hint slot, so pinning an email to all eleven
 *     would fill a 192px cell with addresses — only worth it when the name
 *     alone would silently pick the wrong person.
 */
function staffOptions(
  users: UserSummary[],
  stored: UserSummary | null,
  /**
   * The group this picker is narrowed to, when a stored holder outside it must
   * be MARKED. `null` means say nothing — either nothing narrows this picker, or
   * the list is not known well enough to accuse anybody. See
   * `outsideGroupMarker`.
   */
  outsideGroup: string | null,
): { value: string; label: string }[] {
  const people = [...users];
  // ⚠️ The stored holder is injected whether or not the group contains them —
  // narrowing the list makes this fire routinely rather than rarely.
  const outside = stored !== null && !people.some((person) => person.id === stored.id);
  if (stored && outside) people.unshift(stored);

  const seen = new Map<string, number>();
  for (const person of people) seen.set(person.name, (seen.get(person.name) ?? 0) + 1);

  return people.map((person) => {
    const label =
      (seen.get(person.name) ?? 0) > 1 && person.email !== ''
        ? `${person.name} (${person.email})`
        : person.name;
    // A native <option> has no hint slot, so the marker rides in the label
    // itself — briefer than a Combobox's sentence, since this is also what
    // the closed cell reads.
    return {
      value: String(person.id),
      label:
        outsideGroup !== null && stored !== null && person.id === stored.id && outside
          ? `${label} — not in ${outsideGroup}`
          : label,
    };
  });
}

/**
 * One of the two person columns, editable in place — same idiom as `StageCell`.
 *
 * Disabled, not hidden, while the directory is loading or failed: a picker
 * offering nothing but "Unassigned" is a one-click way to wipe a name on
 * screen, and a failed `/users` fetch is "we do not know who is on staff",
 * never "nobody is".
 *
 * ⚠️ **Unlike stage, a refusal here names no field.** A bad user id answers
 * `422 One or more assigned users do not exist` with **no `details` array at
 * all**, so `fieldErrors` is empty and `changeStaff` falls back to the
 * server's own sentence — still marked on this cell, since it's the control
 * that was just used.
 */
function StaffCell({
  row,
  column,
  users,
  outsideGroup,
  disabled,
  saving,
  error,
  onChange,
}: {
  row: Company;
  column: StaffColumn;
  /** The group's members. Narrowed — see `staffCellProps`. */
  users: UserSummary[];
  /** The group name a stored non-member is marked against, or null. */
  outsideGroup: string | null;
  /** Directory not usable yet: loading, or the fetch failed. */
  disabled: boolean;
  saving: boolean;
  error: string | undefined;
  onChange: (row: Company, column: StaffColumn, userId: number | null) => void;
}) {
  const stored = row[column];

  return (
    <Field label={`${STAFF_LABELS[column]} for ${row.name}`} hideLabel error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Select
          id={id}
          aria-describedby={describedBy}
          value={stored ? String(stored.id) : ''}
          invalid={Boolean(error)}
          disabled={disabled || saving}
          onChange={(event) => {
            const next = event.target.value;
            // `''` is sent as `null`, never itself — an empty reference id is
            // a 422 here, opposite of the free-text columns where `''` clears.
            onChange(row, column, next === '' ? null : Number(next));
          }}
        >
          <option value="">Unassigned</option>
          {staffOptions(users, stored, outsideGroup).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * The message for a refusal of the account lead's EFFECTIVE DATE (or of the
 * history it would write), shown in `AccountLeadChangeDialog` where the date can
 * be changed — else null, and the refusal lands on the cell as before. Matched
 * on `code` and on the request key, never on the status.
 */
function leadDateRefusal(caught: unknown): string | null {
  if (!(caught instanceof ApiError)) return null;
  const known = accountLeadErrorMessage(caught.code);
  if (known !== null) return known;
  const fieldMessage = caught.fieldErrors.accountLeadEffectiveFrom;
  if (fieldMessage !== undefined) return fieldMessage;
  if (caught.code.startsWith('ACCOUNT_LEAD_')) return caught.formMessage ?? caught.message;
  return null;
}

// ⚠️ There is no Faculty column on this page (owner removed it from
// `/companies` only) — no header, cell, card block, funnel, or `facultyId` on
// the request. Faculty is still a real, writable field: `CompanyDetailPage`
// edits it, `CompanyFormModal` sets it, `Company.faculty` still exists, and
// `/companies?facultyId=` still works — this page simply never sends it.

// There is no `RegionsCell` here. `InlineRegionsField` renders EVERY region a
// company names as wrapping badges when idle, nothing elided behind a "+N
// more" — a cell showing one of three would misreport the account. It takes
// `disabled` itself, so a reader who may not write companies sees the same
// thing. See `regionsField` in the page body for the write rules it enforces.

/**
 * The two pills, DERIVED from the shared union rather than written out.
 *
 * ⛔ **The labels come from `PROJECT_CYCLE_SCOPE_LABELS`, which is also what
 * `projectOfInterestOptions` uses to explain why a stored project is pinned.**
 * Spelling `Current` and `Previous` out here would be a second copy of a
 * vocabulary that already has one home, and the drift would show up as a picker
 * contradicting its own pins — a row hinted `Previous` sitting under a pill
 * that calls the same set something else. Mapping the union also means a third
 * scope would appear on this control on its own.
 */
const PROJECT_CYCLE_SCOPE_OPTIONS: readonly SegmentedControlOption<ProjectCycleScope>[] =
  PROJECT_CYCLE_SCOPES.map((scope) => ({
    value: scope,
    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
  }));

interface ProjectsOfInterestCellProps {
  row: Company;
  /**
   * Every ACTIVE project, UNNARROWED by cycle. The scope filter belongs to
   * `projectOfInterestOptions`, which needs the full list to resolve this
   * company's stored projects against — narrowing the fetch instead is what
   * makes an out-of-scope project indistinguishable from a retired one.
   */
  projects: readonly SelectableProject[];
  /** Both cycle scopes' id sets; `UNKNOWN_CYCLE_SCOPES` offers everything. */
  scopes: CycleScopeIdIndex;
  loading: boolean;
  disabled: boolean;
  footnote: string | undefined;
  onSave: (next: string[]) => Promise<InlineSetSaveResult>;
}

/**
 * The Project-of-interest cell: the collapsed summary, the in-place editor, and
 * the Current/Previous pills deciding which cycles its picker offers.
 *
 * ## 🔴 WHY THIS IS A COMPONENT WHERE `regionsField` IS A CLOSURE
 *
 * It is the only cell here holding state of its own, and **the state is PER
 * ROW**. One `useState` in the page body would be one scope shared by twenty
 * pickers, so switching to `Previous` on one company would silently re-offer
 * every other row's picker too — invisible, because only one editor is ever
 * open, right until somebody opens another row and finds it on a pill they
 * never pressed. A component per cell gets one instance per row from React's
 * own reconciliation, under the `key={row.id}` the row already carries, so the
 * pill follows the COMPANY rather than the position in the list.
 *
 * ⚠️ **Declared at module level, never inside `CompaniesPage`.** A component
 * defined during a render is a new function identity on every render, so React
 * unmounts and remounts its whole subtree each time: the scope would reset on
 * every keystroke in the search box and the open editor would close mid-edit.
 * That is why `StageCell`, `CountryCell` and `StaffCell` live out here too.
 *
 * ⚠️ **The desktop table and the sub-`md` card each mount one per row**, so a
 * row has two instances with independent scopes. Only one of the two is ever on
 * screen, and each is the state of the control the reader is actually touching.
 *
 * ## The pill decides what is OFFERED, never what is HELD
 *
 * ⛔ **Switching scope does not clear the selection, and structurally cannot.**
 * The scope reaches `projectOfInterestOptions` and nothing else; `values` comes
 * from the record and the draft lives inside `InlineRegionsField`. A stored
 * project stays ticked under both pills, because that function pins every
 * stored project into EVERY scope — which is the same guarantee that stops a
 * narrowed picker rendering blank over a real value.
 *
 * ⚠️ **It is per-picker UI state and nothing else — not a URL param, not a
 * saved filter, and not a filter on the list.** It narrows a dropdown. In the
 * query string it would make one shared link render a different picker for two
 * people, and would read as though the LIST had been narrowed when it has not.
 *
 * ⚠️ **KNOWN, and it changes nothing about what is written:** tick a `Previous`
 * project and switch back to `Current` before leaving the field, and that
 * project sits in the draft while being in neither the offered list nor
 * `stored` — so `MultiCombobox` labels its chip with the raw id until the save
 * lands. The write still carries the right id and the chip's × still removes
 * it. Closing it properly means pinning the DRAFT, which only
 * `InlineRegionsField` can see and only this page can label, so it is recorded
 * rather than half-solved.
 */
function ProjectsOfInterestCell({
  row,
  projects,
  scopes,
  loading,
  disabled,
  footnote,
  onSave,
}: ProjectsOfInterestCellProps) {
  // Remembered on the account (owner, 2026-10-16); each row still holds its
  // own copy, seeded from the remembered pill.
  const [scope, setScope] = useRememberedState<ProjectCycleScope>(
    'company-projects-cycle-scope',
    DEFAULT_PROJECT_CYCLE_SCOPE,
    (raw) => PROJECT_CYCLE_SCOPES.find((value) => value === raw),
  );
  const title = companyProjectsTitle(row.projectsOfInterest);
  return (
    <InlineRegionsField
      // Includes the company: twenty fields all called "Projects of interest"
      // are twenty identical entries in a screen reader's list.
      label={`${PROJECTS_OF_INTEREST_LABEL} for ${row.name}`}
      values={row.projectsOfInterest.map((project) => String(project.id))}
      options={projectOfInterestOptions(projects, row.projectsOfInterest, scopes, scope)}
      optionsLoading={loading}
      disabled={disabled}
      display={
        <ValuePills
          items={companyProjectsPills(row.projectsOfInterest)}
          empty={<span className="text-fg-subtle">{companyProjectsLabel([])}</span>}
          {...(title ? { title } : {})}
        />
      }
      editorHeader={
        <SegmentedControl
          /*
           * Names the CHOICE, and names the row: this is the group's accessible
           * name and it is not rendered visibly, so twenty groups all called
           * "Project cycle" would be twenty identical entries in a screen
           * reader's list — the same reason the field's own label carries the
           * company.
           */
          label={`Project cycle for ${row.name}`}
          value={scope}
          options={PROJECT_CYCLE_SCOPE_OPTIONS}
          onChange={setScope}
        />
      }
      placeholder={loading ? 'Loading projects…' : 'No projects'}
      searchPlaceholder="Search projects…"
      {...(footnote === undefined ? {} : { footnote })}
      onSave={onSave}
    />
  );
}

/**
 * The pipeline stage, read-only or editable in place.
 *
 * `stage: null` is a real, normal state, rendered as "Unclassified" in the
 * same subdued treatment `OwnerCell` gives "Unassigned" — never shown as
 * "Lead", no default invented.
 *
 * Editing saves immediately, with no confirmation: reclassifying a book of
 * business is a dozen changes in a row, and a dialog per change makes the
 * column unusable. The safety net is the rollback in `changeStage`.
 *
 * Disabled, not hidden, while the master is loading or failed — a select
 * offering only "Unclassified" would let someone clear a real stage by
 * picking the only other option.
 */
function StageCell({
  row,
  stages,
  disabled,
  saving,
  error,
  onChange,
}: {
  row: Company;
  stages: ReferenceRecord[];
  /** Master not usable yet: loading, or the fetch failed. */
  disabled: boolean;
  saving: boolean;
  error: string | undefined;
  onChange: (row: Company, stageId: number | null) => void;
}) {
  const stored = row.stage;

  return (
    <Field label={`Stage for ${row.name}`} hideLabel error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Select
          id={id}
          aria-describedby={describedBy}
          value={stored ? String(stored.id) : ''}
          invalid={Boolean(error)}
          disabled={disabled || saving}
          onChange={(event) => {
            const next = event.target.value;
            onChange(row, next === '' ? null : Number(next));
          }}
        >
          <option value="">Unclassified</option>
          {companyStageOptions(stages, stored).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/** The same value with no control, for anyone who may not write companies. */
function StageValue({ stage }: { stage: MasterRef | null }) {
  if (!stage) return <span className="text-xs text-fg-subtle">Unclassified</span>;
  return <span className="text-sm text-fg-secondary">{companyStageLabel(stage.id, stage.name)}</span>;
}

/**
 * The Stage status, read-only — `—` when the company holds none. `undefined`
 * (an older API that does not serve the key) renders the same dash: there is
 * no status this page can claim either way.
 */
function StageStatusValue({ status }: { status: MasterRef | null | undefined }) {
  if (!status) return <span className="text-sm text-fg-subtle">—</span>;
  return <span className="text-sm text-fg-secondary">{status.name}</span>;
}

const FOLLOW_UP_ON_LABEL = 'Follow up on';

/** Stage status refusals shown on the cell, matched by `error.code`. */
const STAGE_STATUS_CELL_ERROR_CODES: ReadonlySet<string> = new Set([
  'STAGE_STATUS_WRONG_STAGE',
  'FOLLOW_UP_DATE_REQUIRED',
  'FOLLOW_UP_DATE_INVALID',
  'FOLLOW_UP_DATE_NOT_ALLOWED',
  'STAGE_STATUS_REASON_REQUIRED',
]);

/**
 * The follow-up date a resurface sub-stage set, read-only. `null` (none) and
 * `undefined` (an older API) both render `—`.
 */
function FollowUpOnValue({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-sm text-fg-subtle">—</span>;
  return (
    <span className="text-sm whitespace-nowrap text-fg-secondary tabular-nums">
      {formatDateOnly(value)}
    </span>
  );
}

/*
 * ⚠️ *SUPERSEDED 2026-10-25: `StageStatusCell`, the one Stage status column's
 * in-place picker.* The per-stage columns render `StageStatusColumnCell`
 * (`@/components/companies/StageStatusColumnCell`), which is editable only in
 * the column of the company's own stage and offers only that stage's statuses.
 */

/**
 * Where the account came from, read-only — the branch for a reader who may not
 * write companies, and the same shape as `StageValue` and `CountryValue`.
 *
 * ⚠️ **`null` is "Not recorded", and here that is the MAJORITY of rows rather
 * than a residue** — every company predating the column reads back `null`. So
 * this subdued treatment is what most of the column looks like, and it must not
 * read as a fault: nobody has recorded a source, which is a different statement
 * from the master having failed to load. The one that says THAT is the disabled
 * control with its reason, not this.
 */
function SourceValue({ source }: { source: MasterRef | null }) {
  if (!source) return <span className="text-xs text-fg-subtle">Not recorded</span>;
  return (
    <span className="text-sm text-fg-secondary">{companySourceLabel(source.id, source.name)}</span>
  );
}

/**
 * The Source column, edited in place — the same idiom as `StageCell`, and a
 * `Select` rather than a `Combobox` for the reason `CountryCell` is the other
 * way round: five curated options is a choice, 195 is a scroll. The searchable
 * treatment the owner asked for on 2026-09-13 is about FILTER dropdowns; this
 * is an editor, and the column's own funnel carries a search box already.
 *
 * Editing saves immediately, with no confirmation — the same trade `StageCell`
 * states: attributing a book of business is a dozen changes in a row, and a
 * dialog per change makes the column unusable. The safety net is the rollback
 * in `changeSource`.
 *
 * ⚠️ **Disabled, not hidden, while the master is loading or failed — and the
 * "Not recorded" row is FIRST, always.** A select offering nothing but the
 * empty row would be one click from clearing a source that is on screen beside
 * it, which is the same trap `StageCell` and `CountryCell` each close.
 * `companySourceOptions` keeps whatever this row holds in the list either way,
 * including a retired row, so the value stays visible through a failed read.
 */
function SourceCell({
  row,
  sources,
  disabled,
  saving,
  error,
  onChange,
}: {
  row: Company;
  sources: ReferenceRecord[];
  /** Master not usable yet: loading, or the fetch failed. */
  disabled: boolean;
  saving: boolean;
  error: string | undefined;
  onChange: (row: Company, sourceId: number | null) => void;
}) {
  const stored = row.source;

  return (
    <Field label={`Source for ${row.name}`} hideLabel error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Select
          id={id}
          aria-describedby={describedBy}
          value={stored ? String(stored.id) : ''}
          invalid={Boolean(error)}
          disabled={disabled || saving}
          onChange={(event) => {
            const next = event.target.value;
            // Clearing is a real choice and `source_id` is nullable, so `null`
            // is what says it — never `''`, which is a 422 on a reference id.
            onChange(row, next === '' ? null : Number(next));
          }}
        >
          <option value="">Not recorded</option>
          {companySourceOptions(sources, stored).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * What kind of organisation this is, read-only — the branch for a reader who may
 * not write companies, and the same shape as `SourceValue` above.
 *
 * ⚠️ **`null` is "Not recorded", and it is the MAJORITY of rows** — every
 * company predating the column reads back `null`. So this subdued treatment is
 * what most of the column looks like and must not read as a fault.
 *
 * ⛔ **NEVER coalesce a null type to `Institution`.** It is the seeded first row
 * and the overwhelmingly likely answer, which is exactly what makes filling it
 * in silently so tempting and so wrong: "nobody has classified this account" and
 * "somebody classified this account as an institution" are different facts, and
 * only one of them is a claim this app is entitled to make on a customer record.
 */
function CompanyTypeValue({ companyType }: { companyType: MasterRef | null }) {
  if (!companyType) return <span className="text-xs text-fg-subtle">Not recorded</span>;
  return (
    <span className="text-sm text-fg-secondary">
      {companyTypeLabel(companyType.id, companyType.name)}
    </span>
  );
}

/**
 * The Credit balance column — read-only, one figure per currency, NEVER summed.
 *
 * The three states come from `creditHeldOf`, the one place they are told apart:
 * a balance this reader cannot see renders a subdued dash whose `title` says so
 * (never *no credit*), no credit is the plain em dash, and held credit is one
 * pill per currency (`balancePills`, 2026-09-25) that WRAPS rather than
 * truncates, with `balanceTitle` kept on the `title`.
 */
function CreditBalanceValue({
  balances,
}: {
  balances: readonly CompanyCreditBalance[] | null | undefined;
}) {
  const held = creditHeldOf(balances);
  if (held.kind === 'unknown') {
    return (
      <span className="text-xs text-fg-subtle" title={CREDIT_BALANCE_NOT_VISIBLE}>
        {balanceLabel([])}
      </span>
    );
  }
  if (held.kind === 'none') {
    return <span className="text-sm text-fg-subtle">{balanceLabel([])}</span>;
  }
  return (
    <ValuePills
      // `max-w-38` (152px) is the 176px column less `TD`'s `px-3` on both sides:
      // this table is not `table-fixed`, so without a cap the content would
      // widen the column rather than wrap. One pill per currency, never summed.
      className="max-w-38 tabular-nums"
      items={balancePills(held.balances)}
      title={balanceTitle(held.balances)}
    />
  );
}

/**
 * The Type column, edited in place — the same idiom as `SourceCell` beside it,
 * and a `Select` for the same reason: two curated options is a choice, not a
 * scroll. The searchable treatment the owner asked for on 2026-09-13 is about
 * FILTER dropdowns; this is an editor, and the column's own funnel carries a
 * search box already.
 *
 * Editing saves immediately, with no confirmation — the same trade `StageCell`
 * and `SourceCell` each state. The safety net is the rollback in
 * `changeCompanyType`.
 *
 * ⚠️ **Disabled, not hidden, while the master is loading or failed — and the
 * "Not recorded" row is FIRST, always.** A select offering nothing but the empty
 * row would be one click from clearing a type that is on screen beside it.
 * `companyTypeOptions` keeps whatever this row holds in the list either way,
 * including a retired row, so the value stays visible through a failed read.
 */
function CompanyTypeCell({
  row,
  companyTypes,
  disabled,
  saving,
  error,
  onChange,
}: {
  row: Company;
  companyTypes: ReferenceRecord[];
  /** Master not usable yet: loading, or the fetch failed. */
  disabled: boolean;
  saving: boolean;
  error: string | undefined;
  onChange: (row: Company, companyTypeId: number | null) => void;
}) {
  const stored = row.companyType;

  return (
    <Field label={`Type for ${row.name}`} hideLabel error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Select
          id={id}
          aria-describedby={describedBy}
          value={stored ? String(stored.id) : ''}
          invalid={Boolean(error)}
          disabled={disabled || saving}
          onChange={(event) => {
            const next = event.target.value;
            // Clearing is a real choice and `company_type_id` is nullable, so
            // `null` is what says it — never `''`, which a native select yields
            // for the empty row and which is a 422 on a reference id.
            onChange(row, next === '' ? null : Number(next));
          }}
        >
          <option value="">Not recorded</option>
          {companyTypeOptions(companyTypes, stored).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/** The server's own limits, mirrored — never stricter, never looser. */
/** 61 characters is a 422 whose `details` name `shortName` (measured 2026-08-28). */
const COMPANY_SHORT_NAME_MAX = 60;

/**
 * `shortName` IS clearable — and it is the one text column on this endpoint
 * where `''` clears with a 200 rather than being refused. The write path still
 * converts to `null`, so this codebase never learns the looser habit; see
 * `saveShortName`.
 */
function validateCompanyShortName(value: string): string | null {
  if (value.trim().length > COMPANY_SHORT_NAME_MAX) {
    return `A short name cannot be longer than ${COMPANY_SHORT_NAME_MAX} characters.`;
  }
  return null;
}

/** `d_companies.billing_name` is `VARCHAR(255)`, the same limit as `name`. */
const COMPANY_BILLING_NAME_MAX = 255;

/**
 * `billingName` is REQUIRED (owner 2026-10-25, N3/N5): it is the Xero Contact
 * Name, always. A blank box is refused here; the server answers the same
 * write with 422 `BILLING_NAME_REQUIRED`, handled in `saveBillingName`.
 *
 * ⚠️ *SUPERSEDED 2026-10-25: "clearable: empty means Xero gets the university
 * name".*
 */
function validateCompanyBillingName(value: string): string | null {
  if (value.trim() === '') return 'Billing name is required.';
  if (value.trim().length > COMPANY_BILLING_NAME_MAX) {
    return `A billing name cannot be longer than ${COMPANY_BILLING_NAME_MAX} characters.`;
  }
  return null;
}

/**
 * What the two name editors need, assembled once by the page so twenty rows
 * cannot word one refusal twenty ways.
 *
 * `denial` is `denialReason(ability, 'update', 'companies')` — `null` when the
 * reader may write.
 */
interface CompanyNameEdit {
  denial: string | null;
  saveShortName: (row: Company, next: string) => Promise<InlineSaveResult>;
  saveBillingName: (row: Company, next: string) => Promise<InlineSaveResult>;
}

/**
 * The billing name cell, edited in place — the table column and the phone
 * card both render this, so the two cannot disagree.
 *
 * ⚠️ **`undefined` is an older API, not an empty billing name** (CLAUDE.md
 * §4.6): it renders `—` with no editor, because a PATCH the server does not
 * know would be stripped and answered 200.
 */
function CompanyBillingName({ row, edit }: { row: Company; edit: CompanyNameEdit }) {
  if (row.billingName === undefined) {
    return <span className="text-sm text-fg-subtle">—</span>;
  }
  const stored = row.billingName?.trim() ?? '';
  return (
    <InlineField
      label={`${BILLING_NAME_LABEL} for ${row.name}`}
      value={stored}
      maxLength={COMPANY_BILLING_NAME_MAX}
      validate={validateCompanyBillingName}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      emptyLabel="No billing name"
      display={
        stored ? (
          <span className="text-sm text-fg [overflow-wrap:anywhere]">{stored}</span>
        ) : undefined
      }
      onSave={(next) => edit.saveBillingName(row, next)}
    />
  );
}

/**
 * The company name cell — the full name, then the short name on a secondary
 * line. Both names are edited in place.
 *
 * ⚠️ **The billing city USED to sit on that secondary line and was removed on
 * the owner's instruction (2026-08-30): "Remove the addr / city from the
 * companies listing page."** Only the rendering went. `?search=` still matches
 * `billingCity` SERVER-SIDE, which is why the search placeholder and the
 * board/empty-state copy still say the table searches on city — those sentences
 * describe the request, not this cell, and they are worth MORE now that the
 * value is invisible: a search on a city returns rows with nothing on screen
 * explaining why they matched.
 *
 * ⚠️ **`shortName` is shown BESIDE the full name, never INSTEAD of it** —
 * `companyDocumentName()` (which SUBSTITUTES) is deliberately NOT called here.
 * Substituting is right on a quote or invoice, where the reader already knows
 * the account. A LIST is the opposite case: the rows are what tell accounts
 * apart. `HSNC University - Thadomal College of Engineering` and
 * `HSNC University - K C College` could both legitimately shorten to `HSNC`,
 * so substituting would print what reads as one duplicated row.
 *
 * ⚠️ **Empty is the ORDINARY case, permanently.** `shortName` is null on all
 * 148 companies today, Xero has no equivalent field, and nothing will
 * bulk-populate it — a short name must never be derived from the full one. It
 * gets no column of its own, no filter and no sort (`/companies` sorts on
 * three keys only, and an unsupported param is stripped silently); it is
 * simply the field that is usually empty, which is why it needs a visible
 * empty LABEL rather than an absent line — otherwise there is nothing to click
 * on the 148 rows where the value is missing, and a missing affordance is
 * indistinguishable from a removed feature.
 *
 * ⚠️ **The name text is the `display`, and the LINK is the `action`.**
 * *SUPERSEDED and RESTORED on the same day, 2026-09-17.* For part of that day
 * the MAIL button sat here instead, on the owner's instruction ("remove the
 * arrow button beside the company name and put a mail button there instead"),
 * with `OpenAccountLink` rehoming the arrow into the actions lane. Shown the
 * result, the owner asked for the arrow back and for mail to be the control in
 * the lane. **Both halves moved in ONE change**, because dropping one before
 * adding the other is how a record page becomes unreachable — the failure
 * `ACTIONS_COLUMN_KEY` was written about, and one this repo has already had
 * reported by the owner as a DELETED FEATURE.
 *
 * The trigger is a `<button>`, so neither a link nor a button can nest inside
 * it; `InlineField` renders `action` beside the trigger in both branches, so a
 * reader who may not write still gets it. Same shape as `ProjectNameCell`.
 *
 * 🔴 **THIS IS THE ONLY LINK TO `/companies/:id` FROM EITHER LIST MOUNT.** The
 * name slot may not be reused again without rehoming it first.
 *
 * ⚠️ **`CompanyName` renders on the table AND the phone card, which is what
 * keeps the two agreeing about where "open this account" is.** That property is
 * why `OpenAccountLink` — which existed for the few hours the arrow lived in
 * two different-looking places — was DELETED rather than left uncalled: a
 * finished component with no call sites is indistinguishable from a shipped
 * one, and `tsc` cannot tell them apart.
 *
 * ⚠️ **The mail button is NOT gated on `edit.denial` in the lane either** —
 * that sentence is about writing a name, and reading contacts is open to every
 * role. A read gated on a write is a defect this codebase already records
 * against the staff directory.
 *
 * The secondary line now renders ALWAYS rather than only when populated, which
 * makes every loaded row two lines tall — the shape `TableSkeleton` and
 * `ListSkeleton` already draw.
 */
function CompanyName({ row, edit }: { row: Company; edit: CompanyNameEdit }) {
  return (
    <div className="min-w-0">
      {/* 🔴 **The name is a LINK, not an editor — owner, 2026-10-25:** *"make it
          clickable like the dashboard … and not editable from the list view"*.
          Same shape as the University Partnerships record cell. The name is
          renamed on the record page; the arrow button that sat beside the
          inline editor went with it. A router `<Link>`, never `<Button href>`
          (a full page load). */}
      <Link
        to={`/companies/${row.id}`}
        className="block rounded-sm text-sm font-medium text-fg underline-offset-2 [overflow-wrap:anywhere] hover:underline"
      >
        {row.name}
      </Link>
      {/* The secondary line is the short name alone now that the city is gone,
          so it is a plain block rather than the flex row that shared it.
          Blank-but-present is treated as unset, the same rule
          `companyDocumentName()` applies — a `shortName` of spaces is somebody
          having cleared the box. */}
      <div className="mt-0.5 min-w-0 text-xs text-fg-muted">
        <InlineField
          label={`Short name for ${row.name}`}
          value={row.shortName?.trim() ?? ''}
          maxLength={COMPANY_SHORT_NAME_MAX}
          validate={validateCompanyShortName}
          disabled={edit.denial !== null}
          {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
          // A reader who cannot write must not be invited to add one.
          emptyLabel={edit.denial === null ? 'Add short name' : 'No short name'}
          display={
            row.shortName?.trim() ? (
              <span className="text-xs text-fg-muted [overflow-wrap:anywhere]">
                {row.shortName.trim()}
              </span>
            ) : undefined
          }
          onSave={(next) => edit.saveShortName(row, next)}
        />
      </div>
    </div>
  );
}

/*
 * ⚠️ **`OpenAccountLink` AND `OPEN_LINK_SHAPES` LIVED HERE FOR PART OF
 * 2026-09-17 AND ARE DELETED, not left uncalled.** They existed while the arrow
 * sat in the actions lane, and they restated `Button`'s ghost and secondary
 * shapes as raw class strings because `Button` has no router-link form. With
 * the arrow back in `CompanyName` — which BOTH list mounts render — there is
 * one copy again without a restated shape to drift, so the whole mechanism went
 * rather than half of it.
 *
 * ⚠️ **The reason for the restatement is NOT retired and is recorded at the
 * `action` above**: `Button href` is a plain `<a>` and reloads the bundle, so
 * any future "open this record" control here is a `<Link>` with the shape
 * written out, or `Button` gains a router-link form — a change to a shared
 * primitive, which is the real fix and nobody has asked for it.
 */

/**
 * The Country cell's options — **`f_countries` IDS, as strings**.
 *
 * 🔴 **REWRITTEN 2026-08-31: this column now edits `countryId`, the FOREIGN
 * KEY, and no longer edits the free-text `billingCountry`.** *SUPERSEDED, in
 * full: "`billingCountry` is a VARCHAR, not a foreign key. There is no
 * `country_id` on a company and `/countries` is upstream geography that the
 * column has never pointed at — so this picker CONSTRAINS THE INPUT and
 * constrains nothing else … A stored value need not match any option … It is
 * never 'corrected' on load."* Every clause of that described a free-text
 * column, and the owner's decision made the key the single source of truth: the
 * free-text billing country is no longer collected or displayed on a company.
 *
 * ⚠️ **The injected-stored-value defence SURVIVES, and only its SHAPE changed.**
 * A free-text value could fail to match because somebody typed `UK`; an id
 * either resolves against the master or it does not, and the only way it does
 * not is a country the master no longer returns for this reader — a retired
 * row, or a read that came back short. The injection is what keeps that value
 * on screen: a `Combobox` whose `value` matches no option renders its
 * PLACEHOLDER, and the next save would then write the placeholder's meaning
 * (nothing) over a real country — a `200` silently reassigning the field the
 * server derives a customer's currency from.
 *
 * ⚠️ **The label is read DEFENSIVELY from the embed.** `CompanyCountryRef.name`
 * was declared as `countryName` in this app until 2026-08-31 and the server has
 * never sent that spelling, so a mis-declared embed prints `undefined` in a
 * value slot. `Country #id` names the row without claiming to know what it is
 * called. Same guard as `CompanyFormModal`'s `CountryField`.
 *
 * ⚠️ **`isActive` narrows HERE, not on the fetch, and that is deliberate.** The
 * house rule is that a picker over a master offers active rows only — true, and
 * it now applies because this is a foreign key rather than free text. But the
 * page's single `/countries` read also feeds the column's FUNNEL, which must
 * NOT be narrowed: a company may legitimately hold a retired country, and
 * hiding it there makes those rows unreachable through the only control that
 * can reach them (see `countryFilterOptions`). One unfiltered read, narrowed at
 * the one consumer that must narrow, beats a second request.
 */
function countryOptions(
  countries: Country[],
  stored: Company['country'],
): { value: string; label: string }[] {
  const options = countries
    .filter((country) => country.isActive)
    .map((country) => ({
      value: String(country.id),
      label: country.countryName,
    }));
  if (stored !== null && !options.some((option) => option.value === String(stored.id))) {
    const name = typeof stored.name === 'string' ? stored.name.trim() : '';
    // First, not appended: it is the value on screen, and a reader scanning 195
    // options for the one they already hold should not have to reach the end.
    options.unshift({
      value: String(stored.id),
      label: `${name !== '' ? name : `Country #${stored.id}`} — no longer listed`,
    });
  }
  return options;
}

/**
 * The company's country of record, read-only — the branch for a reader who may
 * not write companies, and the same shape as `StageValue` and `OwnerCell`.
 */
function CountryValue({ country }: { country: string | null }) {
  const value = country?.trim() ?? '';
  if (value === '') return <span className="text-xs text-fg-subtle">Not recorded</span>;
  return <span className="text-sm text-fg-secondary">{value}</span>;
}

/**
 * The Country column, edited in place — the same idiom as `StageCell` and
 * `StaffCell`, a `Combobox` rather than a `Select` because 195 options past a
 * native dropdown is a scroll, not a choice.
 *
 * ⚠️ **It writes `countryId`, a number or `null`** — the country of record, and
 * the same column the funnel above it filters on. There is exactly one country
 * on a company row now, so the header, the funnel and this editor all name the
 * same field; the merged column replaced a pair that showed two.
 *
 * ⚠️ **Disabled, not hidden, while `/countries` is unread or failed — and the
 * STORED VALUE STAYS VISIBLE THROUGH IT.** `countryOptions` injects it whether
 * or not the master was read, so a failed fetch leaves the reader looking at
 * what the company holds rather than at a placeholder, and cannot be one click
 * from clearing it. A failed read is "we do not know the countries", never
 * "there are none" — the page says which, above the table.
 */
function CountryCell({
  row,
  countries,
  disabled,
  saving,
  error,
  onChange,
}: {
  row: Company;
  countries: Country[];
  /** The master is not usable: not read yet, empty, or the fetch failed. */
  disabled: boolean;
  saving: boolean;
  error: string | undefined;
  onChange: (row: Company, next: number | null) => void;
}) {
  const stored = row.country === null ? '' : String(row.country.id);

  return (
    <Field label={`Country for ${row.name}`} hideLabel error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          aria-describedby={describedBy}
          value={stored}
          options={countryOptions(countries, row.country)}
          placeholder="Not recorded"
          // Clearing is a real choice and `country_id` is nullable, so `null`
          // is what says it — never `''`, which is a 422 on a reference id.
          emptyOptionLabel="Not recorded"
          searchPlaceholder="Search countries…"
          invalid={Boolean(error)}
          disabled={disabled || saving}
          onChange={(next) => {
            const wanted = next.trim();
            if (wanted === stored) return; // An untouched field sends nothing.
            onChange(row, wanted === '' ? null : Number(wanted));
          }}
        />
      )}
    </Field>
  );
}

/**
 * One icon button per available action, so the column is exactly as wide as
 * it needs to be. Zero is a real case — a `viewer` can neither create,
 * update nor delete — handled by not rendering the column at all.
 */
// Indexed by how many controls actually render. SIX is the admin's row — open,
// activity, create, resend-for-approval, edit, delete. ⚠️ *SUPERSEDED
// 2026-09-17: "Five … activity, create, resend-for-approval, edit, delete",
// when the link to the record lived beside the name instead.* ⚠️ Add an entry
// when you add a control: an index past the end yields `undefined`, which `cx`
// drops silently, and the column then sizes itself to its content instead.
// Each step is one 32px control plus the `gap-1.5`, rounded to the scale.
const ACTION_COL_WIDTHS = ['', 'w-20', 'w-28', 'w-36', 'w-44', 'w-52', 'w-60'] as const;

/**
 * ⛔ **THE ACTIONS LANE, AND IT IS LOCKED.** Every per-record control lives in
 * it — tasks and notes, the "+" create menu, resend-for-approval, Edit and
 * Delete — and the board is the only other place any of them appear, behind a
 * `cardAction` that carries just one of the five. This repo has already had
 * that column made unreachable once (unpinned, it sat past the right edge from
 * 1440 down) and **the owner reported the "+" menu as DELETED, not as hidden**.
 * So it is never hideable, never movable, and it keeps `pinned` rather than
 * joining the freeze model: `pinned` is `sticky right-0`, which is exactly a
 * right freeze at offset zero, and letting the two compete would put a
 * `right-0` class and a measured `right: Npx` on one cell.
 */
const ACTIONS_COLUMN_KEY = 'actions';

/**
 * 🔴 **THE DESKTOP TABLE'S COLUMNS, IN DECLARED ORDER — AND THE ONE LIST THE
 * HEADER, THE BODY AND THE SKELETON ARE ALL DRAWN FROM.**
 *
 * ⚠️ *SUPERSEDED: four parallel hand-written orderings* — the header, the body
 * `<TD>`s, `TableSkeleton`, and (still separate, see below) the mobile cards.
 * The header's own comment recorded the cost in as many words: *"miss one and
 * they misalign … miss the skeleton and it jumps sideways when data lands."*
 * Three lists that must agree, with nothing checking that they do, is the
 * `min-w-` sum problem in a second form — and it is what made hiding or
 * reordering a column inexpressible, because there was no single place a
 * column's identity lived.
 *
 * ⚠️ **The sub-`md` CARD LIST IS DELIBERATELY NOT DRIVEN FROM THIS.** A card has
 * no columns — it is a captioned stack, and every caption is already visible at
 * once — so there is nothing there to hide, reorder or freeze. Driving it from
 * a column layout would let a preference set on a laptop blank a field on a
 * phone, where it is the ONLY way to read it.
 *
 * ⚠️ **`createdAt` and `updatedAt` are NOT here.** They are sort-only column
 * specs with no header and no cell (see `SORT_ONLY_COLUMN_KEYS`); listing them
 * would put two rows in the column manager that can never be shown.
 */
const COLUMN_KEYS = [
  'name',
  /*
   * The name the company's Xero Contact carries when it differs from the
   * university name (owner 2026-10-21). Directly after the name because it is
   * the other spelling of the same account. ⚠️ **HIDDEN BY DEFAULT for
   * everybody** — see `OPT_IN_COLUMN_KEYS`, which is how a column on this page
   * starts hidden when the stored layout can only ever subtract.
   */
  'billingName',
  'country',
  'stage',
  /*
   * ⚠️ *SUPERSEDED 2026-10-25: a single `'stageStatus'` key here.* The per-stage
   * `{Stage} status` columns (`stageStatus:<stageId>`, one per active stage with
   * active statuses, in stage order) are DYNAMIC, so they are not in this
   * literal list: `columnKeys` inserts them directly after `stage`, where the
   * old column sat, once the masters have loaded. The legacy id survives only
   * as a saved-layout alias (`legacyHiddenStageStatusIds`).
   */
  // The resurface date a sub-stage set (spec 2026-10-23). Opt-in like Billing
  // name (`OPT_IN_COLUMN_KEYS`); read-only — it is set through the Stage status
  // dialog. No spec, so no funnel and no sort: `/companies` takes neither.
  'followUpOn',
  // Beside Stage rather than at the end: both answer "what kind of account is
  // this", and a reader comparing where an account came from against where it
  // has got to should not have to cross four columns to do it.
  'source',
  // Beside Source, and NOT a second spelling of it. A source is where an
  // account arrived FROM; a type is what the organisation IS — a university or
  // a partner agency. They sit together because a reader classifying an account
  // answers both in one pass, which is also why the two headings must stay
  // visibly different words rather than "Source" and "Type of source".
  'companyType',
  'accountLead',
  'clientSuccessManager',
  'regions',
  'projectOfInterest',
  /*
   * What the account has actually BOUGHT and the projects it has TAKEN PART IN
   * — owner's decision, read-only, from the `purchaseHistory` embed on the list
   * row. Directly after Projects of interest because the three read as one
   * question (wanted / bought / took part in). ⚠️ *SUPERSEDED 2026-10-09: "No
   * funnel and no sort: the server offers neither."* Both carry a funnel now —
   * a server-side CYCLE filter (`participatedCycleId` / `purchasedCycleId`) and
   * a sort by the COUNT of distinct items, scoped to the filtered cycles.
   */
  'productsPurchased',
  'projectsParticipated',
  /*
   * ✅ THE CREDIT BALANCE COLUMN EXISTS — owner's decision 2026-09-24, reading
   * the `creditBalances` EMBED on the list row, which is the shape the note
   * below asked for. No per-row request is made, no sort and no funnel (the
   * server offers neither), read-only. The note is kept as the reasoning for
   * why the embed, and NOT a per-row lookup, is the only acceptable shape.
   */
  'creditBalance',
  /*
   * ⚠️ SUPERSEDED 2026-09-24, the verdict and not the reasoning:
   * "⛔ THERE IS NO `creditBalance` COLUMN, AND ITS ABSENCE IS A DECISION TAKEN
   * ON 2026-09-15 RATHER THAN A GAP." A company's credit balance is rendered on
   * `/companies/:id` (see `creditBalancesOf` there); it is deliberately not a
   * column here, for a reason about REQUESTS rather than about width.
   *
   * ── 🔴 A PER-ROW LOOKUP IS FORBIDDEN, AND IT IS THE ONLY SHAPE AVAILABLE ──
   *
   * A balance is not on the company row: `Company` carries no such field, and
   * the only read that could answer it is per company. This page serves up to
   * `perPage` 100 rows against the office's shared per-IP budget, so a column
   * here is ONE REQUEST PER ROW — which this app has
   * already watched go wrong: a viewport loop collected `429`s, the client read
   * them as a dead session, and redirected everybody to `/login`, after which
   * every later assertion in that run passed against a signed-out app.
   *
   * ⚠️ *SUPERSEDED: "the office's shared 120-requests-per-60s per-IP budget."*
   * It is **12,000/60s** (raised 2026-09-04, corrected 2026-09-17). ⛔ THE
   * OBJECTION IS THE SHAPE, NOT THE ARITHMETIC. A per-row lookup on a
   * paginated list is still forbidden and the `429` storm above really
   * happened — but 100 requests no longer exhausts that budget, so this
   * refusal must be re-derived from the shape and never from the numbers.
   *
   * ⚠️ A `Promise.all` over the page is the same defect wearing a batch's
   * clothes: 100 concurrent requests is still 100 requests. Staggering them
   * only narrows the window.
   *
   * ── ⛔ AND THERE IS NOTHING TO BATCH TODAY EITHER ──────────────────────────
   *
   * Measured in the backend on 2026-09-15: `balancesForCompanies` exists in
   * `credit-note-allocations.repository.ts` and already answers MANY companies
   * in ONE round trip — and it is reachable from no controller and no route.
   * Only `balancesForCompany` has a service wrapper, and that one is unrouted
   * as well. So even the single-company read this page would have had to loop
   * over does not exist yet.
   *
   * ── WHAT THIS COLUMN NEEDS, IF THE OWNER WANTS IT ─────────────────────────
   *
   * A `creditBalances: CompanyCreditBalance[]` EMBED on the company list row,
   * hydrated by `balancesForCompanies` in one query per page — the shape the
   * `company` embed and `lineProjects` already use on the document lists, and
   * the shape that makes the per-row cost a non-question rather than a risk to
   * manage. That is a backend ask; it is reported, not built around.
   *
   * ⚠️ AND IT IS STILL AN OWNER CALL AFTER THAT, because the cell is empty on
   * most rows: almost no company holds credit. A column empty on SOME rows is
   * ordinary here (`note` and `projectOfInterest` both are), so that is not a
   * refusal — but a value that is an em dash nearly all the way down, in a
   * table already at nine columns, is a width somebody has to agree to spend.
   *
   * ⛔ WHATEVER LANDS, IT IS ONE FIGURE PER CURRENCY AND NEVER A TOTAL. Use
   * `balanceLabel` / `balanceTitle` from `@/lib/credit-notes`, and render the
   * cell in BOTH the desktop table and the sub-`md` stacked card — they are two
   * code paths and a feature reachable only above a breakpoint is
   * indistinguishable from one that was removed.
   */
  /*
   * The account's LAST NOTE and the DATE it was added — owner's decision
   * 2026-10-02, both visible by default and NOT locked, so the column manager
   * moves, hides and freezes them like any other. Two columns rather than one
   * because the date is visible to every reader while the text and author are
   * visible only with `notes.read`; a reader without it still gets a sortable,
   * filterable date column rather than a cell that hides everything.
   */
  'lastNote',
  'lastNoteAt',
  /*
   * The account's LATEST TASK and the DATE it was added (owner, 2026-10-16) —
   * the same two-column split and the same default visibility as the note pair.
   */
  'lastTask',
  'lastTaskAt',
  ACTIONS_COLUMN_KEY,
] as const;

/** The keys declared literally above. */
type StaticCompanyColumnKey = (typeof COLUMN_KEYS)[number];
/** A per-stage status column: `stageStatus:<stageId>` (never rename — stored in layouts). */
type StageStatusColumnKey = `stageStatus:${number}`;
type CompanyColumnKey = StaticCompanyColumnKey | StageStatusColumnKey;
/** Every key except the actions lane — the ones that have a real cell renderer. */
type CompanyDataColumnKey = Exclude<CompanyColumnKey, typeof ACTIONS_COLUMN_KEY>;

function isStageStatusColumnKey(key: string): key is StageStatusColumnKey {
  return parseStageStatusColumnId(key) !== null;
}

/**
 * Module-level so its identity is stable: `useColumnLayout` memoises a `Set`
 * over it, and a fresh array each render would rebuild that every time.
 */
const LOCKED_COLUMN_KEYS: readonly string[] = [ACTIONS_COLUMN_KEY];

/**
 * 🔴 **OPT-IN COLUMNS: HIDDEN UNTIL A READER SHOWS THEM — and the stored
 * `hidden` entry for one of these keys means SHOWN.**
 *
 * `useColumnLayout` stores hidden ids only, so "a stored preference can only
 * ever subtract" and a new column is visible to everybody. It has no
 * default-hidden option, so this page inverts the meaning of the stored entry
 * for these keys and nothing else: absent (every existing layout, and Reset)
 * reads as hidden; present reads as shown. `useOptInColumnLayout` wraps the
 * controller so the header, body, skeleton, column manager and export all see
 * the inverted answer through the one `columnLayout` object.
 *
 * ⛔ **The stored document is therefore NOT readable on its own for these
 * keys.** If the hook ever gains a real default-hidden option, migrate this
 * key to it in the same change and treat a stored `billingName` in `hidden` as
 * "shown", or every reader who opted in loses the column and every one who did
 * not gains it.
 */
const OPT_IN_COLUMN_KEYS: ReadonlySet<string> = new Set(['billingName', 'followUpOn']);

/**
 * Columns the server's `GET /companies/export` does not know (its
 * `COMPANY_EXPORT_COLUMN_IDS`): sending one is a 422 for the whole file, so
 * they are left out of `columns` until the backend lists them. (`followUpOn`
 * is exported since 2026-10-23.)
 */
const NOT_EXPORTED_COLUMN_KEYS: ReadonlySet<string> = new Set([ACTIONS_COLUMN_KEY]);

/**
 * The controller with `OPT_IN_COLUMN_KEYS` inverted. Memoised for the same
 * reason the hook memoises its own: a fresh identity per render re-subscribes
 * everything that depends on it.
 *
 * ⚠️ **`showAll` is two calls, in order.** The hook's `showAll` clears every
 * stored hidden entry — which for an opt-in key means HIDING it — so the opt-in
 * key is toggled back to shown afterwards. Both are functional updates on the
 * hook's state, so the second sees the first's result whatever order React
 * flushes them in, and the hook's per-scope write queue sends them one at a time
 * and coalesces, so the race its own `showAll` note describes does not reopen.
 */
function useOptInColumnLayout(raw: ColumnLayoutController): ColumnLayoutController {
  return useMemo(() => {
    const isVisible = (key: string) =>
      OPT_IN_COLUMN_KEYS.has(key) ? !raw.isVisible(key) : raw.isVisible(key);
    const visibleKeys = raw.orderedKeys.filter(isVisible);
    const optInLive = raw.orderedKeys.filter((key) => OPT_IN_COLUMN_KEYS.has(key));
    return {
      ...raw,
      isVisible,
      visibleKeys,
      hiddenCount: raw.orderedKeys.length - visibleKeys.length,
      showAll: () => {
        raw.showAll();
        for (const key of optInLive) raw.toggleVisible(key);
      },
    };
  }, [raw]);
}

/**
 * Keys this page renders with `pinned` — the actions lane, and only ever that.
 *
 * ⚠️ **NOT THE SAME SET AS `LOCKED_COLUMN_KEYS`, even though they hold the same
 * one key today.** Locked is about hiding and reordering; pinned is about which
 * edge a cell already occupies, and the two answer different questions on every
 * other page (most lock their identifying column, which is also the best
 * freeze-LEFT candidate). Kept as two constants rather than one shared array so
 * a column added to either cannot silently join the other.
 *
 * Passed to **both** hooks, and each needs it for its own reason:
 * `useColumnLayout` so `setFrozen` refuses a freeze that would fight
 * `sticky right-0` — verified in a browser as a pressed button that moved
 * nothing — and `useFrozenOffsets` so the lane's WIDTH is counted, or the first
 * right-frozen column is handed offset 0 and disappears underneath it.
 */
const PINNED_COLUMN_KEYS: readonly string[] = [ACTIONS_COLUMN_KEY];

/**
 * Why a locked column cannot be hidden, in this page's own terms.
 *
 * ⚠️ **The shared default says only "This column cannot be hidden", which names
 * the rule and not the reason.** Here the reason is specific and is the whole
 * argument for the lock: every per-record control lives in that lane, and the
 * board — the only other view — carries just one of the five behind
 * `cardAction`. The owner has already reported that column, when unreachable, as
 * the "+" menu having been DELETED. A **why-this-is-disabled** sentence is one of
 * the four kinds the no-helper-text prohibition explicitly keeps.
 */
function columnLockReasonOf(key: string): string | undefined {
  return key === ACTIONS_COLUMN_KEY
    ? 'Tasks and notes, the create menu, Edit and Delete live only in this column'
    : undefined;
}

/**
 * 🔴 **THE HEADINGS FOR THE COLUMNS THAT CARRY NO `ColumnSpec` — AND THEREFORE
 * NO FUNNEL.**
 *
 * Every other column's label comes from its spec in `columns`, which is also
 * what puts a funnel on its header and a row in the mobile `FilterSheet`. These
 * two have no spec, so `headerFor` would answer with the KEY itself
 * (`projectOfInterest`), which is what the column manager would then call it.
 * One record, read by the header cell AND by `columnLabelOf`, so the two can
 * never disagree.
 *
 * ⛔ **`projectOfInterest` IS HERE BECAUSE IT MUST NOT BE FILTERABLE, not
 * because nobody got round to it.** There is no `?projectOfInterestId=` on
 * `/companies`, so a `scope: 'server'` spec would emit a param the server
 * IGNORES — a **200**, an unfiltered list, and a funnel drawn with ticks over
 * it. The page-scoped alternative is no better: `ColumnSpec.get` returns ONE
 * scalar per row and this cell is a SET, and a funnel accumulated from twenty
 * loaded rows offers a project precisely when some row on this page already
 * names it. **Both failures are silent, and both read as "no companies match".**
 *
 * ⚠️ Adding a spec for this key is therefore not a small improvement — it is
 * the change that puts a lying control on the header AND a row in the mobile
 * filter sheet. It waits on a real query param.
 */
const UNFILTERED_COLUMN_LABELS = {
  // Spelled out rather than written as a computed `[ACTIONS_COLUMN_KEY]` key,
  // for the same reason `COLUMN_WIDTHS` spells it: an unambiguously exhaustive
  // `satisfies` check.
  actions: 'Actions',
  // No spec and so no funnel: `/companies` takes no billing-name filter or
  // sort, and `?search=` is the server's business.
  billingName: BILLING_NAME_LABEL,
  // No spec and so no funnel: `/companies` takes no follow-up filter or sort.
  followUpOn: FOLLOW_UP_ON_LABEL,
  projectOfInterest: PROJECTS_OF_INTEREST_LABEL,
  // ⚠️ *SUPERSEDED 2026-10-09: `productsPurchased` and `projectsParticipated`
  // sat here with "`/companies` takes no purchase filter".* It does now
  // (`purchasedCycleId` / `participatedCycleId`), so both have a spec in
  // `columns` and their headings come from it.
  // No spec and so no funnel: `/companies` takes no credit filter and no credit
  // sort, and a page-scoped funnel over one page of N would narrow a fraction
  // of the accounts under a heading that reads as all of them.
  creditBalance: CREDIT_BALANCE_LABEL,
  // No spec and so no funnel: `/companies` takes no note-text filter, and a
  // page-scoped one over twenty loaded rows would narrow a fraction of the
  // accounts under a heading that reads as all of them. The DATE column beside
  // it carries the server-side range filter and the sort.
  lastNote: ACCOUNT_LAST_NOTE_LABEL,
  // No spec for the same reason as `lastNote`: no task-text filter on
  // `/companies`. The date column beside it carries the range and the sort.
  lastTask: ACCOUNT_LAST_TASK_LABEL,
} as const satisfies Partial<Record<CompanyColumnKey, string>>;

/**
 * The heading for a column with no spec, or `undefined` when the column has one
 * and `headerFor` should answer instead.
 *
 * `Object.hasOwn`, not `in` — the same reason `isStaffColumn` gives: a key that
 * walks the prototype chain would index the record as `undefined` and be read
 * as a column that does have a spec.
 */
function unfilteredColumnLabel(key: string): string | undefined {
  return Object.hasOwn(UNFILTERED_COLUMN_LABELS, key)
    ? UNFILTERED_COLUMN_LABELS[key as keyof typeof UNFILTERED_COLUMN_LABELS]
    : undefined;
}

function isCompanyColumnKey(key: string): key is CompanyColumnKey {
  return (COLUMN_KEYS as readonly string[]).includes(key) || isStageStatusColumnKey(key);
}

/**
 * 🔴 **THE ONE PLACE A COLUMN'S WIDTH LIVES — the Tailwind class the `<th>`
 * wears AND the number the table's `min-width` is summed from.**
 *
 * ⚠️ *SUPERSEDED: a hand-summed arbitrary-value min-width class of 1568px,
 * written TWICE* (on the skeleton table and on the populated one) *with the
 * arithmetic kept in a comment beside the header.*
 *
 * ⚠️ **The old class is spelled out in prose HERE AND NOWHERE ELSE, and it is
 * deliberately broken up.** Tailwind v4 scans SOURCE TEXT, comments included —
 * writing the literal out would emit a rule for a width nothing uses, and would
 * defeat the next person grepping the served bundle to prove it had gone.
 *
 * Two copies of a sum over six widths declared
 * somewhere else is three things that can drift, and the file had already
 * carried a stale one through the Billing-country merge (1776 → 1568).
 *
 * ⛔ **THE SUM MUST BE AN INLINE `style`, NEVER A CLASS.** Tailwind v4 scans
 * SOURCE TEXT, so `min-w-[${n}px]` emits no rule at all — the class lands in
 * the DOM and does nothing, which looks exactly like a table that chose not to
 * reserve the width.
 *
 * ✅ **It goes on `<Table style>` directly.** *SUPERSEDED: "`Table` takes no
 * `style`, so the number goes on a plain wrapper `<div>` inside `TableWrap` …
 * That div must stay free of `overflow`, `transform` and `position`, or it
 * becomes the scroll/containing block and every frozen cell sticks to the wrong
 * thing."* `Table` accepts `style` as of 2026-09-12, so the spacer div is gone
 * and with it the standing hazard that somebody would one day give it a utility
 * that made it a containing block.
 *
 * ⚠️ **The actions lane counts at its WIDEST (240px = `w-60`, the six-control
 * admin row) whenever it renders**, which is what the superseded comment did
 * too: the sum is a FLOOR for the table, so it is cut for the widest case
 * rather than for whichever role happens to be looking. Sizing it per role
 * would make the same page a different width for two people. ⚠️ *SUPERSEDED
 * 2026-09-17: "208px = `w-52`, the five-control admin row", before the link to
 * the record moved into the lane from beside the name.* **That figure and
 * `ACTION_COL_WIDTHS`' last entry are one hand-kept correspondence** — the
 * class is what the cell is drawn at and the px is what the table reserves for
 * it, so a pair that disagrees gives a floor that is not the width the lane
 * takes, and the frozen columns beside it settle at the wrong offsets.
 */
const COLUMN_WIDTHS = {
  name: { px: 320, className: 'w-80' },
  // A Xero Contact name, usually the university name plus a suffix. 256 is
  // `w-64`, the hand-kept pair.
  billingName: { px: 256, className: 'w-64' },
  country: { px: 208, className: 'w-52' },
  stage: { px: 208, className: 'w-52' },
  // (The per-stage status columns are sized by `STAGE_STATUS_COLUMN_WIDTH`.)
  // One date on one line. 144 is `w-36`.
  followUpOn: { px: 144, className: 'w-36' },
  // The same 208 as Stage, and cut the same way: a one-line master label in a
  // picker with a clear affordance, not a free-text column. The five seeded
  // names are short (`Social Media` is the longest), but the master is
  // admin-editable, so this is sized for the control rather than for today's
  // rows.
  source: { px: 208, className: 'w-52' },
  // The same 208 as Source beside it, and cut the same way: a one-line master
  // label in a picker with a clear affordance. ⚠️ **The `px` and the class are
  // the one hand-kept correspondence in this record** — 208 is `w-52`, and a
  // pair that disagrees gives a table whose reserved floor is not the width its
  // headers actually take.
  companyType: { px: 208, className: 'w-52' },
  accountLead: { px: 192, className: 'w-48' },
  clientSuccessManager: { px: 208, className: 'w-52' },
  regions: { px: 224, className: 'w-56' },
  // Wider than Regions because the VALUES are longer, not because the set is:
  // a project reads `BSF India - Summer 2027`, where a region reads `India`.
  // The cell collapses to a summary rather than listing chips, so this is cut
  // for one project's name plus the summary wording, not for four of them.
  projectOfInterest: { px: 256, className: 'w-64' },
  // The same pills as Projects of interest, so the same width — a product or a
  // project reads `BSF India - Summer 2027`. 256 is `w-64`, the hand-kept pair.
  productsPurchased: { px: 256, className: 'w-64' },
  projectsParticipated: { px: 256, className: 'w-64' },
  // One currency's figure (`12,345.00 GBP`) fits on one line; a second or third
  // currency truncates and `balanceTitle` carries the whole set on the `title`.
  // 176 is `w-44` — the hand-kept pair again.
  creditBalance: { px: 176, className: 'w-44' },
  // An author line plus a body clamped to a few lines — wide enough that a
  // short note reads whole. 280 is `w-70`, the hand-kept pair.
  lastNote: { px: 280, className: 'w-70' },
  // One date on one line, plus the funnel and sort affordance in the header.
  // 176 is `w-44`.
  lastNoteAt: { px: 176, className: 'w-44' },
  // A title line over a due-and-assignee line. 256 is `w-64`.
  lastTask: { px: 256, className: 'w-64' },
  // The same one-date column as Last note added. 176 is `w-44`.
  lastTaskAt: { px: 176, className: 'w-44' },
  // The class is the caller's business here — it varies with `actionCount`
  // through `ACTION_COL_WIDTHS`. Only the px floor is fixed, and it is the LAST
  // entry of that array in pixels (`w-60`), never the lane a given reader sees.
  // Spelled out rather than written as a computed `[ACTIONS_COLUMN_KEY]` key so
  // the `satisfies` check below is unambiguously exhaustive.
  actions: { px: 240, className: '' },
} as const satisfies Record<StaticCompanyColumnKey, { px: number; className: string }>;

/**
 * Every per-stage status column's width: the same 208 as Stage — a one-line
 * master label in a picker. 208 is `w-52`, the hand-kept pair.
 */
const STAGE_STATUS_COLUMN_WIDTH = { px: 208, className: 'w-52' } as const;

/** A column's width record, static or per-stage. */
function columnWidthOf(key: CompanyColumnKey): { px: number; className: string } {
  return isStageStatusColumnKey(key) ? STAGE_STATUS_COLUMN_WIDTH : COLUMN_WIDTHS[key];
}

/**
 * One mechanism for sorting and filtering: `FilterableTH` carries the
 * funnel, and sort rows appear only for columns whose spec has a `sortKey`.
 *
 * Header cells are emitted from `columnKeys` — the SAME array, in the SAME
 * order, that the body and the skeleton map over, and the same one
 * `useFrozenOffsets` measures against. ⚠️ **`useFrozenOffsets` indexes this
 * row's `children` BY POSITION**, so this row must emit exactly one cell per
 * key, with no conditional cell inside the loop: an extra or missing `<th>`
 * silently shifts every frozen column's offset by one column's width.
 *
 * ✅ **The row carries the ref `useFrozenOffsets` measures.** *SUPERSEDED: a
 * callback on the min-width wrapper `<div>` that reached down with
 * `querySelector('thead > tr')`, because `TR` declared no `ref` and
 * `Table.tsx` was not this page's to widen.* `TR` takes one as of 2026-09-12,
 * so the row hands over itself and there is no selector to go stale against the
 * markup.
 */
function CompaniesTableHead({
  headerFor,
  actionCount,
  columnKeys,
  frozenSideOf,
  frozenOffsets,
  headRowRef,
}: {
  headerFor: TableFilters<Company>['headerFor'];
  actionCount: number;
  columnKeys: readonly CompanyColumnKey[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {columnKeys.map((key) => {
          /* Pinned to the right edge of the scroller — unpinned, this column
             (tasks/notes, create, edit, delete) sat past the edge from 1440
             down, reachable only by dragging sideways with nothing to say so.
             ⚠️ It takes `pinned` and NOT `frozen`: see `ACTIONS_COLUMN_KEY`.
             `columnKeys` omits it entirely when `actionCount` is 0, so there is
             no conditional cell inside this loop to shift the offsets. */
          if (key === ACTIONS_COLUMN_KEY) {
            return (
              <FilterableTH
                key={key}
                label={UNFILTERED_COLUMN_LABELS.actions}
                numeric
                pinned
                className={ACTION_COL_WIDTHS[actionCount]}
              />
            );
          }

          /* Every heading still comes from ONE expression and this loop still
             emits exactly one cell per key — which is what `useFrozenOffsets`
             indexes by position. The per-column notes that stood here survive
             on their `ColumnSpec`s in `columns` — which is where the funnel,
             the param and the label already lived, and the only place a
             column's meaning is decided. In particular: Country is the FOREIGN
             KEY column merged 2026-08-31 (header, funnel and inline editor
             finally name one field), and Stage's ticked values live in the URL,
             not in the hook, so `?stageId=3` stays a real link with one writer.

             ⛔ **A column with no spec gets its LABEL and NO `filter`.**
             `headerFor` hands back a `filter` for every column it knows, so
             "funnel or not" is decided by whether a spec exists — see
             `UNFILTERED_COLUMN_LABELS` for why Project of interest must not
             have one. `FilterableTH` renders a plain header when `filter` is
             omitted, exactly as the actions lane above relies on. */
          const unfiltered = unfilteredColumnLabel(key);
          return (
            <FilterableTH
              key={key}
              {...(unfiltered === undefined ? headerFor(key) : { label: unfiltered })}
              className={columnWidthOf(key).className}
              frozen={frozenSideOf(key)}
              frozenOffset={frozenOffsets[key]}
            />
          );
        })}
      </TR>
    </THead>
  );
}

/**
 * The shimmer for one column, keyed the same way everything else is.
 *
 * Each is control-shaped where the loaded cell is a control, and drawn that way
 * for EVERYONE — including the reader who gets the plain-text or
 * avatar-and-name branch instead. The skeleton follows the commonest shape
 * rather than branching on the role, because a skeleton that guesses wrong
 * about one reader resizes the table under them when data lands.
 */
function skeletonCellFor(key: CompanyDataColumnKey): ReactNode {
  // Every per-stage status column is a picker, shaped like Stage's.
  if (isStageStatusColumnKey(key)) return <Skeleton className="h-8 w-40" />;
  switch (key) {
    case 'name':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-2.5 w-24" />
        </div>
      );
    case 'regions':
      // Two chips, the commonest case — a company names one or two regions far
      // more often than four.
      return (
        <div className="flex flex-wrap items-center gap-1">
          <Skeleton className="h-4 w-16 rounded-full" />
          <Skeleton className="h-4 w-20 rounded-full" />
        </div>
      );
    case 'billingName':
      return <Skeleton className="h-3 w-40" />;
    case 'followUpOn':
      return <Skeleton className="h-3 w-24" />;
    case 'projectOfInterest':
      // One line of text, not chips: this cell COLLAPSES to a summary when
      // idle, so a chip row here would resize the column when data lands —
      // which is the whole failure the shared skeleton exists to prevent.
      return <Skeleton className="h-3 w-32" />;
    case 'productsPurchased':
    case 'projectsParticipated':
      return <Skeleton className="h-3 w-32" />;
    case 'creditBalance':
      return <Skeleton className="h-3 w-24" />;
    case 'lastNote':
      // An author line over a line of body text — the commonest loaded shape.
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-3 w-48" />
        </div>
      );
    case 'lastNoteAt':
      return <Skeleton className="h-3 w-28" />;
    case 'lastTask':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-2.5 w-28" />
        </div>
      );
    case 'lastTaskAt':
      return <Skeleton className="h-3 w-28" />;
    /*
     * 🔴 **`source` WAS MISSING FROM THIS SWITCH and nothing said so.** This
     * function returns `ReactNode`, which INCLUDES `undefined`, so a key with
     * no arm is not a build error — it is a cell that renders nothing while the
     * page loads and then takes a 208px control when data lands, which is
     * exactly the sideways jump this whole shared skeleton exists to prevent
     * and which the header's own comment records as the cost of the four
     * hand-written orderings it replaced.
     *
     * ⚠️ **`bodyCellFor` has the same unguarded shape and is complete today.**
     * Neither is exhaustiveness-checked, so the only thing keeping them in step
     * is somebody adding both arms in the same pass. A `never` default would
     * make that structural; it is not added here because this pass owns three
     * files and that is a change to how the page reports a missing column,
     * which deserves its own decision rather than riding along with a new one.
     */
    case 'country':
    case 'stage':
    case 'source':
    case 'companyType':
    case 'accountLead':
    case 'clientSuccessManager':
      return <Skeleton className="h-8 w-40" />;
  }
}

/**
 * ⚠️ **Driven from the SAME `columnKeys` as the header and the body, which is
 * the whole point of this change.** It used to be a third hand-written list, and
 * the header's own comment recorded what that cost: *"miss the skeleton and it
 * jumps sideways when data lands."* Now a hidden, reordered or frozen column
 * cannot be reflected in two of the three places and forgotten in the third.
 */
function TableSkeleton({
  actionCount,
  columnKeys,
  frozenSideOf,
  frozenOffsets,
}: {
  actionCount: number;
  columnKeys: readonly CompanyColumnKey[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columnKeys.map((key) =>
            key === ACTIONS_COLUMN_KEY ? (
              <TD key={key} numeric pinned>
                <div className="flex justify-end gap-1.5">
                  {Array.from({ length: actionCount }, (_, n) => (
                    <Skeleton key={n} className="h-6 w-6" />
                  ))}
                </div>
              </TD>
            ) : (
              <TD key={key} frozen={frozenSideOf(key)} frozenOffset={frozenOffsets[key]}>
                {skeletonCellFor(key)}
              </TD>
            ),
          )}
        </TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-36" />
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-32" />
          </div>
          <Skeleton className="h-4 w-10" />
        </li>
      ))}
    </ul>
  );
}

export function CompaniesPage() {
  const { ability, user } = useAuth();

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. An unseeded set, an
   * older API or a failed `/permissions/me` all leave the page visible. See
   * `can()` in `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /companies` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The `canCreate` / `canUpdate` / `canDelete` gates
   * below are WRITE gates and are untouched — a different question.
   */
  const canReadModule = ability.canRead('companies');

  const [result, setResult] = useState<Paginated<Company> | null>(null);
  // Remembered on the account (filter memory, owner 2026-10-16). Sort and the
  // funnels are remembered by `useTableFilters`; the page number never is.
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  // Every matching row in one list, remembered for the account. While on, the
  // pager's page is ignored by the fetch (not reset, so switching back lands
  // where the reader was) — see `load`.
  const [showAll, setShowAll, showAllLoaded] = useShowAll('companies');
  const [showAllTruncated, setShowAllTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** `null` closed, `{}` create, `{ company }` edit. */
  const [formState, setFormState] = useState<{ company?: Company } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Company | null>(null);
  /**
   * Which company the "+" is raising a record for, and which record — `null`
   * closed. ONE flow for the whole page, the same rule the activity drawer
   * states below: a row only says WHICH company it is for.
   *
   * ⚠️ The request carries `{id, name}` and NOTHING ELSE, because the flow
   * fetches the company itself. That is what lets the BOARD card mount it: a
   * `KanbanCard` carries none of a `Company`'s columns and has no
   * `projectsOfInterest` to hand over. Do not "optimise" the two list mounts by
   * passing projects they already hold — that would give the board a second
   * code path, and a control that behaves differently in one view is the
   * failure this page has already had reported once as a DELETED feature.
   */
  const [projectLinesRequest, setProjectLinesRequest] =
    useState<CompanyProjectLinesRequest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /**
   * How many notes on the account about to be deleted the VIEWER CAN SEE — read
   * when the confirmation opens, because a company delete now DESTROYS them
   * (backend migration 111, 2026-08-30).
   *
   * 🔴 A FLOOR, NOT A TOTAL: `/notes/summary` is visibility-scoped, so without
   * the audit grant this counts the viewer's own notes while the cascade
   * destroys everybody's. `null` is "not read yet, or the read failed", and is
   * worded identically to zero. See `company-delete.ts`.
   */
  const [pendingNoteTotal, setPendingNoteTotal] = useState<number | null>(null);
  /**
   * How many of that account's notes SEED EMERGE wrote itself — `systemCount`.
   *
   * 🔴 **A DIFFERENT KIND OF NUMBER from `pendingNoteTotal` above.** Every reader
   * may see every system note, so this one is EXACT and the same for everybody,
   * where the human count is viewer-scoped and only ever a floor. ⛔ Never summed
   * with it — see `company-delete.ts`.
   */
  const [pendingSystemNoteTotal, setPendingSystemNoteTotal] = useState(0);
  /** Which row's note read is still the live one — an earlier reply is dropped. */
  const noteTotalSeq = useRef(0);

  // The stage master, fetched once for the page — never once per row.
  const {
    records: stages,
    loading: stagesLoading,
    failed: stagesFailed,
  } = useCompanyStages();

  // The Stage status master — every status of every stage, once for the page.
  const {
    statuses: stageStatuses,
    loading: stageStatusesLoading,
    error: stageStatusesError,
  } = useCompanyStageStatuses();
  const stageStatusesFailed = stageStatusesError !== null;
  /*
   * The per-stage status columns (owner 2026-10-25), ALL stages — this list
   * holds every stage. Built from the two masters this page already reads
   * rather than through `useStageStatusColumns`, which would read the stage
   * master a second time; the builder is the same one the hook uses.
   *
   * ⚠️ **Empty until BOTH masters have loaded**, so no column flickers in with
   * the wrong set: while either is in flight (or failed) the table renders no
   * stage-status column at all, and the layout hook keeps any saved state for
   * the ids until they reappear.
   */
  const stageStatusColumns = useMemo<StageStatusColumn[]>(
    () =>
      stagesLoading || stageStatusesLoading || stagesFailed || stageStatusesFailed
        ? []
        : buildStageStatusColumns(stages, stageStatuses),
    [stages, stageStatuses, stagesLoading, stageStatusesLoading, stagesFailed, stageStatusesFailed],
  );
  const stageStatusColumnById = useMemo(
    () => new Map(stageStatusColumns.map((column) => [column.id, column])),
    [stageStatusColumns],
  );
  // Asks for a follow-up date / reason before a sub-stage that needs one is
  // saved (spec 2026-10-23). `stageStatusDialog` is mounted once, page level.
  const { request: requestStageStatusChange, dialog: stageStatusDialog } = useStageStatusChange();

  // The source master, fetched once for the page — never once per row, and on
  // the same terms as the stage master beside it.
  const {
    records: sources,
    loading: sourcesLoading,
    failed: sourcesFailed,
  } = useCompanySources();

  // The type master, on the same terms as the two above it — once per page.
  const {
    records: companyTypes,
    loading: companyTypesLoading,
    failed: companyTypesFailed,
  } = useCompanyTypes();

  // Per-row inline-edit state, keyed by company id — one pair per editable
  // column, so two columns saving at once on one row don't share a spinner or
  // overwrite each other's message. Regions is deliberately NOT here:
  // `InlineRegionsField` owns its own draft, phase and per-chip refusal marking.
  const [savingStageIds, setSavingStageIds] = useState<readonly number[]>([]);
  const [stageErrors, setStageErrors] = useState<Record<number, string>>({});
  // Its own pair: Stage and Stage status sit side by side.
  const [savingStageStatusIds, setSavingStageStatusIds] = useState<readonly number[]>([]);
  const [stageStatusErrors, setStageStatusErrors] = useState<Record<number, string>>({});
  // The two person columns are a pair each, not one shared — either can be
  // saving while the other is not, and a shared error map would put "Account
  // lead not saved" under the CSM select.
  const [savingLeadIds, setSavingLeadIds] = useState<readonly number[]>([]);
  const [leadErrors, setLeadErrors] = useState<Record<number, string>>({});
  /*
   * The pending ACCOUNT LEAD change awaiting "Effective from" — `null` is
   * closed. ONE `AccountLeadChangeDialog` for the page (mounted at the bottom),
   * reached from the desktop cell and the phone card alike.
   */
  const [leadChange, setLeadChange] = useState<{
    row: Company;
    nextUserId: number | null;
  } | null>(null);
  const [leadChangeBusy, setLeadChangeBusy] = useState(false);
  const [leadChangeError, setLeadChangeError] = useState<string | null>(null);
  const [savingCsmIds, setSavingCsmIds] = useState<readonly number[]>([]);
  const [csmErrors, setCsmErrors] = useState<Record<number, string>>({});
  const [savingCountryIds, setSavingCountryIds] = useState<readonly number[]>([]);
  const [countryErrors, setCountryErrors] = useState<Record<number, string>>({});
  // Its own pair, like every other editable column — a shared one would put
  // "Source not saved" under the Stage select on the same row.
  const [savingSourceIds, setSavingSourceIds] = useState<readonly number[]>([]);
  const [sourceErrors, setSourceErrors] = useState<Record<number, string>>({});
  // And its own pair again, for the same reason: Source and Type sit side by
  // side, so a shared map would put "Type not saved" under the Source select.
  const [savingCompanyTypeIds, setSavingCompanyTypeIds] = useState<readonly number[]>([]);
  const [companyTypeErrors, setCompanyTypeErrors] = useState<Record<number, string>>({});
  /**
   * A 403 carries no `details`, so it can never be a cell error. One banner
   * for every `<select>` in the table, not one per column — the refusals are
   * all the same one (the role lost `companies.update` under a live token).
   */
  const [writeBanner, setWriteBanner] = useState<string | null>(null);
  /** Announced politely: a select's own value change says nothing about the save. */
  const [writeStatus, setWriteStatus] = useState('');

  // Writing a company takes any write role (`requireRole(...WRITE_ROLES)`);
  // DELETE stays admin-only, which `canDelete` already encodes.
  const canCreate = ability.canCreate('companies');
  const canUpdate = ability.canUpdate('companies');
  const canDelete = ability.canDelete('companies');

  // Creating a deal, quote or invoice is gated on THAT module, not companies —
  // nothing the "+" menu offers writes a company. A target the user cannot
  // create is dropped from the menu rather than shown disabled.
  const createTargets = useMemo(
    () => COMPANY_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );

  /**
   * The "+" menu's interception, built once and used by ALL THREE mounts — the
   * board card, the stacked card and the table's actions cell.
   *
   * ⚠️ ONE FACTORY IS ITSELF THE DEFENCE. Three inline handlers would agree the
   * day they were written and drift on the first correction, and a "+" that
   * seeds lines on the desktop table while plain-navigating on a phone card is
   * indistinguishable from a feature that was removed — which is exactly what
   * the owner reported when this column was once made unreachable (see
   * `ACTIONS_COLUMN_KEY`).
   *
   * ⚠️ THE MENU'S MODULE AND THE FLOW'S KIND ARE DIFFERENT VOCABULARIES and the
   * names are one letter apart: the menu speaks module slugs (`quotes`), the
   * flow speaks a record kind (`quote`). They are mapped, never cast.
   *
   * Returns `true` for every target, so nothing here navigates to the `href`.
   * The `href` stays on the item regardless — it is the honest destination if
   * this interception is ever removed.
   */
  const openProjectLines = useCallback(
    (company: { id: number; name: string }) =>
      (module: CompanyCreateTarget['module']): boolean => {
        const kind = { quotes: 'quote', invoices: 'invoice', deals: 'deal' } as const;
        setProjectLinesRequest({
          kind: kind[module],
          // Rebuilt rather than passed through: a `Company` row carries fifty
          // columns the flow must not start depending on.
          company: { id: company.id, name: company.name },
        });
        return true;
      },
    [],
  );

  // Count what will actually render. ⚠️ *SUPERSEDED 2026-09-17: "there are
  // three possible controls, and a `viewer` sees no Actions column at all
  // rather than an empty one."* **A `viewer` sees the lane, with the two
  // ungated controls in it** — open, and tasks and notes. The empty-lane case
  // the column manager and the header were built around is now unreachable;
  // the machinery is left standing because it is what keeps the header from
  // emitting a conditional cell inside its loop, which is what keeps
  // `useFrozenOffsets`' positional measurement honest.
  /*
   * `POST /companies/:id/approval-request` is ADMIN_ROLES, so the control is
   * offered on the ROLE — `ability.isAdmin`, never `user.role` and never
   * `/permissions/me`. There is no slug that means "admin tier on companies",
   * and borrowing `companies.update` would enable it for `basic_user`, whom the
   * API answers 403. `resendDenialFor()` owns the sentence; this owns whether
   * the column reserves a slot for it.
   *
   * ⚠️ Reserved for every row, not only pending ones. The control disables
   * itself with its reason on a settled company, which keeps the icons aligned
   * down the column and keeps the action discoverable — where rendering it only
   * on pending rows would ripple the whole `justify-end` group sideways row by
   * row, and would make "this school is already approved" and "this build has no
   * resend button" look identical.
   */
  // 2026-10-01: the route is `companies.update` now and the set decides.
  // *SUPERSEDED, the comment above: `ability.isAdmin`, ADMIN_ROLES.*
  const canResendApproval = ability.canUpdate('companies');

  /*
   * ⚠️ **TWO unconditional controls now, not one.** The first is the activity
   * drawer (the task and note routers carry no role guard); the second is the
   * MAIL button, which arrived in this lane on 2026-09-17. ⚠️ *SUPERSEDED the
   * same day: "the second is `OpenAccountLink`, which arrived here when the
   * mail button took its place beside the name" — the owner then swapped the
   * two back, and the COUNT did not move, which is the only reason
   * `ACTION_COL_WIDTHS` and `COLUMN_WIDTHS.actions.px` needed no edit with
   * it.* Reading a company and reading its contacts are both open to every
   * role, so neither is gated and the lane can never be empty — which is why
   * the `actionCount > 0` branches below, true for a while already, are now
   * true by construction rather than by coincidence.
   *
   * ⚠️ **Adding a control here means adding an `ACTION_COL_WIDTHS` entry AND
   * moving `COLUMN_WIDTHS.actions.px` to match it**; an index past the end of
   * that array is `undefined`, which `cx` drops in silence. **A control merely
   * EXCHANGED for another costs neither** — say which you did, because the two
   * look identical in a diff of this line.
   */
  const actionCount =
    2 +
    (createTargets.length > 0 ? 1 : 0) +
    (canResendApproval ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);

  /*
   * The columns this table CAN show, before the reader's stored layout narrows
   * them. The actions lane drops out entirely when nothing renders in it, so
   * the column manager never lists an "Actions" row a `viewer` has no actions
   * for — and, just as importantly, the header emits no conditional cell inside
   * its loop, which is what keeps `useFrozenOffsets`' positional measurement
   * honest.
   */
  /*
   * The per-stage status columns are spliced in directly after Stage, where the
   * single Stage status column sat, so a new id lands at its default position
   * and visible (decision 7). Their ids are `StageStatusColumn.id`, which is
   * always `stageStatus:<stageId>`.
   */
  const columnKeys = useMemo<readonly CompanyColumnKey[]>(() => {
    const keys: CompanyColumnKey[] = [];
    for (const key of COLUMN_KEYS) {
      if (key === ACTIONS_COLUMN_KEY && actionCount === 0) continue;
      keys.push(key);
      if (key === 'stage') {
        for (const column of stageStatusColumns) {
          if (isStageStatusColumnKey(column.id)) keys.push(column.id);
        }
      }
    }
    return keys;
  }, [actionCount, stageStatusColumns]);

  /*
   * Saved layouts (decision 7): a person whose stored layout HID the legacy
   * `stageStatus` column gets every per-stage column hidden until they show
   * one. Memoised so the alias map keeps one identity per column set.
   */
  const columnLayoutOptions = useMemo(
    () => ({ legacyHiddenAliases: legacyHiddenStageStatusIds(stageStatusColumns) }),
    [stageStatusColumns],
  );

  /*
   * Column order, the hidden set and the two frozen edges — stored AGAINST THE
   * ACCOUNT under `preferences.columns.companies`.
   *
   * ⚠️ **The scope string is `'companies'`, the same word the saved FILTERS use
   * — and they cannot collide.** Filters live under `preferences.tables.<scope>`
   * and columns under `preferences.columns.<scope>`; the two are siblings, and
   * the filter store's whole-set replace treats every key it finds under
   * `tables` as one more filter to delete, which is exactly why a layout parked
   * in there would be wiped by the next filter save.
   */
  const storedColumnLayout = useColumnLayout(
    'companies',
    columnKeys,
    LOCKED_COLUMN_KEYS,
    PINNED_COLUMN_KEYS,
    columnLayoutOptions,
  );
  // Billing name and Follow up on start hidden for everybody — see
  // `OPT_IN_COLUMN_KEYS`. Every
  // reader below takes THIS object, never `storedColumnLayout`.
  const columnLayout = useOptInColumnLayout(storedColumnLayout);

  /*
   * ⛔ **The actions lane never answers a freeze**, and `PINNED_COLUMN_KEYS` is
   * now what says so — in BOTH hooks, which is why the local wrapper that used
   * to sit here is gone.
   *
   * ⚠️ *SUPERSEDED: a `useCallback` returning `undefined` for the actions key,
   * "withheld HERE rather than at the render sites so the offset arithmetic
   * below counts it out too: a cell that is not frozen must not consume
   * offset."* **That second clause had it exactly backwards and was a latent
   * bug.** A `pinned` cell is `sticky right-0`, so it DOES occupy the right
   * edge and its width MUST be counted — withholding it handed the first
   * right-frozen column offset 0, stacking it underneath the actions lane where
   * it simply disappears, opaque over opaque, with nothing erroring.
   * `useFrozenOffsets` takes the pinned keys itself now and counts the lane as
   * a right edge; `useColumnLayout` takes them too, so a freeze on that key can
   * no longer be stored at all. **Do not reintroduce a local wrapper here.**
   *
   * `columnLayout.frozenSideOf` is a `useCallback` over the two freeze lists,
   * so it is passed straight down — passing `columnLayout` itself would be a
   * fresh identity every render and re-subscribe the ResizeObserver inside
   * `useFrozenOffsets` on every keystroke in the search box.
   */
  const frozenSideOf = columnLayout.frozenSideOf;

  /*
   * What actually renders, in order. Narrowed back to the declared union so the
   * width table can be indexed without a cast — `applyOrder` already drops any
   * key the table no longer declares, so nothing is lost here.
   */
  const visibleColumnKeys = useMemo(
    () => columnLayout.visibleKeys.filter(isCompanyColumnKey),
    [columnLayout.visibleKeys],
  );

  /*
   * The header row `useFrozenOffsets` measures — an ORDINARY ref on `<TR>`.
   *
   * ⚠️ *SUPERSEDED: "**The header row is found by query rather than by a ref on
   * `<TR>`** … `TR`'s props extend `HTMLAttributes`, which does not declare
   * `ref` … So the callback goes on the min-width wrapper INSIDE each table
   * branch and reaches down for the row", plus a `useState` holding the node and
   * a `useMemo` minting a fresh ref OBJECT per node so the hook would notice the
   * skeleton→data subtree swap.* **Both halves of that are now the shared
   * pieces' business**: `TR` takes a `ref` as of 2026-09-12, and
   * `useFrozenOffsets` tracks the observed element's identity itself — it
   * compares the live node against the one it is observing on every render, so a
   * plain ref mutated by React across the two subtrees re-measures exactly as the
   * fresh-object trick did.
   *
   * ⛔ **Which also retires the reason the min-width spacer `<div>` existed**:
   * it hosted that callback. The floor now goes on `<Table style>` directly.
   */
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    visibleColumnKeys,
    frozenSideOf,
    PINNED_COLUMN_KEYS,
  );

  /*
   * The table's floor width, summed from the columns actually on screen.
   * ⚠️ *SUPERSEDED: a hand-summed 1568px min-width class, written twice* (see
   * `COLUMN_WIDTHS`). A fixed floor over a hideable column set keeps forcing a
   * phantom horizontal scroll for width nothing occupies — and there is nothing
   * on screen to say why the table is wider than its contents.
   */
  const tableMinWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + columnWidthOf(key).px, 0),
    [visibleColumnKeys],
  );

  const rows = useMemo(() => result?.data ?? [], [result]);

  // The regions master, fetched once for the page — the Regions funnel's
  // option list, and the one column whose options cannot come from the
  // loaded rows: a company's regions are a SET, and `useTableFilters` can only
  // accumulate options from single-valued columns. Taking it from the master
  // avoids a region held by nobody on the loaded page being unreachable.
  // `useReferenceOptions` asks for active rows only; a RETIRED region a
  // company still holds is merged back in from the rows below.
  const {
    records: regionRecords,
    loading: regionsLoading,
    failed: regionsFailed,
  } = useReferenceOptions('regions');

  /*
   * The projects a company can be marked interested in.
   *
   * ⚠️ **Gated on `canUpdate`, and the regions master beside it is NOT — the
   * difference is real, not an oversight.** Regions has a FUNNEL, which every
   * role may open, so gating that read would hand a `viewer` an option list
   * accumulated from twenty loaded rows. Project of interest has no funnel (see
   * `UNFILTERED_COLUMN_LABELS`), so the PICKER is its only consumer, and a
   * reader's cell renders from names the ROW already carries. Fetching it for
   * somebody who can never open the picker is one more request against the
   * office-wide 12,000/60s (⚠️ *not the 120/60s this once said*) limiter, for a
   * list nothing would read.
   *
   * ⚠️ `hasMore` is surfaced, not swallowed: `/projects` caps at 100 and there
   * are ~97 live, so the 101st is close. A picker silently missing an option is
   * indistinguishable from that project not existing.
   */
  const {
    projects: projectRecords,
    loading: projectsLoading,
    failed: projectsFailed,
    hasMore: moreProjects,
  } = useProjectOptions(canUpdate);

  /*
   * Which cycles count as CURRENT, for the Project-of-interest picker. One
   * `/cycles` read for the whole page — **never one per row**: `projectsField`
   * runs per row and would otherwise be a request per row against the
   * office-wide 12,000/60s (⚠️ *not the 120/60s this once said*) limiter. The
   * pill is per row; the data is not.
   *
   * Gated on `canUpdate` for the same reason `useProjectOptions` above is: the
   * PICKER is the only consumer of the narrowing, a reader's cell renders from
   * the names the ROW already carries, and this column has no funnel to feed
   * (`UNFILTERED_COLUMN_LABELS`). Disabled, the hook returns
   * `UNKNOWN_CYCLE_SCOPES`, which offers everything — the safe direction.
   *
   * ⚠️ **`Project.cycle` is a `MasterRef` and carries NO dates**, so the
   * projects cannot classify themselves and this read is not avoidable by
   * reaching into the rows already in hand.
   */
  const projectCycleScopes = useCycleScopeIds(canUpdate);

  // No `useFaculties` call here — the Faculty column is gone, so the master
  // had no picker left to fill and the request was one more call against the
  // office-wide 12,000/60s (⚠️ not the 120/60s this once said) limiter.
  // `CompanyDetailPage` and `CompanyFormModal` still fetch it for their own.

  /*
   * The staff directory. Fetched once, and read by TWO consumers with
   * different audiences — the same split the countries master below carries,
   * for the same reason:
   *
   *   - the **Account lead / CSM** inline PICKERS, which only a writer sees;
   *   - those two columns' FUNNELS, which every role may use.
   *
   * ⚠️ **So it is no longer gated on `canUpdate`, and that is a real change.**
   * It used to be, which was correct while the pickers were the only consumer.
   * Left gated, both person funnels would offer a `viewer` nothing but the ids
   * the loaded twenty rows happen to name — and a funnel over one page of a
   * paginated list is a funnel that hides matches, with nothing in the popover
   * saying anybody is missing. `GET /users` carries no role guard at all
   * (`viewer` included), so a reader may read it; this is the same correction
   * the country funnel needed on 2026-08-31.
   *
   * ⚠️ **`withGroups` IS still `canUpdate`, and that is deliberate rather than
   * a half-finished change.** The `/user-groups` master narrows the two
   * PICKERS — naming somebody outside `account_lead` is a 422 from
   * `/companies` — and nothing else reads it. A FUNNEL must never be narrowed
   * to a group (see `staffFilterOptions`), so widening that fetch too would
   * spend a second request, against an office-wide 12,000/60s (⚠️ *not the
   * 120/60s this once said*) limiter, on a list a reader has no control for.
   * A writer's `byGroup` is exactly what it was.
   */
  //
  // ⚠️ `hasMore` is not decoration. `useUserOptions` reads ONE page at the
  // API's maximum of 100, so beyond that a person is absent from the list.
  //
  // ⚠️ *SUPERSEDED 2026-09-13, the REASON only: "and a native `<select>` has
  // nowhere to put a footnote, so it's said above the table instead."* That was
  // a true statement about a native control and is no longer why anything sits
  // above the table — `Combobox` takes a `footnote` prop, and the person
  // pickers this sentence is about are `InlineField`/`Combobox` already. **The
  // PLACEMENT is untouched and was NOT reopened in that pass** (owner's
  // decision was to convert the Sort and Cycle CONTROLS, nothing else); moving
  // a truncation notice into a popover is a layout decision somebody has to
  // take on its own. Recorded so the next reader does not inherit a limitation
  // that no longer exists as the argument for keeping the placement.
  /*
   * ⚠️ `hasMore` is no longer destructured — it gated "Pickers show the first
   * 100 staff only.", removed under the owner's stricter helper-text ruling
   * (2026-09-05), and had no other reader. `failed` STAYS: it also disables both
   * person pickers, and the banner above the table is a why-this-is-disabled
   * sentence rather than a load-failure narration.
   */
  /*
   * ⚠️ **Kept whole as well as destructured**, because
   * `useDefaultAccountLeadFilter` takes the object — it reads both `byGroup` and
   * the two loading flags, and a hand-built subset at this call site is a shape
   * that can drift from the hook's.
   */
  const staff = useUserOptions(true, canUpdate);
  const { users, loading: usersLoading, failed: usersFailed, byGroup } = staff;
  /*
   * The countries master. Fetched once for the page, and read by TWO consumers
   * with different audiences:
   *
   *   - the **Country** column's inline PICKER, which only a writer sees;
   *   - the **Country** column's FUNNEL, which every role may use.
   *
   * Both now read the same field — `d_companies.country_id` — since the
   * free-text Billing country column was merged away on 2026-08-31. They still
   * want DIFFERENT subsets of this list, which is what the next note is about.
   *
   * ⚠️ **So it is no longer gated on `canUpdate`, and that was a real change**
   * (2026-08-31). It used to return early for a reader, because the read-only
   * branch renders the stored string and needs no list — correct while the
   * picker was the only consumer. Left gated, the country funnel would have
   * offered a `viewer` an empty option list, which is indistinguishable from a
   * failed read and from "there are no countries".
   *
   * ⚠️ **`perPage: 250` is the ceiling on THIS endpoint** (200 elsewhere, 251 a
   * 422), and 195 rows fit in one page. The default of 50 would offer a quarter
   * of the world with nothing on screen saying so.
   *
   * ⚠️ **No `isActive` filter ON THE REQUEST, and the reason CHANGED on
   * 2026-08-31 even though the code did not.** *SUPERSEDED: "this column is
   * FREE TEXT with no foreign key, so a retired country is not an unsaveable
   * id, it is a perfectly legal string the server will store."* It is a foreign
   * key now, so a retired row IS a value a picker should not offer — but the
   * FUNNEL still must show one, or a company holding a retired country becomes
   * unreachable through the only control that can reach it. The read therefore
   * stays wide and `countryOptions` narrows the picker; see it for the split.
   *
   * ⚠️ **A failed read is not "no countries"** — the two look identical at a
   * disabled control, so only one of them says anything, and it says
   * "couldn't be loaded". See `CountryCell` and the notice above the table.
   */
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesFailed, setCountriesFailed] = useState(false);

  /*
   * ⚠️ **There is no `countriesLoading` flag, deliberately.** Both states this
   * fetch has to gate on are already readable from the data: the picker is
   * unusable while `countries` is empty (not read yet, or an organisation that
   * genuinely lists none) and unusable when the read failed. A separate boolean
   * would have to be set synchronously inside the effect, which is a cascading
   * render and what oxlint's `set-state-in-effect` is pointing at — and it
   * would be a second representation of a fact `countries.length` already
   * carries, which is how two flags drift.
   *
   * `failed` is still its own state because it is NOT derivable from an empty
   * list: "we could not read the countries" and "there are none" are different
   * facts and only the first gets a notice. It is cleared on the success path
   * rather than before the request, so a later successful read takes the
   * banner down and nothing is set synchronously here.
   */
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCountries({ perPage: 250 }, controller.signal)
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setCountries(data);
        setCountriesFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCountriesFailed(true);
      });
    return () => controller.abort();
    // Runs ONCE. `canUpdate` left the dependency list with the early return it
    // guarded — see below.
  }, []);

  /*
   * The countries at least one company is IN — the Country FUNNEL's options,
   * owner's request 2026-10-04 (the same narrowing the UP dashboard's funnel
   * uses). ⛔ **The FILTER only — never the inline picker**, which keeps the
   * full master above: a picker offering only countries that already have a
   * company would make it impossible to record the first company anywhere new.
   * `null` until read, and a failed read stays `null`, so the funnel falls back
   * to the full master — a longer list, never a missing one.
   */
  const [countriesWithCompanies, setCountriesWithCompanies] = useState<Country[] | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCountries({ perPage: 250, hasCompanies: true }, controller.signal)
      .then(({ data }) => {
        if (!controller.signal.aborted) setCountriesWithCompanies(data);
      })
      .catch(() => {
        // Left null: the funnel uses the full master instead.
      });
    return () => controller.abort();
    // Runs ONCE, like the master read. The original note on that read follows:
    // `canUpdate` left the dependency list with the early return it
    // guarded — keeping it would re-fetch 195 rows when a role changed under a
    // live token, against a 12,000/60s (⚠️ not the 120/60s this once said)
    // per-IP limit shared across the office.
  }, []);

  // ⚠️ Both inline pickers are GATED by a user group. Naming somebody outside
  // `account_lead` as `accountLeadId` is a 422 on `/companies`, so each cell
  // offers that group's members and nobody else. The un-narrowed `users` list
  // is still read below by `changeStaff`, which must find a person from
  // EITHER narrowed list for its optimistic patch.
  const staffByColumn: Record<StaffColumn, StaffNarrowing> = {
    accountLead: byGroup[USER_GROUP_SLUGS.accountLead],
    clientSuccessManager: byGroup[USER_GROUP_SLUGS.clientSuccessManager],
  };

  /**
   * The Account lead and Client success manager funnels' options — **the whole
   * staff directory**, not the loaded page.
   *
   * ⚠️ **Without this both columns can only ever offer the people the pages
   * somebody has already looked at.** `useTableFilters` accumulates a server
   * column's options out of loaded rows (`seenServerValues`), so a lead who
   * holds no company on page 1 is a lead nobody can filter for — and the
   * popover gives no hint that anyone is missing, so it reads as "these are all
   * the leads there are". Worse on a person column than on most: the account
   * you are hunting is usually the one you have NOT already got on screen.
   *
   * ⚠️ **NOT narrowed to the user group, and this is the same split
   * `countryFilterOptions` draws below.** The inline PICKERS offer
   * `byGroup[…]` because naming somebody outside `account_lead` is a 422 — but
   * a FILTER is not a picker, and a company can legitimately hold a lead who is
   * not in the group: the approval path makes the requester the lead of the
   * company they raised, membership or not. Narrowing here would make exactly
   * those companies unreachable through the only control that can reach them.
   * `staffOptions` (the picker's list) and this one must never be swapped, for
   * the same reason `regionOptions` and `regionPickerOptions` are aliased apart
   * at the top of this file.
   *
   * 🔴 **`undefined` WHILE THE DIRECTORY HAS RETURNED NOTHING, WHICH IS NOT
   * `[]`.** `ColumnSpec.options` REPLACES the accumulated list outright, so an
   * empty array handed over after a failed read would render "No values on this
   * page" over a table whose rows visibly name people — a closed set asserted
   * from a request that never came back. `undefined` hands the column back to
   * the hook's accumulated values: narrower than the directory, but at least
   * derived from rows actually on screen. The spread at the call site is what
   * keeps the key ABSENT rather than present-and-undefined.
   *
   * Loaded rows are appended, exactly as `regionOptions` appends retired
   * regions: `useUserOptions` reads ONE page of 100, so past that a real lead
   * is missing from the directory and would otherwise vanish from the funnel
   * while being visible in the column beside it.
   *
   * Sorted by label rather than used as received, which is the opposite of
   * Stage, Regions and Country. Those three read a curated `sortOrder` off a
   * master and their sequence carries meaning; `/users` is asked for no `sort`
   * at all, so its order is arbitrary — and alphabetical is also what the
   * accumulated list this replaces already showed, so the popover does not
   * silently reshuffle on the day the directory loads.
   */
  const staffFilterOptions = useMemo<Record<StaffColumn, ColumnFilterOption[] | undefined>>(() => {
    const build = (pick: (row: Company) => UserSummary | null) => {
      // Tested on the directory, never on the assembled list: rows alone could
      // make this non-empty, and a handful of loaded ids is precisely the
      // narrow-and-silent list the whole memo exists to replace.
      if (users.length === 0) return undefined;
      const people = new Map<string, UserSummary>();
      for (const person of users) people.set(String(person.id), person);
      // ⚠️ Rows only ADD, never overwrite. A company's embedded `accountLead` and
      // the directory's row are the same person read at two different moments,
      // and the directory is the fresher of the two — letting a page-1 embed win
      // would label the checkbox with a name somebody has since changed.
      for (const row of rows) {
        const person = pick(row);
        if (person && !people.has(String(person.id))) people.set(String(person.id), person);
      }
      // Emails only where a name is genuinely ambiguous, the same rule
      // `staffOptions` applies to the picker: two identical checkboxes are
      // unusable, and eleven addresses in a 288px popover are unreadable.
      const counts = new Map<string, number>();
      for (const person of people.values()) {
        counts.set(person.name, (counts.get(person.name) ?? 0) + 1);
      }
      return Array.from(people, ([value, person]) => ({
        value,
        // `name` is NOT NULL but can be `''`; `User #<id>` is the honest label
        // and matches what the column's own `format` falls back to.
        label:
          person.name.trim() === ''
            ? `User #${value}`
            : (counts.get(person.name) ?? 0) > 1 && person.email !== ''
              ? `${person.name} (${person.email})`
              : person.name,
      })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    };
    return {
      accountLead: build((row) => row.accountLead),
      clientSuccessManager: build((row) => row.clientSuccessManager),
    };
  }, [users, rows]);

  /** `name` is NOT NULL but can in principle be `''` — fall back to the id. */
  const regionOptions = useMemo<ColumnFilterOption[]>(() => {
    const options: ColumnFilterOption[] = [];
    const seen = new Set<string>();
    const add = (id: number, name: string) => {
      const value = String(id);
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: name.trim() === '' ? `#${id}` : name });
    };
    // Master order is the curated picker order (`sortOrder`, then `name`) and is
    // used as received — `useReferenceOptions` says not to re-sort it.
    for (const record of regionRecords) add(record.id, record.name);
    // Retired regions are excluded from the master query (offering one in a
    // picker guarantees a 422), but a FILTER is not a picker — a company can
    // still hold and show a retired region, so loaded rows are appended too.
    for (const row of rows) for (const region of row.regions) add(region.id, region.name);
    return options;
  }, [regionRecords, rows]);

  /**
   * The Country funnel's options — the country OF RECORD.
   *
   * ⚠️ **Values are `f_countries` IDS, because `?countryId=` is a foreign key.**
   * *SUPERSEDED 2026-08-31: "`countryOptions` further up this file builds a
   * different list for the Billing country picker, whose values are country
   * NAMES because that column is free text."* Both lists carry ids now — the
   * picker writes the same key this filters. **They still differ in ONE way and
   * it is not the value shape: `countryOptions` narrows to ACTIVE rows plus the
   * one the record holds, and this list narrows to nothing.** See the next
   * note.
   *
   * ⚠️ **NO `isActive` NARROWING, and this is a decision rather than a default.**
   * The master read already sends none (see the fetch above), and a FILTER is
   * not a PICKER: the server checks a country for EXISTENCE and never for
   * `isActive` when one is assigned, so a company can legitimately hold a
   * retired country — and hiding it here would make those companies unreachable
   * through the only control that can reach them. `withSelectedValues` in
   * `headerFor` covers the remaining hole from the other side: a ticked id the
   * master did not return still renders a checkbox, so the column can be
   * cleared rather than sticking as filtered with no way out.
   *
   * ⚠️ **BUILT FROM THE MASTER ALONE — loaded rows are deliberately NOT
   * appended, unlike `regionOptions` directly above.** Two measured reasons,
   * and the first is a live contract hazard:
   *
   *  1. **{@link CompanyCountryRef}'s label has been spelled wrong here
   *     before.** The type declared `{id, countryName}` until 2026-08-31 while
   *     the server's mapper has always serialised the embed as
   *     `{id, name, code, billingRegion}` (`companies.repository.ts`,
   *     `CompanyCountry` / `toCompanyCountry`) — so `row.country.countryName`
   *     read `undefined` at run time while `tsc` insisted it was a `string`,
   *     the `LineCity`-versus-project-`cities` trap the type's OWN doc predicts
   *     in as many words. The type is corrected; the instruction it carries —
   *     *"read `id` and prefer the `/countries` master for the LABEL"* — is
   *     what this does, and what `countryRowLabel` does with a defensive embed
   *     fallback for the one case the master cannot cover.
   *  2. **The master is COMPLETE in one page.** `perPage: 250` covers all 195
   *     rows with no `isActive` narrowing, so unlike regions — whose master
   *     read excludes retired rows — there is no country a row can hold that
   *     the master did not return.
   *
   * A selected id the master somehow lacks is still recoverable:
   * `withSelectedValues` in `headerFor` renders it as `#<id>` so the column can
   * be cleared, rather than sticking as filtered with no checkbox to untick.
   */
  const countryFilterOptions = useMemo<ColumnFilterOption[]>(() => {
    // Used in the order received. 195 rows is a lot for a checkbox list, which
    // is why `ColumnFilterPanel` carries its own search box — re-sorting here
    // would only disagree with the master.
    // Only countries a company is in (see `countriesWithCompanies`); the full
    // master until that read lands or if it fails. A ticked country no longer
    // in the narrowed list is still padded back by `withSelectedValues`.
    return (countriesWithCompanies ?? countries).map((record) => ({
      value: String(record.id),
      // `countryName` IS the right spelling on the MASTER row ({@link Country});
      // it is only the embed that differs. NOT NULL, but `''` is expressible.
      label: record.countryName.trim() === '' ? `#${record.id}` : record.countryName,
    }));
  }, [countries, countriesWithCompanies]);

  /**
   * The same labels by id, for the Country column's `format`.
   *
   * DERIVED FROM THE OPTIONS rather than from `countries` a second time, so the
   * funnel's checkbox and the column's rendered value can never spell one
   * country two ways.
   */
  const countryLabels = useMemo(
    () => new Map(countryFilterOptions.map((option) => [option.value, option.label])),
    [countryFilterOptions],
  );

  /**
   * One row's country as a label, or `null` for "not recorded" — the read-only
   * branch of the merged Country column.
   *
   * ⚠️ **Master first, embed second, `#id` last.** The master is the label
   * every other country control on this page renders from, so preferring it
   * means the read-only cell and the funnel's checkbox cannot spell one country
   * two ways. The embed is the fallback for a country the master did not return
   * — a retired row, or a short read — because a company that holds a country
   * must never render blank: blank is what the picker's placeholder means, and
   * a reader would take it for "nobody has set one".
   *
   * The embed's label is read DEFENSIVELY: `CompanyCountryRef.name` was
   * declared here as `countryName` until 2026-08-31 and the server has never
   * sent that spelling, so a mis-declared embed prints `undefined` in a value
   * slot.
   */
  const countryRowLabel = (row: Company): string | null => {
    if (row.country === null) return null;
    const fromMaster = countryLabels.get(String(row.country.id));
    if (fromMaster !== undefined) return fromMaster;
    const name = typeof row.country.name === 'string' ? row.country.name.trim() : '';
    return name !== '' ? name : `Country #${row.country.id}`;
  };

  /**
   * The Source funnel's options — read from the `/company-sources` MASTER, and
   * never accumulated from the loaded page.
   *
   * ⛔ **The page-accumulated alternative is not merely narrower here, it is
   * almost empty.** `source_id` is `null` on essentially every company today,
   * so twenty loaded rows would name nought or one source between them — and a
   * funnel built from them offers a source precisely when a row on this page
   * already carries it, which is the opposite of what somebody opens the
   * control to do. `headerFor` therefore supplies `options` ALWAYS, including
   * an empty array while the master is in flight, so the hook's accumulation
   * can never apply to this column.
   *
   * ⚠️ **NO `isActive` NARROWING — a FILTER is not a PICKER.** The server
   * checks a source for EXISTENCE, not for activity, so a company can
   * legitimately hold a retired source; hiding it here would make those
   * companies unreachable through the only control that can reach them. The
   * editor's list (`companySourceOptions`) narrows to active rows plus the one
   * held, which is the opposite rule and the right one there.
   *
   * ⚠️ **There is no "Not recorded" OPTION, and there cannot be one.** The
   * server renders `source_id IN (...)`, which is UNKNOWN for a NULL, so
   * `?sourceId=` cannot ask for the unrecorded rows — and on this column those
   * are the majority rather than a residue. The funnel's Is empty / Is not
   * empty is page-scoped over the loaded page, like every other column here,
   * and answers a narrower question than it looks like it does.
   */
  const sourceFilterOptions = useMemo<ColumnFilterOption[]>(
    // Used in the order received — `sortOrder, name` is the curated master
    // order, and `useCompanySources` says not to re-sort it.
    () =>
      sources.map((record) => ({
        value: String(record.id),
        label: companySourceLabel(record.id, record.name),
      })),
    [sources],
  );

  /**
   * The same labels by id, for the Source column's `format`.
   *
   * DERIVED FROM THE OPTIONS rather than from `sources` a second time, so the
   * funnel's checkbox and the column's rendered value can never spell one
   * source two ways — the rule `countryLabels` above already follows.
   */
  const sourceLabels = useMemo(
    () => new Map(sourceFilterOptions.map((option) => [option.value, option.label])),
    [sourceFilterOptions],
  );

  /**
   * The Type funnel's options — read from the `/company-types` MASTER, and never
   * accumulated from the loaded page.
   *
   * ⛔ **The page-accumulated alternative is not merely narrower here, it is
   * EMPTY.** `company_type_id` is `null` on every company that predates the
   * column, which today is essentially all of them — so twenty loaded rows would
   * name no type at all and the funnel would offer nothing, which reads as a
   * master with no rows in it rather than as a column nobody has filled in.
   * `headerFor` therefore supplies `options` ALWAYS, empty array included, so
   * the hook's accumulation can never apply to this column.
   *
   * ⚠️ **NO `isActive` NARROWING — a FILTER is not a PICKER**, and
   * `companyTypeOptions` says so at the helper itself. A company can hold a
   * retired type, and hiding it here would make exactly those companies
   * unreachable through the only control that can reach them. The editor's list
   * narrows to active rows plus the one held, which is the opposite rule and the
   * right one there.
   *
   * ⚠️ **There is no "Not recorded" OPTION, and there cannot be one.** The server
   * renders `company_type_id IN (...)`, which is UNKNOWN for a NULL, so
   * `?companyTypeId=` cannot ask for the unclassified rows — and on this column
   * those are the majority. Is empty / Is not empty is page-scoped, like every
   * other column here, and answers a narrower question than it looks like it
   * does.
   */
  const companyTypeFilterOptions = useMemo<ColumnFilterOption[]>(
    // Used in the order received — `sortOrder, name` is the curated master
    // order, and `useCompanyTypes` says not to re-sort it.
    () =>
      companyTypes.map((record) => ({
        value: String(record.id),
        label: companyTypeLabel(record.id, record.name),
      })),
    [companyTypes],
  );

  /*
   * Each per-stage status funnel's options, keyed by column id — ONLY that
   * stage's statuses (decision 5), retired ones included (a FILTER is not a
   * PICKER), in the server's status order. Matched by `status.stage.id`, never
   * by name. No stage prefix on the label: the column heading names the stage.
   */
  const stageStatusFilterOptionsByColumn = useMemo(() => {
    const byColumn = new Map<string, ColumnFilterOption[]>();
    for (const column of stageStatusColumns) {
      byColumn.set(
        column.id,
        stageStatuses
          .filter((status) => status.stage.id === column.stageId)
          .map((status) => ({
            value: String(status.id),
            label: status.name.trim() || `#${status.id}`,
          })),
      );
    }
    return byColumn;
  }, [stageStatusColumns, stageStatuses]);

  /** Status names by id, for the per-stage columns' `format`. */
  const stageStatusLabels = useMemo(
    () =>
      new Map(stageStatuses.map((status) => [String(status.id), status.name.trim() || `#${status.id}`])),
    [stageStatuses],
  );

  /*
   * The CYCLE master, for the Projects participated in and Products purchased
   * funnels (2026-10-09). Both filter by cycle on the server, so the options
   * are the whole master — never accumulated from loaded rows, which would only
   * offer a cycle some row on this page already names.
   *
   * ⛔ **Ordered by START DATE, never by id** — the ids are production's counter
   * and are not in date order (`2022 - 23` holds the highest one). Newest first,
   * the same `YYYY-MM-DD` string comparison every date here uses; id only breaks
   * a tie so two reads never disagree.
   */
  const {
    cycles: cycleMaster,
    loading: cyclesLoading,
    failed: cyclesFailed,
  } = useCycleOptions();
  const cycleFilterOptions = useMemo<ColumnFilterOption[]>(
    () =>
      [...cycleMaster]
        .sort((a, b) =>
          a.startDate === b.startDate ? b.id - a.id : a.startDate < b.startDate ? 1 : -1,
        )
        .map((cycle) => ({
          value: String(cycle.id),
          label: cycle.cycleName.trim() || `#${cycle.id}`,
        })),
    [cycleMaster],
  );

  /**
   * The same labels by id, for the Type column's `format`.
   *
   * DERIVED FROM THE OPTIONS rather than from `companyTypes` a second time, so
   * the funnel's checkbox and the column's rendered value can never spell one
   * type two ways — the rule `countryLabels` and `sourceLabels` already follow.
   */
  const companyTypeLabels = useMemo(
    () => new Map(companyTypeFilterOptions.map((option) => [option.value, option.label])),
    [companyTypeFilterOptions],
  );

  // Which row's tasks-and-notes sheet is open, held as the company itself so
  // the sheet keeps its heading while closing rather than flashing blank.
  // Deliberately NOT `ActivityButton` per row (as record pages use) — that
  // fetches its own counts, so twenty-five rows would be fifty requests for
  // badges nobody asked for.
  const [activityFor, setActivityFor] = useState<Company | null>(null);
  // Where the sheet opens. The last-note cells open it on the notes; the
  // row's tasks-and-notes button opens it as it always has, so every opener
  // sets this rather than leaving a previous row's focus behind.
  const [activityFocus, setActivityFocus] = useState<'notes' | undefined>(undefined);
  function openActivity(company: Company, focus?: 'notes') {
    setActivityFocus(focus);
    setActivityFor(company);
  }
  // Set when the sheet reports an accepted note or task write, and spent on
  // close: ONE refetch per visit, not one per write, so the "Last note" and
  // "Last note added" columns catch up once the reader is done. A ref, not
  // state — nothing renders from it.
  const activityChangedRef = useRef(false);

  /*
   * Which company the contacts manager is open on, and ⛔ **ONE mount
   * for the whole PAGE, never one per row** — a modal per row is twenty-five
   * modals, twenty-five of whatever they fetch, and twenty-five copies of a
   * dialog only one of which can ever be open.
   *
   * ⚠️ **`{id, name}` and not the `Company`**, the rule `openProjectLines`
   * already follows above: a row carries fifty columns the modal must not start
   * depending on, and the two mounts that open this hold different things (the
   * table and the card hold a whole row; a board card would hold neither).
   * `null` is closed, which is also what makes "which row" and "is it open" one
   * piece of state rather than two that can disagree.
   */
  const [contactsFor, setContactsFor] = useState<{ id: number; name: string } | null>(null);

  /*
   * Handed to both action groups — the table's lane and the phone card's row —
   * built once so twenty-five rows cannot word the same thing twenty-five ways,
   * the same reasoning as `nameEdit` and `openProjectLines`. ⚠️ *SUPERSEDED
   * 2026-09-17: "Handed to both `CompanyName` mounts", while the trigger sat
   * beside the name.*
   */
  const openContactsFor = useCallback((row: Company) => {
    setContactsFor({ id: row.id, name: row.name });
  }, [setContactsFor]);

  // The stage filter is held in the URL, so `/companies?stageId=3` is a real
  // deep link. `useSearchParams()` takes NO argument — its first parameter is
  // a default init, not options — so every write passes `{ replace: true }`
  // to the setter instead, to avoid stacking a history entry per pick.
  const [searchParams, setSearchParams] = useSearchParams();
  // Depend on the string, never on the object: the object's identity changes
  // on every render.
  const query = searchParams.toString();

  // ⚠️ **The URL is the Stage filter's STORE; the column funnel is only its
  // control.** `useTableFilters` starts at `useState({})` and never reads the
  // query string, so a stage moved wholly into the hook would leave
  // `/companies?stageId=1` rendering unfiltered — and that link is live:
  // `/university-partnerships` uses it for its "Leads" table. Routing the
  // funnel's `value`/`onChange` through here in `headerFor` keeps the deep
  // link working with one mechanism rather than two.
  //
  // `?stageId=1` and `?stageId=1,2` are both accepted — `/companies` is the
  // ONE endpoint that parses the comma-separated form. A junk member drops
  // the WHOLE value rather than being skipped: a half-applied filter is worse
  // than an unapplied one.
  const stageFilterIds = useMemo<number[]>(() => idListFromQuery(query, 'stageId'), [query]);

  const setStageFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'stageId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  /*
   * ✅ **THE ACCOUNT LEAD FUNNEL IS URL-BACKED TOO — 2026-09-12, owner's
   * instruction**, and it is the second column to get the treatment Stage has
   * had since this page was built.
   *
   * ⚠️ **IT HAD TO COME FIRST, BEFORE THE DEFAULT COULD MEAN ANYTHING.** The
   * owner asked for the filter to start on the signed-in person across
   * companies, quotes, deals and invoices. The other three already parsed
   * `accountLeadId` out of the query string; here the funnel's ticked values
   * lived in `useTableFilters`' own `useState({})`, so **`/companies?accountLeadId=5`
   * rendered UNFILTERED** and a seed written into the URL would have been
   * persisted by the saved filters, reloaded on the next arrival, and **ignored
   * by every request** — a filter that saves cleanly and does nothing. Seeding
   * it without this change would have been the worse bug.
   *
   * ⚠️ **So this control now has TWO HOMES, both written on every change**,
   * exactly as Stage does: `selected` lives in the URL and `emptiness` stays in
   * the hook (page-scoped — no endpoint here filters on a null lead). The hook's
   * `selected` is deliberately held EMPTY so it can never emit a competing
   * `accountLeadId` from `serverParams`.
   *
   * ⚠️ **`?accountLeadId=1,2` is a LIST, and `/companies` is the one endpoint
   * that parses one.** Every other list and every other board 422s on the
   * comma-separated form, so this spelling must not be carried anywhere else.
   *
   * ✅ A side effect worth naming rather than discovering: the funnel is
   * **deep-linkable and shareable for the first time**, and is now saved against
   * the account — see `SAVED_FILTER_KEYS`.
   */
  const accountLeadFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'accountLeadId'),
    [query],
  );

  const setAccountLeadFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'accountLeadId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  /*
   * ✅ **AND SO ARE THE REMAINING THREE — 2026-09-12, the same pass, the same
   * shape.** Client success manager, Country and Regions are the other members
   * of `SERVER_FILTER_PARAMS`, and each carried exactly the defect the Account
   * lead block above describes: the funnel's ticked ids lived in
   * `useTableFilters`' own `useState({})`, so **`/companies?countryId=82`
   * rendered UNFILTERED**, none of the three was shareable or deep-linkable, and
   * there was nothing in the query string for the saved filters to remember.
   *
   * ⚠️ **Two homes each, both written on every change**: `selected` in the URL,
   * `emptiness` in the hook, and the hook's `selected` held EMPTY so
   * `serverParams` can never emit a competing value beside the one `load()`
   * sends. One split, five columns — `urlColumnFilters` below is the single
   * place it is wired, so a sixth is a row there rather than a sixth pair of
   * arms in `headerFor`.
   *
   * ⚠️ **The CSV spelling is a `/companies` SPECIALITY and stops here.**
   * `?countryId=82,60` parses as a list on this endpoint alone; every other list
   * and every other board answers "expected number, received NaN". A junk member
   * drops the WHOLE value (`idListFromQuery`) rather than narrowing to the parts
   * that happened to parse, and an empty list DELETES the key rather than
   * writing `?countryId=`, which is a 422 on these `.strict()` schemas and would
   * be saved back as one.
   *
   * ⛔ **`countryId` REACHES THE LIST AND NOT THE BOARD, and moving it here did
   * not change that** — see `BOARD_UNSUPPORTED_PARAMS`. Where the value is
   * stored and which surfaces receive it are two different questions, which is
   * why `boardParams` still assembles its keys by hand and why board view still
   * says the country filter is not applied there.
   */
  const csmFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'clientSuccessManagerId'),
    [query],
  );

  const setCsmFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'clientSuccessManagerId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  const countryFilterIds = useMemo<number[]>(() => idListFromQuery(query, 'countryId'), [query]);

  const setCountryFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'countryId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  const sourceFilterIds = useMemo<number[]>(() => idListFromQuery(query, 'sourceId'), [query]);

  const setSourceFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'sourceId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  const companyTypeFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'companyTypeId'),
    [query],
  );

  const setCompanyTypeFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'companyTypeId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  const regionFilterIds = useMemo<number[]>(() => idListFromQuery(query, 'regionId'), [query]);

  const setRegionFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'regionId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  // Stage status (2026-10-23), URL-backed like every funnel above.
  const rawStageStatusFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'stageStatusId'),
    [query],
  );

  /*
   * Both masters read, so the per-stage columns are known. Until then (or when
   * a read failed) nothing about which ids belong anywhere can be decided.
   */
  const stageStatusMastersReady =
    !stagesLoading && !stageStatusesLoading && !stagesFailed && !stageStatusesFailed;

  /*
   * The ids the request actually sends (2026-10-25): once the masters have
   * loaded, ONLY ids some current per-stage column claims. An id whose stage
   * lost its column, or that the master does not list, has no funnel to clear
   * it, so it must not narrow the list either — and the effect below strips it
   * from the URL. While the masters load or failed, the URL is sent as is
   * (stripping or dropping then would be a guess).
   */
  const stageStatusFilterIds = useMemo<number[]>(() => {
    if (!stageStatusMastersReady) return rawStageStatusFilterIds;
    return rawStageStatusFilterIds.filter((id) =>
      stageStatusColumns.some((column) =>
        columnSelection(column, [id], stageStatuses).includes(id),
      ),
    );
  }, [rawStageStatusFilterIds, stageStatusMastersReady, stageStatusColumns, stageStatuses]);

  const setStageStatusFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'stageStatusId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  // Strip orphan status ids out of the URL (and so out of the saved filter)
  // once the masters have loaded — never while they are loading or failed.
  useEffect(() => {
    if (!stageStatusMastersReady) return;
    if (stageStatusFilterIds.length === rawStageStatusFilterIds.length) return;
    setSearchParams(writeIdList(query, 'stageStatusId', stageStatusFilterIds), { replace: true });
  }, [
    stageStatusMastersReady,
    stageStatusFilterIds,
    rawStageStatusFilterIds,
    query,
    setSearchParams,
  ]);

  // The two CYCLE filters (2026-10-09), URL-backed like every funnel above.
  // ⚠️ The ids are CYCLE ids, not project or product ids: "participated in /
  // purchased in any of these cycles". The server also scopes the column's count
  // sort to them.
  const participatedCycleFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'participatedCycleId'),
    [query],
  );

  const setParticipatedCycleFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'participatedCycleId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  const purchasedCycleFilterIds = useMemo<number[]>(
    () => idListFromQuery(query, 'purchasedCycleId'),
    [query],
  );

  const setPurchasedCycleFilter = useCallback(
    (next: readonly number[]) => {
      setSearchParams(writeIdList(query, 'purchasedCycleId', next), { replace: true });
      setPage(1);
    },
    [query, setSearchParams],
  );

  /**
   * The four server params' ticked ids, keyed by PARAM — what `listFilters`
   * overwrites `serverParams` with.
   *
   * ⛔ **Typed `Record<ServerFilterParam, …>`, so it is EXHAUSTIVE by
   * construction.** Adding a param to `SERVER_FILTER_PARAMS` without wiring it
   * to the URL is then a build error here rather than a funnel that ticks,
   * reads back from the hook, and quietly disagrees with the request.
   *
   * `stageId` is absent on purpose: Stage is not a `ServerFilterParam` and is
   * spelled into `load()` and `boardParams` by hand.
   */
  const urlFilterIdsByParam = useMemo<Record<ServerFilterParam, readonly number[]>>(
    () => ({
      accountLeadId: accountLeadFilterIds,
      clientSuccessManagerId: csmFilterIds,
      countryId: countryFilterIds,
      regionId: regionFilterIds,
      sourceId: sourceFilterIds,
      companyTypeId: companyTypeFilterIds,
      participatedCycleId: participatedCycleFilterIds,
      purchasedCycleId: purchasedCycleFilterIds,
      stageStatusId: stageStatusFilterIds,
    }),
    [
      accountLeadFilterIds,
      csmFilterIds,
      countryFilterIds,
      regionFilterIds,
      sourceFilterIds,
      companyTypeFilterIds,
      participatedCycleFilterIds,
      purchasedCycleFilterIds,
      stageStatusFilterIds,
    ],
  );

  /**
   * The same five funnels keyed by COLUMN, with their writers — what `headerFor`
   * substitutes a `value` and an `onChange` for, and what the filter count, the
   * hidden-column sentence and the empty state read.
   *
   * Not memoised: it is only ever read during render, and the setters it holds
   * are already stable per `query`.
   *
   * ⚠️ **`| undefined` is load-bearing**: `headerFor` is handed a plain `string`,
   * including page-scoped keys (`name`) and the sort-only ones, and those must
   * fall through to the hook untouched.
   */
  const urlColumnFilters: Readonly<Record<string, UrlColumnFilter | undefined>> = {
    stage: { ids: stageFilterIds, set: setStageFilter },
    accountLead: { ids: urlFilterIdsByParam.accountLeadId, set: setAccountLeadFilter },
    clientSuccessManager: {
      ids: urlFilterIdsByParam.clientSuccessManagerId,
      set: setCsmFilter,
    },
    country: { ids: urlFilterIdsByParam.countryId, set: setCountryFilter },
    regions: { ids: urlFilterIdsByParam.regionId, set: setRegionFilter },
    // The sixth, and the row this record's own comment predicted: a funnel
    // added here rather than a sixth pair of arms in `headerFor`.
    source: { ids: urlFilterIdsByParam.sourceId, set: setSourceFilter },
    /*
     * ⛔ **THE ONE ROW IN THIS WIRING THAT NOTHING CHECKS.** The record is
     * `Record<string, UrlColumnFilter | undefined>` — it has to be, because
     * `headerFor` is handed page-scoped and sort-only keys too — so a MISSING
     * row is not a build error anywhere. Without it the funnel ticks, the query
     * string never changes, the request goes out unfiltered, and
     * `useTableFilters` narrows only the rows already loaded: a short list that
     * reads as "there are three companies of this type" rather than as a filter
     * that was never sent.
     */
    companyType: { ids: urlFilterIdsByParam.companyTypeId, set: setCompanyTypeFilter },
    // ⛔ The same unchecked-row hazard: a missing row here sends no cycle filter.
    projectsParticipated: {
      ids: urlFilterIdsByParam.participatedCycleId,
      set: setParticipatedCycleFilter,
    },
    productsPurchased: {
      ids: urlFilterIdsByParam.purchasedCycleId,
      set: setPurchasedCycleFilter,
    },
    /*
     * ⛔ The same unchecked-row hazard: a missing row here sends no status filter.
     * One row PER STAGE COLUMN, all over the ONE shared `?stageStatusId=` list
     * (decision 5): each funnel reads and replaces only its own stage's slice,
     * so ticks in two columns combine as a union — a company is in one stage,
     * so an intersection would always be empty.
     */
    ...Object.fromEntries(
      stageStatusColumns.map((column): [string, UrlColumnFilter] => [
        column.id,
        {
          ids: columnSelection(column, urlFilterIdsByParam.stageStatusId, stageStatuses),
          set: (next) =>
            setStageStatusFilter(
              replaceColumnSelection(
                column,
                urlFilterIdsByParam.stageStatusId,
                next,
                stageStatuses,
              ),
            ),
        },
      ]),
    ),
  };

  /*
   * `?stageStatusId=` members no per-stage column claims — a status of a stage
   * with no column now, or an id the master does not list. They still narrow
   * the request, so they are counted and named as a filter (below) rather than
   * left invisible; "Clear column filters" clears them with the rest.
   */
  const orphanStageStatusFilterIds = urlFilterIdsByParam.stageStatusId.filter(
    (id) =>
      !stageStatusColumns.some((column) =>
        columnSelection(column, [id], stageStatuses).includes(id),
      ),
  );

  /**
   * Every column funnel's ids out of the query string, in ONE write.
   *
   * ⛔ **Never five setter calls in a row.** Each closes over the `query`
   * captured at render, so the last would rebuild the params from a snapshot
   * still carrying the four keys its predecessors had deleted — a Clear that
   * reports success and leaves the table filtered. See `URL_FILTER_KEYS`.
   */
  const clearUrlFilters = useCallback(() => {
    const params = new URLSearchParams(query);
    for (const key of URL_FILTER_KEYS) params.delete(key);
    setSearchParams(params, { replace: true });
    setPage(1);
  }, [query, setSearchParams]);

  // Junk is the table, not a 404. `view` never reaches the API — the list
  // request is built from named keys below.
  const view: CompaniesView = searchParams.get('view') === 'board' ? 'board' : 'table';

  const setView = useCallback(
    (next: CompaniesView) => {
      const params = new URLSearchParams(query);
      // The table is the default, spelled by the key's absence rather than by
      // `?view=table` — one canonical URL per view.
      if (next === 'board') params.set('view', 'board');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [query, setSearchParams],
  );

  /*
   * Saved filters, against the EMERGE account rather than this browser.
   *
   * ✅ **EVERY COLUMN FUNNEL IS REMEMBERED NOW — 2026-09-12.** *SUPERSEDED, and
   * it was true for about a day: "`stageId` and `accountLeadId` are the whole
   * FILTER surface here, and that is not an oversight. Search, the CSM picker,
   * the country and region funnels and the pager live in `useState`, never in
   * the query string — so they are not shareable, not deep-linkable, and there
   * is nothing for this to save."* The three remaining server-scoped funnels
   * moved onto the URL in the same pass that wrote that sentence's successor —
   * see `urlColumnFilters`.
   *
   * ⚠️ **What is still NOT saved, and each for its own reason**: `search` lives
   * in `useState` and would need moving to the query string first; every
   * funnel's `emptiness` half is page-scoped, over the loaded rows, so there is
   * no server filter to remember; and `page` is excluded by the rule at
   * `SAVED_FILTER_KEYS` rather than by omission.
   *
   * ⚠️ **The ORDER is the whole point and it held for all four keys**: the page
   * must genuinely READ a key out of the query string before the key is listed
   * above. A key saved but not read saves cleanly, reloads on the next arrival,
   * and is ignored by every request — a filter that persists and does nothing.
   *
   * `view` is independent: a link naming a stage says nothing about whether the
   * recipient prefers the board, so it is merged per key while `stageId` is
   * all-or-nothing. See `arrivalDecision`.
   *
   * ✅ **THE ACCOUNT-LEAD DEFAULT IS LIVE HERE NOW — 2026-09-12.**
   * *SUPERSEDED, and it was right when written: "⛔ **THIS IS WHY THERE IS NO
   * `useDefaultAccountLeadFilter` CALL ON THIS PAGE, AND ADDING ONE WOULD DO
   * NOTHING VISIBLE** … `/companies?accountLeadId=5` renders UNFILTERED today,
   * and a seed written into the URL would be persisted by the saved filters
   * above, reloaded on the next arrival, and **silently ignored by every
   * request**."*
   *
   * ⚠️ **The refusal was not overturned — its PRECONDITION was met.** The work
   * it named is exactly the work that was done, in that order: the funnel's
   * `value`/`onChange` re-routed through `accountLeadFilterIds` /
   * `setAccountLeadFilter` with `emptiness` left in the hook, the CSV spelling
   * (`?accountLeadId=1,2`, which this endpoint alone parses), a `listFilters`
   * that prefers the URL over `serverParams`, and the key added to
   * `SAVED_FILTER_KEYS`. **Adding the hook call first would have shipped the
   * trap the old comment describes**, so if the URL half is ever unwound, this
   * call goes with it.
   */
  const applySavedFilters = useSavedFilterApply<SavedCompaniesKey>();
  const savedFilters = useSavedFilters({
    scope: 'companies',
    keys: SAVED_FILTER_KEYS,
    independentKeys: SAVED_VIEW_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  /*
   * The Account lead funnel starts on the signed-in person — owner's
   * instruction, 2026-09-12, and the same shared hook `/deals`, `/quotes` and
   * `/invoices` call. It writes the id INTO the URL once per mount and stands
   * down on every state that means somebody has already answered: a saved set,
   * a link naming any filter, an address already naming a lead, or a funnel the
   * reader has cleared.
   *
   * ⚠️ **`enabled` is `canReadModule`**, so a reader who meets the page's own
   * refusal panel writes nothing into their address bar.
   *
   * ⚠️ **Seeding needs GROUP evidence, and `withGroups` here is `canUpdate`** —
   * see `useUserOptions` above, where that gating is a deliberate decision about
   * a request nobody else needs. The consequence is worth stating rather than
   * discovering: a reader who cannot write gets `narrowed: false`, the hook
   * finds no evidence and **stands down**, so only writers are seeded. That is
   * the right population anyway — `accountLeadId` is refused for anybody outside
   * the `account_lead` group, so a non-writer is vanishingly unlikely to be one
   * — and standing down on absent evidence is the safe direction: a list that
   * has not been narrowed is not proof of membership.
   *
   * ⚠️ **The pager reset is not optional.** This waits on a preferences read
   * that can take seconds, so somebody may have paged already, and page 4 of the
   * whole list is a page number for a different result set — it comes back empty
   * and reads as "no companies".
   */
  useDefaultAccountLeadFilter({
    user,
    staff,
    savedFilters,
    enabled: canReadModule,
    onSeeded: () => setPage(1),
  });

  // Names for stage ids, from the master first and loaded rows second — the
  // second half lets a deep link into a retired stage say "Past Client"
  // rather than "#4".
  const stageNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const record of stages) map.set(record.id, record.name);
    for (const row of rows) if (row.stage) map.set(row.stage.id, row.stage.name);
    return map;
  }, [stages, rows]);

  const stageFilterRefs = useMemo<MasterRef[]>(
    () => stageFilterIds.map((id) => ({ id, name: stageNames.get(id) ?? `#${id}` })),
    [stageFilterIds, stageNames],
  );

  // One name at exactly one, a COUNT above that — never one of several
  // printed as though it were the only one.
  const stageFilterLabel = useMemo<string | null>(() => {
    const [only] = stageFilterRefs;
    if (stageFilterRefs.length === 1 && only) return companyStageLabel(only.id, only.name);
    return stageFilterRefs.length === 0 ? null : `${stageFilterRefs.length} stages`;
  }, [stageFilterRefs]);

  // The funnel's options come from the `/company-stages` MASTER, not the
  // loaded page like the two person columns — filtering TO a stage holding no
  // rows on this page is the main use case, and `rows` could never offer one.
  const stageOptions = useMemo<ColumnFilterOption[]>(() => {
    const options: ColumnFilterOption[] = [];
    const seen = new Set<string>();
    const add = (id: number, name: string) => {
      const value = String(id);
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: companyStageLabel(id, name) });
    };
    // Master order is the curated picker order (`sortOrder`, then `name`) and is
    // used as received — `useReferenceOptions` says not to re-sort it.
    for (const record of stages) add(record.id, record.name);
    // Then whatever the URL names that the master no longer lists, so a link
    // into a RETIRED stage still reads as itself and can be cleared.
    // `companyStageOptions` does the same job for the inline cell picker; a
    // filter and a picker have opposite rules about retired rows.
    for (const id of stageFilterIds) add(id, stageNames.get(id) ?? `#${id}`);
    return options;
  }, [stages, stageFilterIds, stageNames]);

  // Each column declares what the API can really do with it, and the header
  // primitive renders only that. ⚠️ *SUPERSEDED 2026-10-09: "`sortKey` appears
  // on exactly the three columns `/companies` orders by — `name`, `createdAt`
  // and `updatedAt`".* It was already untrue (Source, Type and Last note added
  // theirs) and since backend `32057ed` every funnel column sorts on the server.
  // The `sortKey` is always the same string as the column KEY — load-bearing,
  // because `applySortChoice` reaches the sort through `headerFor(sortKey)`.
  //
  // ⚠️ **That sentence said "three" while only TWO were declared**, from the
  // day the Updated column was removed until 2026-09-11, and it is the artefact
  // that made the gap invisible: a reader checking whether `updatedAt` was
  // wired found a comment saying it was.
  //
  // Five columns are backed by a query param, all `multi`: only on
  // `/companies` and `/companies/kanban` do the id filters parse a
  // comma-separated list, so ticking two people filters the whole table
  // rather than narrowing the loaded page.
  //
  // ⚠️ **NO column's options come from the loaded page any more.** *SUPERSEDED:
  // "Options come from the loaded page (Regions is the exception — see its own
  // comment)."* All five substitute a master in `headerFor` — Stage
  // `stageOptions`, Country `countryFilterOptions`, Regions `regionOptions`,
  // and both person columns `staffFilterOptions`. What the hook accumulates is
  // now only the FALLBACK for a master that has not been read, which is why
  // every one of those spreads leaves the key absent rather than passing `[]`.
  const columns = useMemo<ColumnSpec<Company>[]>(() => {
    // One name map per person column, feeding `format`. ⚠️ It maps an id back to
    // whatever name the loaded page happens to know, so it is only reached on
    // the DEGRADED path — the `/users` read failed or has not landed, and the
    // funnel has fallen back to accumulated values. `staffFilterOptions` names
    // everybody the directory carries and is what the popover normally renders.
    const namesFor = (pick: (row: Company) => { id: number; name: string } | null) => {
      const map = new Map<string, string>();
      for (const row of rows) {
        const person = pick(row);
        if (person) map.set(String(person.id), person.name);
      }
      return map;
    };
    const leadNames = namesFor((row) => row.accountLead);
    const csmNames = namesFor((row) => row.clientSuccessManager);
    return [
      /*
       * ⚠️ **PAGE-SCOPED, AND THERE IS NOTHING TO CONVERT IT TO. Do not give it
       * a `param`.** `/companies` has no company-name filter of any kind:
       * `CompanyListParams` carries `search`, and that is a different question —
       * it matches name, short name, email AND billing city, it is a substring
       * rather than a value, and **the search box above this table already owns
       * it**. Two writers of one query key is the conflict `/invoices` records
       * on its Bill date column, and here it would be worse than a conflict:
       * `listFilters` only forwards the four ids in `SERVER_FILTER_PARAMS`, so a
       * `search` emitted by the hook would be **dropped before the request** —
       * while `visibleRows` returns early for a server column and stops
       * filtering the page too. Ticking a name would narrow nothing, anywhere,
       * with the funnel drawn as filtered and nothing failing.
       *
       * So this funnel picks exact names out of the rows already loaded and its
       * footnote says exactly that. Widening it needs an exact-match `name`
       * filter on `/companies`, which is a backend change and not a `param:`
       * added here.
       */
      { key: 'name', label: COMPANY_NAME_LABEL, get: (row) => row.name, sortKey: 'name' },
      /*
       * ⚠️ **Server-scoped, but the hook does NOT hold its value — the URL does.**
       * `param` is declared because `stageId` really is how this column filters,
       * and `multi` because `/companies` is the one endpoint that parses
       * `?stageId=1,2`. The page overrides `value` and `onChange` in `headerFor`,
       * so the hook's own `selected` for this key stays empty and it can never
       * emit a second, competing `stageId` alongside the one `load()` sends.
       *
       * `get` still earns its place: it feeds the funnel's Is empty / Is not
       * empty, which is page-scoped for every column because no endpoint here
       * exposes an is-null filter — a company predating stages reads back
       * `stage: null` and is "Unclassified".
       */
      {
        key: 'stage',
        label: 'Stage',
        get: (row) => row.stage?.id ?? null,
        format: (value) => stageNames.get(Number(value)) ?? `#${value}`,
        scope: 'server',
        param: 'stageId',
        multi: true,
        // Orders by the stage's NAME; no sentinel, so ascending opens on the
        // unclassified companies.
        sortKey: 'stage' satisfies CompanyListSort,
      },
      /*
       * The per-stage status columns (owner 2026-10-25), one spec per column,
       * directly after Stage. Server-filtered through the ONE shared
       * `?stageStatusId=` list — the page's `urlColumnFilters` row for each
       * column reads and writes only that stage's slice, and `headerFor`
       * supplies only that stage's statuses as options.
       *
       * ⚠️ *SUPERSEDED: "No `sortKey`: `/companies` does not sort by it."* It
       * does now: `?sort=stageStatus&sortStageId=<stageId>`. The `sortKey` is
       * the column id (`stageStatus:<stageId>`) — the same string as the key,
       * which `applySortChoice` relies on — and `sort` / `sortStageId` below
       * translate it into the server's pair.
       *
       * `get` is the status id only when the company is IN this column's stage
       * — every other row renders `—` here, so it reads as empty here too.
       */
      ...stageStatusColumns.map(
        (column): ColumnSpec<Company> => ({
          key: column.id,
          label: column.label,
          get: (row) =>
            row.stage?.id === column.stageId ? (row.stageStatus?.id ?? null) : null,
          format: (value) => stageStatusLabels.get(String(value)) ?? `#${value}`,
          scope: 'server',
          param: 'stageStatusId',
          multi: true,
          sortKey: column.id,
        }),
      ),
      /*
       * Where the account came from — server-filtered through `?sourceId=`, and
       * the one column here that also SORTS on the server.
       *
       * ⚠️ **`param: 'sourceId'` and `sortKey: 'source'`, one word apart.** The
       * filter names the foreign key and the sort names the master's NAME
       * column (`?sort=source` orders on the source's name, never `source_id`,
       * for the reason `country` already records: an id is production's
       * counter, so ordering by it yields a sequence that looks alphabetical at
       * a glance and is not — which reads as bad DATA rather than as the wrong
       * column). Sending either spelling where the other belongs is a 422
       * naming the key, not a silent no-op.
       *
       * `multi`, like every server column here: `/companies` is the one
       * endpoint on this API that parses `?sourceId=1,2`.
       *
       * `get` returns the id and feeds only the funnel's Is empty / Is not
       * empty, which is page-scoped for every column here because no endpoint
       * exposes an is-null filter. ⚠️ **That matters more on this column than
       * on any other**: blank is the ORDINARY state, so "Is empty" over the
       * loaded page is a genuinely different question from the filter beside
       * it, and the server cannot be asked the second one at all.
       *
       * `format` is never reached in practice — `headerFor` substitutes real
       * options — but resolves through the master rather than the embed, so it
       * cannot spell a source differently from the checkbox.
       */
      {
        key: 'source',
        label: 'Source',
        get: (row) => row.source?.id ?? null,
        format: (value) => sourceLabels.get(String(value)) ?? `#${value}`,
        scope: 'server',
        param: 'sourceId',
        multi: true,
        sortKey: 'source',
      },
      /*
       * What KIND of organisation the account is — server-filtered through
       * `?companyTypeId=` and server-sorted through `?sort=companyType`.
       *
       * ⚠️ **`param: 'companyTypeId'` and `sortKey: 'companyType'`, and the two
       * are not one word apart but one SUFFIX apart** — which is the same trap
       * Source and Country each record, in its most confusable form yet. The
       * filter names the foreign key; the sort names the master's NAME column,
       * because ordering by an id is production's counter and yields a sequence
       * that looks alphabetical at a glance and is not.
       *
       * ⛔ **`sortKey` is what keeps the two Type options in the mobile sort
       * select.** `sortChoices` is a NARROWING over the columns that declare
       * one, so a `SORT_OPTIONS` entry with no backing `sortKey` is dropped
       * silently — which looks exactly like the change not having shipped.
       *
       * `multi`, like every server column here: `/companies` is the one endpoint
       * on this API that parses `?companyTypeId=1,2`.
       *
       * `get` returns the id and feeds only the funnel's Is empty / Is not
       * empty, which is page-scoped for every column here because no endpoint
       * exposes an is-null filter. As on Source, unclassified is the ORDINARY
       * state today, so that test asks a genuinely narrower question than the
       * filter beside it and the server cannot be asked it at all.
       *
       * `format` is never reached in practice — `headerFor` substitutes real
       * options — but resolves through the master rather than the embed, so it
       * cannot spell a type differently from the checkbox.
       */
      {
        key: 'companyType',
        label: 'Type',
        get: (row) => row.companyType?.id ?? null,
        format: (value) => companyTypeLabels.get(String(value)) ?? `#${value}`,
        scope: 'server',
        param: 'companyTypeId',
        multi: true,
        sortKey: 'companyType',
      },
      /*
       * The two person columns. Both server-filtered, and both have their
       * options SUBSTITUTED in `headerFor` from `staffFilterOptions` — the whole
       * `/users` directory, not the ids the loaded page happens to name.
       *
       * `get` is not thereby redundant: it feeds the funnel's Is empty / Is not
       * empty, which is page-scoped on every column here because no endpoint
       * exposes an is-null filter (`?accountLeadId=` cannot ask for "nobody
       * leads this account"), and it is what the hook accumulates from on the
       * degraded path.
       */
      {
        key: 'accountLead',
        label: 'Account lead',
        get: (row) => row.accountLead?.id ?? null,
        format: (value) => leadNames.get(value) ?? `User #${value}`,
        scope: 'server',
        param: 'accountLeadId',
        // The popover has always rendered checkboxes; only the request was
        // single. `/companies` and `/companies/kanban` now parse `1,2`, so
        // ticking two people queries the whole table rather than silently
        // narrowing to the rows already loaded.
        multi: true,
        // Orders by the person's NAME, not the user id.
        sortKey: 'accountLead' satisfies CompanyListSort,
      },
      {
        key: 'clientSuccessManager',
        label: 'Client success manager',
        get: (row) => row.clientSuccessManager?.id ?? null,
        format: (value) => csmNames.get(value) ?? `User #${value}`,
        scope: 'server',
        param: 'clientSuccessManagerId',
        multi: true,
        sortKey: 'clientSuccessManager' satisfies CompanyListSort,
      },
      /*
       * The country OF RECORD (`d_companies.country_id`), server-filtered
       * through `?countryId=`.
       *
       * ⚠️ **THE ONLY country column now** — *SUPERSEDED 2026-08-31: "NOT the
       * Billing country column beside it … which is why they are two columns
       * rather than one header with a funnel bolted on."* The free-text column
       * is gone, and this one gained its inline editor (`CountryCell`,
       * `changeCountry`). So the funnel, the header and the editor all name
       * `country_id` — the split that made two columns necessary is closed, and
       * this is the field the server derives billing from.
       *
       * `get` returns the id and feeds only the funnel's Is empty / Is not
       * empty, which is page-scoped for every column here because no endpoint
       * exposes an is-null filter — `?countryId=` cannot express "no country
       * recorded" and is not meant to. `format` is never reached in practice
       * (`headerFor` substitutes real options) but resolves through the master
       * rather than the embed, for the label reason in `countryFilterOptions`.
       *
       * `multi`, like every server column here: `/companies` is the one endpoint
       * that parses `?countryId=1,2`.
       */
      {
        key: 'country',
        label: 'Country',
        get: (row) => row.country?.id ?? null,
        format: (value) => countryLabels.get(String(value)) ?? `#${value}`,
        scope: 'server',
        param: 'countryId',
        multi: true,
        // `country`, NOT `countryId` — the sort orders on the country's NAME.
        sortKey: 'country' satisfies CompanyListSort,
      },
      {
        key: 'regions',
        label: 'Regions',
        // A SET, in a model with one scalar per cell — so `get` doesn't decide
        // filtering. The SERVER filters this column (`?regionId=1,2`), and
        // `useTableFilters` trusts it for a `multi` column. What's left for
        // `get`: the emptiness test and a value for the hook's option list —
        // every region the row names, since reducing to one would misstate
        // the company. `headerFor` substitutes the real `regionOptions`.
        get: (row) =>
          row.regions.length === 0 ? null : row.regions.map((region) => region.id).join(','),
        scope: 'server',
        param: 'regionId',
        multi: true,
        // A SET, so the server orders by how MANY regions the company names.
        sortKey: 'regions' satisfies CompanyListSort,
      },
      /*
       * Projects participated in / Products purchased (2026-10-09). The FUNNEL
       * is a CYCLE filter — `?participatedCycleId=` / `?purchasedCycleId=`,
       * comma-separated, server-side — and the SORT is by the COUNT of distinct
       * projects / products, which the server scopes to the filtered cycles.
       *
       * ⚠️ The option values are CYCLE ids, so `get` cannot feed the option list
       * (it returns the row's item ids for the Is empty test only); `headerFor`
       * always supplies `cycleFilterOptions`.
       */
      {
        key: 'projectsParticipated',
        label: PROJECTS_PARTICIPATED_LABEL,
        get: (row) => {
          const items = purchasedItemsOf(purchaseHistoryOf(row), 'projects');
          return items === null || items.length === 0
            ? null
            : items.map((item) => item.id).join(',');
        },
        scope: 'server',
        param: 'participatedCycleId',
        multi: true,
        sortKey: 'projectsParticipated' satisfies CompanyListSort,
      },
      {
        key: 'productsPurchased',
        label: PRODUCTS_PURCHASED_LABEL,
        get: (row) => {
          const items = purchasedItemsOf(purchaseHistoryOf(row), 'products');
          return items === null || items.length === 0
            ? null
            : items.map((item) => item.id).join(',');
        },
        scope: 'server',
        param: 'purchasedCycleId',
        multi: true,
        sortKey: 'productsPurchased' satisfies CompanyListSort,
      },
      /*
       * The DATE of the account's last human note — server-sorted through
       * `?sort=lastNoteAt` and server-filtered through the INCLUSIVE pair
       * `?lastNoteAfter=` / `?lastNoteBefore=` (`YYYY-MM-DD`).
       *
       * ⚠️ **`range` is the whole switch** — the popover offers date operators
       * instead of a value list, and the hook emits the two bounds into
       * `serverParams` whatever `scope` says. `load()` merges them by NAME (see
       * `noteDateFilters`), because `listFilters` forwards only the id params.
       *
       * ⚠️ **Held in the hook, NOT in the URL**, unlike every id funnel here — so
       * the two keys are deliberately absent from `SAVED_FILTER_KEYS`. A key saved
       * but never read from the query string persists and narrows nothing.
       *
       * ⚠️ **A company with no human note matches NEITHER bound**, so the pair
       * does not partition the list; Is empty / Is not empty (page-scoped, off
       * `get`) remain the only way to reach the unnoted accounts on screen.
       */
      {
        key: 'lastNoteAt',
        label: ACCOUNT_LAST_NOTE_AT_LABEL,
        get: (row) => row.accountLastNote?.createdAt ?? null,
        sortKey: 'lastNoteAt',
        range: { minParam: 'lastNoteAfter', maxParam: 'lastNoteBefore', date: true },
      },
      // The same shape for the account's latest task: `?sort=lastTaskAt`, and
      // the INCLUSIVE pair `?lastTaskAfter=` / `?lastTaskBefore=`, merged into
      // the request by name through `taskDateFilters`.
      {
        key: 'lastTaskAt',
        label: ACCOUNT_LAST_TASK_AT_LABEL,
        get: (row) => row.accountLastTask?.createdAt ?? null,
        sortKey: 'lastTaskAt',
        range: { minParam: 'lastTaskAfter', maxParam: 'lastTaskBefore', date: true },
      },
      // ⚠️ **SORT-ONLY. There is no Created COLUMN any more** — this entry is
      // where "Newest first" lives. `useTableFilters` owns the sort state and
      // the ONLY way in is `headerFor(key).filter.sort.onSort`, which exists
      // only for a column declaring a `sortKey`. Delete this and the mobile
      // Sort select would still list "Newest first" and silently do nothing.
      // It renders nowhere: `SORT_ONLY_COLUMN_KEYS` keeps it out of the
      // mobile filter sheet, and it's absent from `CompaniesTableHead`.
      { key: 'createdAt', label: 'Created', get: (row) => row.createdAt, sortKey: 'createdAt' },
      /*
       * 🔴 **SORT-ONLY, AND IT WAS MISSING UNTIL 2026-09-11 — WHICH IS WHY
       * "Recently updated" AND "Least recently updated" DID NOTHING.**
       *
       * Reported by Daryna Kodinets on 4 September and reproduced in a browser
       * against the production API: picking either one left the select showing
       * `createdAt:desc` and **fired no request at all**, while the control
       * beside it — "Name A-Z" — held its value and sent
       * `?sort=name&order=asc`.
       *
       * The mechanism is the one the comment on `createdAt` immediately above
       * predicts in as many words: `useTableFilters` owns the sort state and
       * the ONLY way in is `headerFor(key).filter.sort.onSort`, which exists
       * only for a column declaring a `sortKey`. With no such column,
       * `applySortChoice` reached an optional chain over `undefined`, did
       * nothing, and the select snapped back. **No error, no request, no
       * console line — the option was a control that could not be operated.**
       *
       * ⚠️ **The Created column was deleted and left its sort behind; the
       * Updated column was deleted and did not.** Two removals of the same
       * shape, one of which remembered. `sortChoices` below is what stops the
       * third being silent.
       */
      { key: 'updatedAt', label: 'Updated', get: (row) => row.updatedAt, sortKey: 'updatedAt' },
    ];
  }, [
    rows,
    stageNames,
    stageStatusColumns,
    stageStatusLabels,
    countryLabels,
    sourceLabels,
    companyTypeLabels,
  ]);

  // Derived, never hand-listed, minus the sort-only entries — a filter over a
  // column nobody can see is a filter nobody can explain.
  const filterKeys = useMemo(
    () => columns.filter((column) => !SORT_ONLY_COLUMN_KEYS.has(column.key)).map((column) => column.key),
    [columns],
  );

  /**
   * The sorts this page can actually PERFORM — `SORT_OPTIONS` narrowed to the
   * keys some column declares a `sortKey` for.
   *
   * 🔴 **This exists so that an option offering a sort nothing can drive is
   * UNCONSTRUCTIBLE, rather than guarded against.** The select's only route
   * into the sort state is `headerFor(key).filter.sort.onSort`, which is
   * `undefined` for a key no column claims — so an unbacked option is a
   * control that silently refuses every click, which is exactly what
   * "Recently updated" was.
   *
   * ⚠️ **The failure it converts is silent-to-VISIBLE, not silent-to-loud.** A
   * sort whose column spec is deleted now DISAPPEARS from this select instead
   * of sitting in it doing nothing. A missing option is something somebody
   * reports; a dead one is something they work around and stop trusting the
   * page for.
   */
  const sortChoices = useMemo(() => {
    const driveable = new Set(
      columns.map((column) => column.sortKey).filter((key): key is string => key !== undefined),
    );
    const fixed: { value: string; label: string }[] = SORT_OPTIONS.filter((option) =>
      driveable.has(option.value.split(':')[0] ?? ''),
    );
    /*
     * One pair per per-stage status column, so a phone (where this select is
     * the only sort control) can reach the same sorts as the headers. The value
     * is `<column id>:<order>` — the column id itself contains a colon, so the
     * select's handler splits on the LAST one. Ordered by the status master's
     * order, companies outside the stage last in both directions (server).
     */
    const perStage = stageStatusColumns
      .filter((column) => driveable.has(column.id))
      .flatMap((column) => [
        { value: `${column.id}:asc`, label: `${column.label}, first to last` },
        { value: `${column.id}:desc`, label: `${column.label}, last to first` },
      ]);
    return [...fixed, ...perStage];
  }, [columns, stageStatusColumns]);

  /*
   * A STABLE memory id (2026-10-25). Without one the entry is keyed by a hash
   * of the column keys, and the per-stage status columns arrive after the
   * masters load and change whenever an admin edits stages or statuses — so
   * the key moved on every mount and remembered funnels/sort were lost, or
   * restored late with a visible reorder. `memoryId` keys it as
   * `<path>/table-id-companies` whatever columns are showing.
   *
   * ⚠️ `memoryId` always gets a `table-id-` prefix, so it can NEVER equal the
   * hash key existing users hold. Instead, `LEGACY_TABLE_MEMORY_SUFFIX` below
   * is copied across once (see the effect after this call).
   */
  const filters = useTableFilters(rows, columns, { memoryId: 'companies' });

  /*
   * One-time carry-over of the PRE-2026-10-25 memory entry. Its key was
   * `table-<djb2 base36 of the column keys joined by '|'>` (`shortHash` in
   * `lib/filter-memory/use-filter-memory.ts`) over the then-static column set
   * `name|stage|stageStatus|source|companyType|accountLead|clientSuccessManager|
   * country|regions|projectsParticipated|productsPurchased|lastNoteAt|
   * lastTaskAt|createdAt|updatedAt`, which hashes to `127syka`. Copied only
   * when the new entry has never been written (`undefined`; a remembered clear
   * is `''` and blocks it), once the account copy has settled, and never while
   * acting as somebody else. The table's late-memory path then adopts it. A
   * remembered funnel on the retired `stageStatus` column is dropped by
   * `rememberedFilters` (no column has that key) and a sort on it stays
   * pending/ignored by `rememberedSort`, so the old blob is harmless.
   * Removable once existing accounts have opened the page.
   */
  const location = useLocation();
  const memoryPath = stateMemoryPath(location.pathname);
  const legacyMemoryKey = stateMemoryKey(memoryPath, LEGACY_TABLE_MEMORY_SUFFIX);
  const currentMemoryKey = stateMemoryKey(memoryPath, 'table-id-companies');
  const legacyMemory = getFilterMemory('state', legacyMemoryKey);
  const currentMemory = getFilterMemory('state', currentMemoryKey);
  const memoryReady = isFilterMemoryReady();
  useEffect(() => {
    if (!memoryReady || !isFilterMemoryWritable()) return;
    if (currentMemory !== undefined || legacyMemory === undefined) return;
    setFilterMemory('state', currentMemoryKey, legacyMemory);
  }, [memoryReady, currentMemory, legacyMemory, currentMemoryKey]);

  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;

  // Only ONE layout is mounted, and only the rows near the screen. "Show all"
  // puts every company on the page, and mounting the phone cards and the
  // table rows for all of them at once froze the page (2026-10-04). The key
  // moves when the SET or ORDER of rows changes, never on an in-place edit, so
  // saving a cell does not collapse the list back to its first batch.
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const rowSetKey = useMemo(() => visibleRows.map((row) => row.id).join(','), [visibleRows]);
  const {
    start: windowStart,
    end: windowEnd,
    padTop: windowPadTop,
    padBottom: windowPadBottom,
    topSpacerRef,
  } = useRowWindow(visibleRows.length, isDesktop && visibleRows.length > 60, rowSetKey);
  const {
    start: cardsStart,
    end: cardsEnd,
    padTop: cardsPadTop,
    padBottom: cardsPadBottom,
    topSpacerRef: cardsTopSpacerRef,
  } = useRowWindow(visibleRows.length, !isDesktop && visibleRows.length > 40, rowSetKey, 320, 6);

  // The hook can neither count nor clear the five server-scoped funnels — the
  // URL holds them. So the banner and mobile sheet read the hook's count plus
  // this one, and "Clear column filters" clears both stores.
  //
  // ⚠️ **A column whose `emptiness` is set is ALREADY in `filters.activeCount`,
  // so counting its URL ids as well would report one funnel as two.** That is
  // why the test is "has ids AND its emptiness is `any`" rather than a plain
  // count of the non-empty entries.
  const urlFilterCount = Object.entries(urlColumnFilters).filter(
    ([key, entry]) =>
      entry !== undefined &&
      entry.ids.length > 0 &&
      (filters.headerFor(key).filter?.value.emptiness ?? 'any') === 'any',
  ).length +
    // Stage status ids no per-stage column claims still narrow the request.
    (orphanStageStatusFilterIds.length > 0 ? 1 : 0);
  const activeFilterCount = filters.activeCount + urlFilterCount;
  const clearAllFilters = useCallback(() => {
    filters.clearAll();
    clearUrlFilters();
  }, [filters, clearUrlFilters]);

  /*
   * The word a column is called, read from its own `ColumnSpec` rather than
   * written down a second time — so the column manager and the header can never
   * disagree about what a column is named.
   *
   * ⚠️ **Two columns have no spec**, because neither carries a funnel — the
   * actions lane and Project of interest — and `headerFor` answers an unknown
   * key with the KEY ITSELF, so the column manager would offer a switch labelled
   * `projectOfInterest`. `UNFILTERED_COLUMN_LABELS` is where both are spelled,
   * and the header cell reads the same record, so there is still exactly one
   * place a column's name lives.
   */
  const columnLabelOf = (key: string) =>
    unfilteredColumnLabel(key) ?? filters.headerFor(key).label;

  /*
   * 🔴 **HIDING A COLUMN HIDES ITS FUNNEL, AND FIVE OF THE SIX FUNNELS HERE
   * NARROW THE REQUEST.** Stage, Country, Account lead, Client success manager
   * and Regions are `scope: 'server'` — the filter goes to `/companies`, so a
   * hidden column leaves the LIST narrowed with no control on screen to clear
   * it. A short list reads as a small dataset, never as a filter, which this
   * page's own saved-filter note already calls "the worst outcome available".
   *
   * ⚠️ **The answer taken is (b): the filter stays CLEARABLE FROM ELSEWHERE, and
   * the column is named.** The banner below the panel header already renders
   * whenever `activeFilterCount > 0` — it counts a hidden column's filter, since
   * `filters.activeCount` runs over the column SPECS and knows nothing about
   * what renders — and it already carries "Clear column filters". All that was
   * missing was WHICH column, because a count alone cannot be acted on when the
   * funnel that set it is off screen.
   *
   * ⛔ **The other option — forcing a filtered column back on screen — was
   * built, tested against its own failure case and REJECTED.** It makes the
   * manager's checkbox a dead control: untick Account lead while an account-lead
   * filter is set and the tick clears, the column stays exactly where it was,
   * and nothing says why. That is the affordance-with-no-outcome this repo
   * forbids, and it would be silent. The consolation it offered — reaching the
   * one funnel rather than clearing all of them — is not worth a switch that
   * visibly does nothing.
   *
   * This is a state the reader cannot otherwise see, which is the documented
   * exception to the no-helper-text rule, not a caption on a control.
   */
  const hiddenFilteredColumnLabels = columnKeys
    .filter((key) => {
      if (key === ACTIONS_COLUMN_KEY) return false;
      if (visibleColumnKeys.includes(key)) return false;
      // Every server-scoped funnel's ticked ids live in the URL; only its
      // emptiness is hook state, which the check below still catches.
      const urlFilter = urlColumnFilters[key];
      if (urlFilter && urlFilter.ids.length > 0) return true;
      const value = filters.headerFor(key).filter?.value;
      return value !== undefined && (value.selected.length > 0 || value.emptiness !== 'any');
    })
    .map(columnLabelOf)
    // A status filter no column on screen can show (see `orphanStageStatusFilterIds`).
    .concat(orphanStageStatusFilterIds.length > 0 ? ['Stage status'] : []);

  /*
   * How many countries the funnel is narrowed to, and how to clear just that
   * one.
   *
   * ⚠️ *SUPERSEDED 2026-09-12: "read from the HOOK, unlike Stage, whose value
   * lives in the URL."* **Country's ticked ids live in the URL now too**, so
   * this reads `countryFilterIds` — and the distinction the old sentence drew
   * has gone with it, since all five server funnels are stored the same way.
   *
   * ⚠️ `emptiness` is deliberately NOT counted, and `setCountryFilter` cannot
   * touch it. The empty-state branch this feeds explains that no company has a
   * country of record, and Is empty / Is not empty is page-scoped over the
   * loaded twenty — a different claim. Only a selection of real ids can produce
   * the "matches nothing at all" case, and this control clears the COUNTRY
   * filter rather than everything the column holds: "Clear column filters" is
   * the one that clears both, and offering two controls that mean the same
   * thing is how a reader learns neither is trustworthy.
   */
  const countryFilterCount = countryFilterIds.length;
  const clearCountryFilter = useCallback(() => {
    setCountryFilter([]);
  }, [setCountryFilter]);

  // `serverParams` keeps a stable identity while its contents are unchanged, so
  // it is safe both as a memo dep and, through `listFilters`, as a fetch dep.
  const { serverParams } = filters;
  // Typed against `CompanyListParams`, whose id filters are `IdListFilter` —
  // one id or a validated comma-separated list, the only params type on the
  // API that accepts one.
  const listFilters = useMemo(() => {
    const next: Pick<CompanyListParams, ServerFilterParam> = {};
    for (const param of SERVER_FILTER_PARAMS) {
      const value = idFilterValue(serverParams[param]);
      if (value !== undefined) next[param] = value;
    }
    /*
     * ⚠️ **THE URL WINS FOR ALL FOUR, AND IT IS WRITTEN AFTER THE LOOP FOR THAT
     * REASON.** Every funnel's ticked ids live in the query string now (see
     * `urlColumnFilters`); each hook `selected` is held empty so it cannot
     * compete, but reading `serverParams` first and overwriting here means the
     * two can never disagree even if a future change starts storing something
     * there — the URL is the single source of truth for these keys and the last
     * writer is the one that says so.
     *
     * ⚠️ **The loop above is therefore overwritten on every key today, and it
     * stays.** It is what a server param added to `SERVER_FILTER_PARAMS` without
     * URL backing would fall through to; deleting it would make that param
     * silently unsendable rather than merely un-shareable.
     *
     * ⛔ **The list is spelled as a CSV STRING, one id as a NUMBER**, exactly as
     * `load()` and `boardParams` spell Stage. `/companies` is the one endpoint
     * that parses the comma-separated form — this spelling must not be carried
     * to another page's params builder.
     */
    for (const param of SERVER_FILTER_PARAMS) {
      const ids = urlFilterIdsByParam[param];
      const [only] = ids;
      if (ids.length === 1 && only !== undefined) next[param] = only;
      else if (ids.length > 1) next[param] = ids.join(',');
      // Cleared means cleared: never leave a stale id behind from `serverParams`.
      else delete next[param];
    }
    return next;
  }, [serverParams, urlFilterIdsByParam]);

  /*
   * The Last-note-added date bounds, out of `serverParams` BY NAME.
   *
   * ⛔ **Without this the funnel is a control that silently does nothing** —
   * `listFilters` forwards only the id params in `SERVER_FILTER_PARAMS`, so the
   * two bounds the hook emits would be dropped before the request while the
   * funnel reads as filled. Kept apart from `listFilters` because that object
   * also feeds `boardParams`' key-by-key reads, and the companies kanban schema
   * is `.strict()` and declares neither key — board view never sends them.
   *
   * The strings go through exactly as the box holds them (`YYYY-MM-DD`); an
   * empty or invalid box is omitted upstream by `rangeBounds`.
   */
  const noteDateFilters = useMemo<Pick<CompanyListParams, 'lastNoteAfter' | 'lastNoteBefore'>>(
    () => ({
      ...(serverParams.lastNoteAfter ? { lastNoteAfter: serverParams.lastNoteAfter } : {}),
      ...(serverParams.lastNoteBefore ? { lastNoteBefore: serverParams.lastNoteBefore } : {}),
    }),
    [serverParams],
  );

  // The Last-task-added bounds, by name, for the same reason as the note pair.
  const taskDateFilters = useMemo<Pick<CompanyListParams, 'lastTaskAfter' | 'lastTaskBefore'>>(
    () => ({
      ...(serverParams.lastTaskAfter ? { lastTaskAfter: serverParams.lastTaskAfter } : {}),
      ...(serverParams.lastTaskBefore ? { lastTaskBefore: serverParams.lastTaskBefore } : {}),
    }),
    [serverParams],
  );

  // Written out key by key, only with keys the COMPANIES board accepts:
  // `search` matches the card title (company NAME only, narrower than the
  // list's name/email/city); `stageId` narrows to the selected columns;
  // `accountLeadId`,
  // `clientSuccessManagerId`, `regionId` are the three server-scoped funnels.
  // (`facultyId` is a fourth key the board accepts but this page never sends,
  // since it has no Faculty column.) Deliberately absent: page-scoped column
  // filters and the sort — the board has no ordering parameter at all.
  const boardParams = useMemo<KanbanQueryParams>(() => {
    // `stageStatusId` is declared by the companies board (2026-10-25) but not
    // yet on the shared `KanbanQueryParams` type (`lib/types.ts`), so it is
    // widened here; `listQuery` serialises every key it is handed.
    const params: KanbanQueryParams & { stageStatusId?: IdListFilter } = {
      cardsPerColumn: BOARD_CARDS_PER_COLUMN,
    };
    if (search) params.search = search;
    // The Stage funnel's value lives in the URL, spelled here exactly as
    // `load()` spells it for the list. The companies board is the ONE board
    // that parses a list — every other board 422s on it.
    if (stageFilterIds.length === 1 && stageFilterIds[0] !== undefined) {
      params.stageId = stageFilterIds[0];
    } else if (stageFilterIds.length > 1) {
      params.stageId = stageFilterIds.join(',');
    }
    // The board accepts all three of these as lists, on this board alone, so
    // a filter set on the table survives the switch to Board. Key by key,
    // never by spreading `listFilters`: the kanban schema is `.strict()` and
    // built per board, so a key belonging to another board is a 422.
    if (listFilters.accountLeadId !== undefined) params.accountLeadId = listFilters.accountLeadId;
    if (listFilters.clientSuccessManagerId !== undefined) {
      params.clientSuccessManagerId = listFilters.clientSuccessManagerId;
    }
    // `regionId` is shared with the DELIVERABLES board, which takes exactly
    // one id and 422s on a list — it's a list here only because this is the
    // companies board.
    if (listFilters.regionId !== undefined) params.regionId = listFilters.regionId;
    // The per-stage status funnels' ONE shared list, orphan ids already
    // dropped (`stageStatusFilterIds`), as a list like the others here.
    if (listFilters.stageStatusId !== undefined) params.stageStatusId = listFilters.stageStatusId;
    return params;
  }, [search, stageFilterIds, listFilters]);

  // The board's move endpoint is WRITE_ROLES, same tier as the inline Stage
  // select — dragging a card is not an admin act.
  const moveDenial = denialReason(ability, 'update', 'companies');

  const activeSort = filters.sort;
  /*
   * ⚠️ **Widened from `ListSort` to `CompanyListSort` on 2026-09-14, WITH the
   * header sort it carries and not before it.** A `sortKey` on a column spec is
   * only half a sort: the header renders its Ascending/Descending rows the
   * moment the spec declares one, and if this line still narrowed to the three
   * shared members, `source` would fall through to `createdAt` here — a sort
   * control that visibly ticks and silently reorders by something else. That is
   * strictly worse than the "Recently updated" defect two comments down, which
   * at least did nothing.
   */
  /*
   * A per-stage status column's header sort (decision 6): its `sortKey` is the
   * column id `stageStatus:<stageId>`, and the server takes the PAIR
   * `?sort=stageStatus&sortStageId=<stageId>` (422 without the id). Only a
   * stage that still has a column counts — a remembered sort on a column that
   * has gone falls back to the default rather than sending a dead stage.
   */
  const sortStageId: number | null =
    activeSort && stageStatusColumnById.has(activeSort.key)
      ? parseStageStatusColumnId(activeSort.key)
      : null;
  const sort: CompanyListSort =
    sortStageId !== null
      ? 'stageStatus'
      : activeSort && isCompanySortKey(activeSort.key)
        ? activeSort.key
        : 'createdAt';
  const order: ListOrder = activeSort?.direction ?? 'desc';
  /** The key the Sort control and `applySortChoice` speak: the column id for a stage sort. */
  const sortControlKey: string =
    sortStageId !== null && activeSort ? activeSort.key : sort;

  /**
   * What the Sort control OFFERS: `sortChoices`, with the sort that is ALREADY
   * IN EFFECT pinned back if that narrowing dropped it.
   *
   * 🔴 **This exists because the control became a `Combobox` on 2026-09-13**
   * (owner's decision: every non-column filter dropdown is searchable, however
   * few options it carries). **A `Combobox` whose `value` matches no option
   * renders its PLACEHOLDER** — the trap `countryOptions` above is written
   * against, arriving here on a list that is NARROWED rather than fetched. A
   * native `<select>` kept the value on screen; this one would read "Sort
   * companies", i.e. *unsorted*, over a request that really is ordering by
   * that key. Wrong, and invisible.
   *
   * ⚠️ **It cannot fire today, and that is not a reason to leave it out.**
   * `sort` and `order` are normalised immediately above, so the value is always
   * one of `SORT_OPTIONS`; and the three sort-bearing column specs are declared
   * unconditionally, so `sortChoices` is currently the whole list. **Both
   * halves are one column-spec deletion away from being false — which is
   * exactly the deletion this file already records happening TWICE**, to
   * Created and to Updated, and only one of the two remembered its sort.
   *
   * ⚠️ **A pin is NOT a re-offer and it does not undo `sortChoices`.** That
   * narrowing exists so a sort nothing can drive is never offered as a CHOICE;
   * this adds back only the one value already in effect, labelled as unbacked,
   * so the reader can still see what the list is ordered by. **Fail towards the
   * visible wrong answer**: a labelled dead row is something somebody reports,
   * a placeholder over a live sort is something nobody ever sees.
   */
  const sortSelectOptions = useMemo(() => {
    const active = `${sortControlKey}:${order}`;
    const options = sortChoices.map((choice) => ({ value: choice.value, label: choice.label }));
    if (options.some((option) => option.value === active)) return options;
    // First, not appended — it is the value on screen, and the same shape as
    // `countryOptions`' injection of a country the master no longer lists.
    // The `??` is structural: `active` is built from the normalised pair, so a
    // miss here means `SORT_OPTIONS` itself stopped covering `ListSort`.
    const label = SORT_OPTIONS.find((option) => option.value === active)?.label ?? active;
    return [{ value: active, label: `${label} — no longer offered` }, ...options];
  }, [sortChoices, sortControlKey, order]);

  // The page a request asks for. Pinned to 1 while showing all, so the many
  // `setPage(1)` resets a filter change performs do not each re-read every row.
  const requestPage = showAll ? 1 : page;

  /*
   * Everything the list request narrows by, without the paging. ONE object for
   * the list read and the Export, so the file can never describe a different
   * set of companies from the one on screen.
   */
  const listQuery = useMemo<Omit<CompanyListParams, 'page' | 'perPage'>>(
    () => ({
      sort,
      order,
      // Only with `sort=stageStatus`, and never without it there (a 422).
      ...(sort === 'stageStatus' && sortStageId !== null ? { sortStageId } : {}),
      ...listFilters,
      ...noteDateFilters,
      ...taskDateFilters,
      ...(search ? { search } : {}),
      // One id goes as a number, several as the comma-separated form
      // `/companies` alone parses. `IdListFilter` is `number | string`.
      ...(stageFilterIds.length === 1 && stageFilterIds[0] !== undefined
        ? { stageId: stageFilterIds[0] }
        : stageFilterIds.length > 1
          ? { stageId: stageFilterIds.join(',') }
          : {}),
    }),
    [sort, sortStageId, order, listFilters, noteDateFilters, taskDateFilters, search, stageFilterIds],
  );

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /*
   * Exports EXACTLY the list on screen: the same `listQuery`, no paging, and the
   * columns the reader currently shows, in order. `actions` is controls, not
   * data. The server appends its own fixed extras and refuses (422) rather than
   * truncating over its cap.
   */
  const runExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const file = await api.exportCompanies({
        ...listQuery,
        columns: columnLayout.visibleKeys.filter((key) => !NOT_EXPORTED_COLUMN_KEYS.has(key)),
      });
      saveBlob(file.filename, file.blob);
    } catch (caught) {
      setExportError(exportFailureMessage(caught));
    } finally {
      setExporting(false);
    }
  }, [listQuery, columnLayout.visibleKeys]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: CompanyListParams = {
        page: requestPage,
        perPage: PER_PAGE,
        ...listQuery,
      };
      /*
       * "Show all": the same params, read page after page at the server's
       * ceiling of 100 and handed on as ONE page, so everything below — the
       * column filters, the optimistic row patches, the footer — reads it
       * exactly as it reads a normal page. `fetchAllPages` throws rather than
       * returning a partial list, so a failed second page lands in the same
       * catch as a failed single read. A load with no signal (the reload after
       * a save) gets its own controller: the helper needs one either way.
       */
      const request: Promise<Paginated<Company>> = showAll
        ? fetchAllPages<Company>(
            (pageNumber, perPage, pageSignal) =>
              api.listCompanies({ ...params, page: pageNumber, perPage }, pageSignal),
            signal ?? new AbortController().signal,
          ).then((all) => {
            setShowAllTruncated(all.truncated);
            return {
              data: all.rows,
              meta: { page: 1, perPage: all.rows.length, total: all.total, totalPages: 1 },
            };
          })
        : api.listCompanies(params, signal).then((data) => {
            setShowAllTruncated(false);
            return data;
          });
      return request
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          /*
           * 🔴 **DEPLOY ORDER: THE BACKEND HALF OF `?countryId=` SHIPS FIRST,
           * AND THIS IS WHAT HAPPENS IF IT HAS NOT.**
           *
           * `listCompaniesQuerySchema` is `.strict()`, so a server predating the
           * filter answers **422 naming the key** — not a silent strip, and not
           * an unfiltered 200. Left unhandled that renders as a bare
           * "Validation failed" over an empty table, which reads as the company
           * list being broken rather than as one filter being ahead of the API.
           *
           * ⚠️ Matched on the KEY through `formMessages`, never on the message
           * text: an unrecognised key arrives as `{path: '', key: 'countryId'}`
           * and `isUnrecognisedKeyDetail` is what separates it from the
           * "at least one field is required" refusal that shares its empty path.
           * Matching the wording would break the day the sentence improves.
           *
           * The filter stays SET rather than being silently dropped and retried
           * — a retry without the key succeeds and returns the unfiltered list
           * under a filtered heading, which is the failure this whole page
           * spends three other comments avoiding. The reader is told to clear
           * it, and can.
           */
          if (
            caught instanceof ApiError &&
            caught.status === 422 &&
            caught.formMessages.some((line) => line.includes('countryId'))
          ) {
            setError(
              'This EMERGE build filters companies by country, but the API it is talking to does not support that yet. Clear the Country filter to see the list.',
            );
            return;
          }
          // The same deploy-order case for the Last-note-added range: a server
          // predating it answers a 422 naming the key. Kept SET, never retried
          // without it, for the reason above.
          if (
            caught instanceof ApiError &&
            caught.status === 422 &&
            caught.formMessages.some(
              (line) => line.includes('lastNoteAfter') || line.includes('lastNoteBefore'),
            )
          ) {
            setError(
              `This SEED EMERGE build filters companies by the date of their last note, but the API it is talking to does not support that yet. Clear the ${ACCOUNT_LAST_NOTE_AT_LABEL} filter to see the list.`,
            );
            return;
          }
          // The same deploy-order case for the Last-task-added range, and for
          // `?sort=lastTaskAt` on a server that does not know the sort key.
          if (
            caught instanceof ApiError &&
            caught.status === 422 &&
            (caught.formMessages.some(
              (line) => line.includes('lastTaskAfter') || line.includes('lastTaskBefore'),
            ) ||
              (sort === 'lastTaskAt' && caught.fieldErrors.sort !== undefined))
          ) {
            setError(
              `This SEED EMERGE build sorts and filters companies by the date of their last task, but the API it is talking to does not support that yet. Clear the ${ACCOUNT_LAST_TASK_AT_LABEL} sort and filter to see the list.`,
            );
            return;
          }
          // The same deploy-order case for the per-stage status sort.
          if (
            caught instanceof ApiError &&
            caught.status === 422 &&
            sort === 'stageStatus' &&
            (caught.fieldErrors.sort !== undefined ||
              caught.fieldErrors.sortStageId !== undefined ||
              caught.formMessages.some((line) => line.includes('sortStageId')))
          ) {
            setError(
              'This SEED EMERGE build sorts companies by stage status, but the API it is talking to does not support that yet. Choose another sort to see the list.',
            );
            return;
          }
          setError(caught instanceof Error ? caught.message : 'Failed to load companies');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    // `sort` is read again in the catch, to word a refused sort key.
    [requestPage, showAll, listQuery, sort],
  );

  // Debounce so typing doesn't fire a request per keystroke. Board view skips
  // the list request outright — it has its own fetch and its own debounce, and
  // two requests per keystroke for a table nobody can see is the wrong shape
  // against an office-wide per-IP limiter — 12,000/60s (⚠️ not the 120/60s
  // this once said, which is what made it read as imminent rather than wasteful).
  // It also waits for the stored show-all choice, so an account that keeps it
  // on does not read page 1 and then every row. A failed read resolves too.
  useEffect(() => {
    if (view === 'board' || !showAllLoaded) return;
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, view, showAllLoaded]);

  // The hook's headers, with the page reset folded into the events that
  // change the query, so no request fires for a page that no longer exists.
  // A page-scoped column deliberately does NOT reset — it filters rows
  // already loaded. Not memoised: the hook returns a fresh object each
  // render, so a `useCallback` here would hold nothing.
  function headerFor(key: string): FilterableTHProps {
    const props = filters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    const sortControl = filter.sort;
    // `undefined` for every key that is not a person column, and ALSO for a
    // person column whose directory has not been read — the two are handled by
    // one spread below, because both mean the same thing to the hook: keep the
    // accumulated list. See `staffFilterOptions`.
    const staffOptionList = isStaffColumn(key) ? staffFilterOptions[key] : undefined;
    /*
     * The store this column's ticked ids come from: the URL for every
     * server-scoped funnel, the hook for the page-scoped ones.
     *
     * ⚠️ **`selectedValues` is what the option lists must be padded against, not
     * `filter.value.selected`.** The hook's `selected` is held empty on a
     * URL-backed column, so passing it to `withSelectedValues` would pad against
     * nothing — and a ticked id the master does not list (a retired region, a
     * country the `hasCompanies` filter no longer offers, a departed colleague)
     * would render NO checkbox, leaving the column reading as filtered with no
     * way out of it but Clear column filters.
     */
    const urlFilter = urlColumnFilters[key];
    const selectedValues = urlFilter ? urlFilter.ids.map(String) : filter.value.selected;
    return {
      ...props,
      filter: {
        ...filter,
        // Regions is the one column whose options the hook cannot derive —
        // its cell is a set — so the page supplies them. See `regionOptions`.
        ...(key === 'regions'
          ? {
              options: withSelectedValues(regionOptions, selectedValues),
              loading: regionsLoading,
            }
          : {}),
        // Country's options come from the `/countries` MASTER, not from the
        // loaded page: `get` returns an id, so the hook could only ever offer
        // the handful of ids the current twenty rows happen to name. A funnel
        // over one page of a paginated list is a funnel that hides matches.
        // (⚠️ The old reason — "today that is none at all, since every company
        // reads `country: null`" — expired on 2026-08-31: 111 of 149 companies
        // carry one. The argument above never depended on it.)
        //
        // A failed master read is `disabled` rather than an empty list, the same
        // rule Stage follows below and the same rule the inline picker follows:
        // "we could not read the countries" and "there are no countries" are
        // different facts, and only a disabled control with a reason says which.
        // A column already carrying a selection stays enabled, or a reader could
        // not clear a filter they can see is applied.
        ...(key === 'country'
          ? {
              // A ticked country outside the narrowed list keeps its NAME from
              // the full master rather than reading as `#id`.
              options: withSelectedValues(
                [
                  ...countryFilterOptions,
                  ...countries
                    .filter(
                      (record) =>
                        selectedValues.includes(String(record.id)) &&
                        !countryFilterOptions.some((o) => o.value === String(record.id)),
                    )
                    .map((record) => ({ value: String(record.id), label: record.countryName })),
                ],
                selectedValues,
              ),
              disabled: countriesFailed && selectedValues.length === 0,
            }
          : {}),
        // Account lead and Client success manager: the whole staff directory,
        // for the reason Country reads its master — a funnel over one page of a
        // paginated list hides matches and cannot say so.
        //
        // ⚠️ **Spread, so a failed or unfinished read leaves `options` ABSENT
        // rather than `[]`.** An empty array REPLACES the hook's accumulated
        // list and renders "no values" over rows that visibly name people; an
        // absent key falls back to accumulation, which is narrower but true.
        // Do not "tidy" this into `options: staffOptionList ?? []`.
        //
        // `loading` is passed either way: it is what the popover says while the
        // directory is in flight, and saying nothing there is how a list that is
        // about to grow reads as a list that is complete.
        //
        // ⚠️ NOT `disabled` on a failed read, unlike Country and Stage. Those
        // two have no fallback list at all — theirs come from a master and
        // nowhere else — where these columns still hold accumulated values a
        // reader can use, so disabling would take away a working control.
        // `usersFailed` already has its own sentence above the table.
        ...(isStaffColumn(key)
          ? {
              ...(staffOptionList
                ? { options: withSelectedValues(staffOptionList, selectedValues) }
                : {}),
              loading: usersLoading,
            }
          : {}),
        // Stage supplies its own options from the `/company-stages` master; a
        // failed read is `disabled`, not an empty list — the same rule the
        // inline picker follows. Its `value` comes from the generic block below,
        // like every other URL-backed column.
        ...(key === 'stage'
          ? {
              options: stageOptions,
              loading: stagesLoading,
              disabled: stagesFailed && selectedValues.length === 0,
            }
          : {}),
        /*
         * Source, on exactly the same terms as Stage: options from the master,
         * a failed read `disabled` rather than an empty list, and a column
         * already carrying a selection left ENABLED so a reader can always
         * clear a filter they can see is applied.
         *
         * ⚠️ **`options` is passed unconditionally, empty array included** —
         * NOT spread the way the staff columns' is. Those two have accumulated
         * values worth falling back to; this column's `get` is an id and the
         * page's rows are almost all `null`, so falling back to accumulation
         * would offer a source only when a loaded row already names it. An
         * empty list with `loading` set says "still reading"; an accumulated
         * one would quietly claim to be the vocabulary.
         */
        ...(key === 'source'
          ? {
              options: withSelectedValues(sourceFilterOptions, selectedValues),
              loading: sourcesLoading,
              disabled: sourcesFailed && selectedValues.length === 0,
            }
          : {}),
        /*
         * Type, on exactly the same terms as Source directly above: options from
         * the master, a failed read `disabled` rather than an empty list, and a
         * column already carrying a selection left ENABLED so a reader can always
         * clear a filter they can see is applied.
         *
         * ⚠️ **`options` is passed unconditionally, empty array included** — not
         * spread the way the staff columns' is. There is nothing worth
         * accumulating on this column: every row's `get` is `null` until somebody
         * classifies the account, so the fallback list would be empty anyway and
         * would claim to be the vocabulary while the master was still in flight.
         */
        ...(key === 'companyType'
          ? {
              options: withSelectedValues(companyTypeFilterOptions, selectedValues),
              loading: companyTypesLoading,
              disabled: companyTypesFailed && selectedValues.length === 0,
            }
          : {}),
        // A per-stage status column: ONLY its stage's statuses, from the master,
        // passed unconditionally (accumulated ids would carry no name). The
        // column exists only once both masters have loaded, so there is no
        // loading or failed state to show here.
        ...(stageStatusFilterOptionsByColumn.has(key)
          ? {
              options: withSelectedValues(
                stageStatusFilterOptionsByColumn.get(key) ?? [],
                selectedValues,
              ),
            }
          : {}),
        // The two cycle funnels: options from the CYCLE master, passed
        // unconditionally — the hook's accumulated values would be project and
        // product ids, which are not what these funnels filter by. A failed read
        // is `disabled` unless a selection must stay clearable, as on Type.
        ...(key === 'projectsParticipated' || key === 'productsPurchased'
          ? {
              options: withSelectedValues(cycleFilterOptions, selectedValues),
              loading: cyclesLoading,
              disabled: cyclesFailed && selectedValues.length === 0,
            }
          : {}),
        /*
         * ⚠️ **The five server-scoped funnels are the columns the hook does NOT
         * store** — ticked ids come from the URL and go straight back into it,
         * which is what keeps `/companies?countryId=82` a working deep link.
         * `emptiness` still belongs to the hook, so each control has two homes
         * and both are written on every change.
         *
         * ⚠️ **Spread LAST, after every option block, so its `value` wins.** The
         * `isStaffColumn` and `country` blocks above supply `options`, `loading`
         * and `disabled`, all of which these columns still want; only the ticked
         * half is overridden.
         */
        ...(urlFilter
          ? { value: { selected: selectedValues, emptiness: filter.value.emptiness } }
          : {}),
        onChange: (next) => {
          if (urlFilter) {
            // Junk cannot reach the URL from a checkbox, but the option list is
            // padded with whatever the query string already named, so a member
            // that arrived by hand can be ticked off again rather than being
            // re-asserted on the next change.
            const ids = next.selected.map(Number).filter((id) => Number.isInteger(id) && id > 0);
            urlFilter.set(ids);
            // The hook keeps only the emptiness half; `selected` stays empty so
            // it can never emit a competing value from `serverParams` beside the
            // one `load()` sends. `set` resets the pager itself.
            filter.onChange({ selected: [], emptiness: next.emptiness });
            return;
          }
          filter.onChange(next);
          // A range column's bounds are ALWAYS server-side, whatever its `scope`
          // says (see `ColumnSpec.range`), so it resets the pager too — page 4 of
          // the unfiltered list is a page number for a different result set.
          if (filter.scope === 'server' || filter.range) setPage(1);
        },
        ...(sortControl
          ? {
              sort: {
                direction: sortControl.direction,
                onSort: (direction) => {
                  sortControl.onSort(direction);
                  setPage(1);
                },
              },
            }
          : {}),
      },
    };
  }

  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  // Drives the same hook state the column headers do, rather than a second
  // `useState` pair, so the two can't become competing sort mechanisms.
  // `nextSort` is a column's `sortKey` — a `CompanyListSort`, or a per-stage
  // status column id (`stageStatus:<stageId>`).
  function applySortChoice(nextSort: string, nextOrder: ListOrder) {
    headerFor(nextSort).filter?.sort?.onSort(nextOrder);
  }

  function clearSearch() {
    setSearch('');
    setPage(1);
  }

  /**
   * Rewrites part of one loaded row in place — the optimistic write and its
   * undo. `patch` is a partial, not a whole record, since every inline editor
   * owns exactly one column and merging a whole `Company` back in would let
   * one column's save quietly revert another's.
   */
  function patchRow(id: number, patch: Partial<Company>) {
    setResult((current) =>
      current
        ? {
            ...current,
            data: current.data.map((row) => (row.id === id ? { ...row, ...patch } : row)),
          }
        : current,
    );
  }

  /** Sets or clears one row's message in a per-row error map. */
  function setRowError(
    setErrors: (updater: (current: Record<number, string>) => Record<number, string>) => void,
    id: number,
    message: string | null,
  ) {
    setErrors((current) => {
      if (message === null) {
        if (!(id in current)) return current;
        const rest: Record<number, string> = {};
        for (const [key, value] of Object.entries(current)) {
          if (Number(key) !== id) rest[Number(key)] = value;
        }
        return rest;
      }
      return { ...current, [id]: message };
    });
  }

  const setStageError = (id: number, message: string | null) =>
    setRowError(setStageErrors, id, message);
  const setStaffError = (column: StaffColumn, id: number, message: string | null) =>
    setRowError(column === 'accountLead' ? setLeadErrors : setCsmErrors, id, message);

  /**
   * Saves one row's stage immediately, optimistically, and rolls back if the
   * server refuses.
   *
   * ⚠️ **The failure this is shaped around is a SUCCESS.** `PATCH /companies`
   * is not `.strict()` — unknown body keys are silently stripped. Verified
   * against production: `PATCH { name: <unchanged>, stagId: 2 }` (typo)
   * answers **200** with the stage unchanged. So "the request resolved" is
   * not evidence the change happened.
   *
   * Two defences, both required: (1) the payload is built from ONE explicit
   * literal key, never a spread or computed key — exactly what a typo
   * survives; (2) the response's `data.stage` is compared to what was asked
   * for, turning a silent no-op into a rollback and a report.
   */
  async function changeStage(row: Company, nextStageId: number | null) {
    const previous = row.stage;
    // Nothing to express: an unchanged value is not a PATCH, and a PATCH with
    // no effective change is noise in the audit log at best.
    if ((previous?.id ?? null) === nextStageId) return;

    const optimistic: MasterRef | null =
      nextStageId === null
        ? null
        : {
            id: nextStageId,
            name: stages.find((record) => record.id === nextStageId)?.name ?? `#${nextStageId}`,
          };

    // A stage change CLEARS the Stage status server-side (owner 2026-10-23), so
    // the optimistic row clears it too and a rollback restores it.
    const previousStatus = row.stageStatus;
    const restore: Partial<Company> = {
      stage: previous,
      ...(previousStatus !== undefined ? { stageStatus: previousStatus } : {}),
    };

    setStageError(row.id, null);
    setRowError(setStageStatusErrors, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    setSavingStageIds((current) => [...current, row.id]);
    patchRow(row.id, {
      stage: optimistic,
      ...(previousStatus !== undefined ? { stageStatus: null } : {}),
    });

    try {
      // ONE explicit key. See the note above — this is the whole defence
      // against a misspelling that would answer 200 and change nothing.
      const { data } = await api.updateCompany(row.id, { stageId: nextStageId });

      const applied = data.stage?.id ?? null;
      if (applied !== nextStageId) {
        patchRow(row.id, restore);
        const message =
          'Stage not saved — the server didn’t apply the change. Try again.';
        setStageError(row.id, message);
        setWriteStatus(`${row.name}: stage not saved.`);
        return;
      }

      // The server's own object, not the optimistic one: it carries the
      // canonical name, which the master may spell differently.
      // The Stage status comes back from the server too: the stage change has
      // cleared it there. `undefined` (an older API) leaves the row untouched.
      patchRow(row.id, {
        stage: data.stage,
        ...(data.stageStatus !== undefined ? { stageStatus: data.stageStatus } : {}),
      });
      setWriteStatus(
        `${row.name} is now ${data.stage ? companyStageLabel(data.stage.id, data.stage.name) : 'unclassified'}.`,
      );

      // The row has stopped matching the stage filter it was listed under, so
      // the total is now wrong by one — refetch rather than hide it locally.
      // The same for a Stage status filter, which the cleared status no longer
      // matches.
      const appliedStatus = data.stageStatus?.id ?? null;
      if (
        (stageFilterIds.length > 0 && (applied === null || !stageFilterIds.includes(applied))) ||
        (stageStatusFilterIds.length > 0 &&
          (appliedStatus === null || !stageStatusFilterIds.includes(appliedStatus)))
      ) {
        void load();
      }
    } catch (caught) {
      patchRow(row.id, restore);
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this can only ever be a banner. The role
        // can change under a live access token, which lasts over five days, so
        // a hidden control is an optimisation and this is the guarantee.
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            'Your account no longer has permission to change a company’s stage.',
        );
        setWriteStatus(`${row.name}: stage change refused.`);
      } else {
        /*
         * A 422 here DOES name the path — the service throws
         * `[{ path: 'stageId', message: 'Company stage 99 does not exist' }]` —
         * which is unusual enough in this API to be worth relying on: it marks
         * the cell the user just used instead of a banner they have to
         * associate with a row themselves. Anything else falls back to the
         * server's own message.
         */
        const message =
          (caught instanceof ApiError ? caught.fieldErrors.stageId : undefined) ??
          // `formMessage` before `message`: a `.strict()` rejection carries an
          // EMPTY path, so it misses `fieldErrors` and arrives here, and on a
          // 422 `message` is only "Validation failed". The key it names lives
          // on `formMessage`.
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : 'Stage change failed');
        setStageError(row.id, message);
        setWriteStatus(`${row.name}: stage not saved.`);
      }
    } finally {
      setSavingStageIds((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's Stage status immediately, optimistically, and rolls back if
   * the server refuses — the same shape and both defences as `changeStage`:
   * ONE explicit literal key, and the response's `data.stageStatus` compared to
   * what was asked for.
   *
   * ⚠️ **`stageStatusId` on the way out, `stageStatus` on the way back.** A
   * status that does not belong to the row's stage is a 422
   * `STAGE_STATUS_WRONG_STAGE` naming `stageStatusId`, shown as the cell error.
   */
  async function changeStageStatus(row: Company, nextStatusId: number | null) {
    const previous = row.stageStatus ?? null;
    if ((previous?.id ?? null) === nextStatusId) return;

    const nextStatus =
      nextStatusId === null
        ? null
        : (stageStatuses.find((status) => status.id === nextStatusId) ?? null);

    // The status BEFORE the change, from the master by id. A status the master
    // no longer lists is left out (unknown), never guessed.
    const currentStatus =
      previous === null
        ? null
        : (stageStatuses.find((status) => status.id === previous.id) ?? undefined);

    // A sub-stage that resurfaces or needs a reason asks first (flags, never
    // names). `null` is a cancel: nothing changes and nothing is sent.
    const extra = await requestStageStatusChange({
      companyName: row.name,
      status: nextStatus,
      currentFollowUpOn: row.followUpOn ?? null,
      ...(currentStatus === undefined ? {} : { currentStatus }),
    });
    if (extra === null) return;

    const optimistic: MasterRef | null =
      nextStatusId === null
        ? null
        : { id: nextStatusId, name: nextStatus?.name ?? `#${nextStatusId}` };

    setRowError(setStageStatusErrors, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    setSavingStageStatusIds((current) => [...current, row.id]);
    patchRow(row.id, { stageStatus: optimistic });

    try {
      // ONE explicit key for the status — the defence `changeStage` describes —
      // plus only the keys the dialog collected (`followUpOn`,
      // `stageStatusReason`).
      const { data } = await api.updateCompany(row.id, {
        stageStatusId: nextStatusId,
        ...extra,
      });

      const applied = data.stageStatus?.id ?? null;
      if (applied !== nextStatusId) {
        patchRow(row.id, { stageStatus: previous });
        setRowError(
          setStageStatusErrors,
          row.id,
          'Stage status not saved — the server didn’t apply the change. Try again.',
        );
        setWriteStatus(`${row.name}: stage status not saved.`);
        return;
      }

      // The server's own objects: the stage is unchanged by this write, but it
      // is the one the status was validated against.
      patchRow(row.id, {
        stageStatus: data.stageStatus ?? null,
        stage: data.stage,
        // The server sets or clears the follow-up date with the status;
        // `undefined` (an older API) leaves the row untouched.
        ...(data.followUpOn !== undefined ? { followUpOn: data.followUpOn } : {}),
        ...(data.stageStatusSetAt !== undefined ? { stageStatusSetAt: data.stageStatusSetAt } : {}),
        ...(data.followUpTaskId !== undefined ? { followUpTaskId: data.followUpTaskId } : {}),
      });
      setWriteStatus(
        `${row.name}: stage status is now ${data.stageStatus ? data.stageStatus.name : 'cleared'}.`,
      );

      // The row may have stopped matching the status filter it was listed under.
      if (
        stageStatusFilterIds.length > 0 &&
        (applied === null || !stageStatusFilterIds.includes(applied))
      ) {
        void load();
      }
    } catch (caught) {
      patchRow(row.id, { stageStatus: previous });
      if (caught instanceof ApiError && caught.status === 403) {
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            'Your account no longer has permission to change a company’s stage status.',
        );
        setWriteStatus(`${row.name}: stage status change refused.`);
      } else {
        // `STAGE_STATUS_WRONG_STAGE` names `stageStatusId`, so `fieldErrors`
        // carries it; the code is matched next so the refusal still lands on
        // the cell if the path ever moves.
        // The follow-up and reason refusals (spec 2026-10-23) are matched by
        // code and land on the same cell.
        const message =
          (caught instanceof ApiError ? caught.fieldErrors.stageStatusId : null) ??
          (caught instanceof ApiError && STAGE_STATUS_CELL_ERROR_CODES.has(caught.code)
            ? (caught.fieldErrors.followUpOn ??
              caught.fieldErrors.stageStatusReason ??
              caught.message)
            : null) ??
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : null) ??
          'Stage status change failed';
        setRowError(setStageStatusErrors, row.id, message);
        setWriteStatus(`${row.name}: stage status not saved.`);
      }
    } finally {
      setSavingStageStatusIds((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's source immediately, optimistically, and rolls back if the
   * server refuses.
   *
   * ⚠️ **Deliberately the same shape as `changeStage` above, including both
   * defences its comment describes, and for the same reason:** `PATCH
   * /companies` is NOT `.strict()`, so an unrecognised body key is silently
   * STRIPPED and the request still answers **200** having changed nothing. So
   * (1) the payload is ONE explicit literal key — never a spread, never a
   * computed key, which is exactly what a typo survives — and (2) the
   * response's `data.source` is compared to what was asked for, turning a
   * silent no-op into a rollback and a report.
   *
   * ⚠️ **`sourceId` on the way out, `source` on the way back.** Read key ≠
   * write key, so the read-back below names `data.source` while `fieldErrors`
   * is keyed on the REQUEST key, `sourceId`. Reading the wrong one of the pair
   * is a message that never renders and a change that is never verified.
   */
  async function changeSource(row: Company, nextSourceId: number | null) {
    const previous = row.source;
    // Nothing to express: an unchanged value is not a PATCH, and a PATCH with
    // no effective change is noise in the audit log at best.
    if ((previous?.id ?? null) === nextSourceId) return;

    /*
     * Built from the MASTER row, which is where the label comes from — the same
     * source `sourceLabels` renders from, so the cell cannot flash a different
     * spelling of the source the reader just picked.
     */
    const optimistic: MasterRef | null =
      nextSourceId === null
        ? null
        : {
            id: nextSourceId,
            name: sources.find((record) => record.id === nextSourceId)?.name ?? `#${nextSourceId}`,
          };

    setRowError(setSourceErrors, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    setSavingSourceIds((current) => [...current, row.id]);
    patchRow(row.id, { source: optimistic });

    try {
      // ONE explicit key. See the note above — this is the whole defence
      // against a misspelling that would answer 200 and change nothing.
      const { data } = await api.updateCompany(row.id, { sourceId: nextSourceId });

      const applied = data.source?.id ?? null;
      if (applied !== nextSourceId) {
        patchRow(row.id, { source: previous });
        setRowError(
          setSourceErrors,
          row.id,
          'Source not saved — the server didn’t apply the change. Try again.',
        );
        setWriteStatus(`${row.name}: source not saved.`);
        return;
      }

      // The server's own object, not the optimistic one: it carries the
      // canonical name, which the master may spell differently.
      patchRow(row.id, { source: data.source });
      setWriteStatus(
        `${row.name}: source is now ${
          applied === null ? 'not recorded' : (sourceLabels.get(String(applied)) ?? `#${applied}`)
        }.`,
      );

      /*
       * The row may have stopped matching the source filter it was listed
       * under — refetch rather than hide it locally, which would disagree with
       * the total. It is a `multi` column, so the param is a CSV.
       */
      const sourceFilter = listFilters.sourceId;
      if (sourceFilter !== undefined) {
        const wanted = String(sourceFilter).split(',');
        if (applied === null || !wanted.includes(String(applied))) void load();
      }
    } catch (caught) {
      patchRow(row.id, { source: previous });
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this can only ever be the page banner. The
        // role can change under a live access token, which lasts over five
        // days, so a hidden control is an optimisation and this is the
        // guarantee.
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            'Your account no longer has permission to change a company’s source.',
        );
        setWriteStatus(`${row.name}: source change refused.`);
      } else {
        // Ordered by specificity, the correction recorded on `changeStaff` and
        // `changeCountry`: a 422 naming the field beats the form message, which
        // beats `message` — "Validation failed" on its own helps nobody.
        // `fieldErrors` is keyed on the REQUEST key, so `sourceId`, never the
        // read key `source`.
        const message =
          (caught instanceof ApiError ? caught.fieldErrors.sourceId : null) ??
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : null) ??
          'Source change failed';
        setRowError(setSourceErrors, row.id, message);
        setWriteStatus(`${row.name}: source not saved.`);
      }
    } finally {
      setSavingSourceIds((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's type immediately, optimistically, and rolls back if the
   * server refuses.
   *
   * ⚠️ **Deliberately the same shape as `changeSource` above, including both
   * defences its comment describes, and for the same reason:** `PATCH
   * /companies` is NOT `.strict()`, so an unrecognised body key is silently
   * STRIPPED and the request still answers **200** having changed nothing. So
   * (1) the payload is ONE explicit literal key — never a spread, never a
   * computed key, which is exactly what a typo survives — and (2) the response's
   * `data.companyType` is compared to what was asked for, turning a silent no-op
   * into a rollback and a report.
   *
   * ⚠️ **`companyTypeId` on the way out, `companyType` on the way back**, and
   * here the two differ only by a SUFFIX rather than by a word. Read key ≠ write
   * key, so the read-back below names `data.companyType` while `fieldErrors` is
   * keyed on the REQUEST key, `companyTypeId`. Reading the wrong one of the pair
   * is a message that never renders and a change that is never verified — and on
   * this pair the wrong spelling still typechecks against nothing at all.
   */
  async function changeCompanyType(row: Company, nextCompanyTypeId: number | null) {
    const previous = row.companyType;
    // Nothing to express: an unchanged value is not a PATCH, and a PATCH with
    // no effective change is noise in the audit log at best.
    if ((previous?.id ?? null) === nextCompanyTypeId) return;

    /*
     * Built from the MASTER row, which is where the label comes from — the same
     * source `companyTypeLabels` renders from, so the cell cannot flash a
     * different spelling of the type the reader just picked.
     */
    const optimistic: MasterRef | null =
      nextCompanyTypeId === null
        ? null
        : {
            id: nextCompanyTypeId,
            name:
              companyTypes.find((record) => record.id === nextCompanyTypeId)?.name ??
              `#${nextCompanyTypeId}`,
          };

    setRowError(setCompanyTypeErrors, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    setSavingCompanyTypeIds((current) => [...current, row.id]);
    patchRow(row.id, { companyType: optimistic });

    try {
      // ONE explicit key. See the note above — this is the whole defence
      // against a misspelling that would answer 200 and change nothing.
      const { data } = await api.updateCompany(row.id, { companyTypeId: nextCompanyTypeId });

      const applied = data.companyType?.id ?? null;
      if (applied !== nextCompanyTypeId) {
        patchRow(row.id, { companyType: previous });
        setRowError(
          setCompanyTypeErrors,
          row.id,
          'Type not saved — the server didn’t apply the change. Try again.',
        );
        setWriteStatus(`${row.name}: type not saved.`);
        return;
      }

      // The server's own object, not the optimistic one: it carries the
      // canonical name, which the master may spell differently.
      patchRow(row.id, { companyType: data.companyType });
      setWriteStatus(
        `${row.name}: type is now ${
          applied === null
            ? 'not recorded'
            : (companyTypeLabels.get(String(applied)) ?? `#${applied}`)
        }.`,
      );

      /*
       * The row may have stopped matching the type filter it was listed under —
       * refetch rather than hide it locally, which would disagree with the
       * total. It is a `multi` column, so the param is a CSV.
       */
      const companyTypeFilter = listFilters.companyTypeId;
      if (companyTypeFilter !== undefined) {
        const wanted = String(companyTypeFilter).split(',');
        if (applied === null || !wanted.includes(String(applied))) void load();
      }
    } catch (caught) {
      patchRow(row.id, { companyType: previous });
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this can only ever be the page banner. The
        // role can change under a live access token, which lasts over five
        // days, so a hidden control is an optimisation and this is the
        // guarantee.
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            'Your account no longer has permission to change a company’s type.',
        );
        setWriteStatus(`${row.name}: type change refused.`);
      } else {
        // Ordered by specificity, the correction recorded on `changeStaff` and
        // `changeCountry`: a 422 naming the field beats the form message, which
        // beats `message` — "Validation failed" on its own helps nobody.
        // `fieldErrors` is keyed on the REQUEST key, so `companyTypeId`, never
        // the read key `companyType`.
        const message =
          (caught instanceof ApiError ? caught.fieldErrors.companyTypeId : null) ??
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : null) ??
          'Type change failed';
        setRowError(setCompanyTypeErrors, row.id, message);
        setWriteStatus(`${row.name}: type not saved.`);
      }
    } finally {
      setSavingCompanyTypeIds((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's account lead or client success manager, optimistically, and
   * puts the row back if the server refuses.
   *
   * Deliberately the same shape as `changeStage` above, including both defences
   * its comment describes, and for the same reason —
   * `PATCH /companies` is not `.strict()`, so an unrecognised key is STRIPPED
   * and the request still answers **200** having changed nothing:
   *
   *  1. the payload is ONE explicit literal key, written out per branch rather
   *     than computed from `column`, because a computed key is exactly what a
   *     typo survives here;
   *  2. the response is read back, so a silent no-op becomes a visible failure.
   *
   * ⚠️ **Two things differ from the other two columns, and both are the API's,
   * not this page's:**
   *
   * - **A refusal names no field.** A bad user id is `422 One or more assigned
   *   users do not exist` with **no `details` array**, so there is no
   *   `fieldErrors.accountLeadId` to read and the server's own sentence is the
   *   message. It is still shown on the cell rather than as a banner: the
   *   sentence cannot say which of the three staff links was wrong, but this
   *   page can, because only one control was used.
   * - **`''` is not a way to clear.** An empty reference id is a 422; `null`
   *   clears. `StaffCell` converts at the boundary, so nothing below sees `''`.
   */
  /*
   * ⚠️ **Account lead history (owner 2026-10-25): an ACCOUNT LEAD change is never
   * written straight from the cell.** It opens `AccountLeadChangeDialog` (one
   * mount, at the bottom of the page) to ask "Effective from"; only its confirm
   * calls `changeStaff` with `accountLeadEffectiveFrom`. Cancel writes nothing,
   * and the controlled select still shows the stored lead. The CSM column is
   * unchanged and still saves on change. Returns the message for the dialog
   * when the server refused the DATE (the reader can fix it there), else null.
   */
  function requestStaffChange(row: Company, column: StaffColumn, nextUserId: number | null) {
    if (column !== 'accountLead') {
      void changeStaff(row, column, nextUserId);
      return;
    }
    if ((row.accountLead?.id ?? null) === nextUserId) return;
    setLeadChangeError(null);
    setLeadChange({ row, nextUserId });
  }

  async function confirmLeadChange(effectiveFrom: string) {
    if (leadChange === null) return;
    const { row, nextUserId } = leadChange;
    setLeadChangeBusy(true);
    setLeadChangeError(null);
    try {
      const dialogError = await changeStaff(row, 'accountLead', nextUserId, effectiveFrom);
      if (dialogError !== null) {
        setLeadChangeError(dialogError);
        return;
      }
      setLeadChange(null);
    } finally {
      setLeadChangeBusy(false);
    }
  }

  async function changeStaff(
    row: Company,
    column: StaffColumn,
    nextUserId: number | null,
    accountLeadEffectiveFrom?: string,
  ): Promise<string | null> {
    const previous = row[column];
    // An untouched field sends nothing — an unchanged value is not a PATCH.
    if ((previous?.id ?? null) === nextUserId) return null;

    const chosen = nextUserId === null ? null : users.find((person) => person.id === nextUserId);
    /*
     * The chosen person is always in `users` — the only option that is not is
     * the stored one, which the guard above has already returned on. The
     * fallback exists so the optimistic value is still a `UserSummary` if that
     * ever stops being true, rather than dropping the name to `undefined`.
     */
    const optimistic: UserSummary | null =
      nextUserId === null ? null : (chosen ?? { id: nextUserId, name: `#${nextUserId}`, email: '' });

    const label = STAFF_LABELS[column].toLowerCase();
    /*
     * Written out per branch rather than as `{ [column]: person }`. A computed
     * key widens to an index signature, which `Partial<Company>` accepts
     * happily — so a key that is not a column of `Company` would compile. The
     * point of these patches is that a wrong key is caught here and not by a
     * 200 that changes nothing.
     */
    const rowPatch = (person: UserSummary | null): Partial<Company> =>
      column === 'accountLead' ? { accountLead: person } : { clientSuccessManager: person };

    setStaffError(column, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    const setSaving = column === 'accountLead' ? setSavingLeadIds : setSavingCsmIds;
    setSaving((current) => [...current, row.id]);
    patchRow(row.id, rowPatch(optimistic));

    try {
      // ONE explicit key per branch. See the note above — this is the whole
      // defence against a misspelling that would answer 200 and change nothing.
      // The effective date rides ONLY with `accountLeadId` — alone it is a 422
      // (`ACCOUNT_LEAD_EFFECTIVE_FROM_WITHOUT_LEAD`).
      const { data } =
        column === 'accountLead'
          ? await api.updateCompany(
              row.id,
              accountLeadEffectiveFrom === undefined
                ? { accountLeadId: nextUserId }
                : { accountLeadId: nextUserId, accountLeadEffectiveFrom },
            )
          : await api.updateCompany(row.id, { clientSuccessManagerId: nextUserId });

      const saved = column === 'accountLead' ? data.accountLead : data.clientSuccessManager;
      const applied = saved?.id ?? null;
      if (applied !== nextUserId) {
        patchRow(row.id, rowPatch(previous));
        const message = `${label} not saved — the server didn’t apply the change. Try again.`;
        setStaffError(column, row.id, message);
        setWriteStatus(`${row.name}: ${label} not saved.`);
        return null;
      }

      // The server's own object, not the optimistic one: it carries the
      // canonical name and email as the directory spells them.
      patchRow(row.id, rowPatch(saved));
      setWriteStatus(`${row.name}: ${label} is now ${saved ? saved.name : 'unassigned'}.`);

      // The row may have stopped matching the column filter it was listed
      // under. Refetch rather than hide it locally, which would disagree
      // with the count. Both keys are `multi` columns.
      const staffFilter =
        column === 'accountLead' ? listFilters.accountLeadId : listFilters.clientSuccessManagerId;
      if (staffFilter !== undefined) {
        const wanted = String(staffFilter).split(',');
        if (applied === null || !wanted.includes(String(applied))) void load();
      }
      return null;
    } catch (caught) {
      patchRow(row.id, rowPatch(previous));
      // A refused EFFECTIVE DATE goes back to the dialog, where the date can be
      // fixed; every other refusal lands on the cell exactly as before.
      const dateRefusal = column === 'accountLead' ? leadDateRefusal(caught) : null;
      if (dateRefusal !== null) {
        setWriteStatus(`${row.name}: ${label} not saved.`);
        return dateRefusal;
      }
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this can only ever be a banner. The role can
        // change under a live access token, which lasts over five days, so a
        // hidden control is an optimisation and this is the guarantee.
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            `Your account no longer has permission to change a company’s ${label}.`,
        );
        setWriteStatus(`${row.name}: ${label} change refused.`);
      } else {
        // ⚠️ TWO staff 422s here carry different shapes, which is why this
        // reads `fieldErrors` first: *"One or more assigned users do not
        // exist"* carries **no `details` array at all**, so only
        // `formMessage`/`message` sees it. The **user-group refusal NAMES the
        // field** (`path: "accountLeadId"`) and its sentence says who may
        // hold the role and how to add somebody to the group — dropping to
        // `message` would print "Validation failed" over the top of it. The
        // server's own sentence is surfaced verbatim in both cases.
        const writeKey = column === 'accountLead' ? 'accountLeadId' : 'clientSuccessManagerId';
        const message =
          (caught instanceof ApiError ? caught.fieldErrors[writeKey] : null) ??
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : null) ??
          `${STAFF_LABELS[column]} change failed`;
        setStaffError(column, row.id, message);
        setWriteStatus(`${row.name}: ${label} not saved.`);
      }
      return null;
    } finally {
      setSaving((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's COUNTRY OF RECORD, optimistically, rolling back if the
   * server refuses — the same shape as `changeStage` and for the same reason.
   *
   * 🔴 **This writes `countryId`, a FOREIGN KEY, since 2026-08-31.** *SUPERSEDED:
   * "Saves one row's billing COUNTRY … the server TRIMS free text
   * (`optionalText` is `.trim()`) … `''` collapses to `NULL` server-side."*
   * Those two notes were about `billingCountry`, which this column no longer
   * edits and this app no longer collects on a company. An id has no trimming
   * and no empty-string collapse: `null` is the clear and `''` would be a 422.
   *
   * ⚠️ **The read-back is KEPT, and its justification is corrected rather than
   * dropped.** *SUPERSEDED: "`PATCH /companies` is not `.strict()`, so a 200 is
   * not evidence the key applied."* It IS strict (`companies.schema.ts:553` and
   * `:561`), so an unrecognised key is a 422 naming it, not a silent strip. The
   * read-back answers a DIFFERENT question, which strictness never answered: the
   * key was recognised and *not applied* — a guard that ignored it, a write that
   * landed elsewhere. Same reasoning as `CompanyDetailPage`'s `readBack`.
   *
   * ⚠️ **The stakes are higher than the old free-text write.** `country_id` is
   * what the server derives the billing region from, which decides the currency
   * and the Xero tax and branding defaults on a customer-facing approval — so a
   * silent no-op here is a company that looks re-filed and is not.
   *
   * ONE explicit key, never a spread or a computed key.
   */
  async function changeCountry(row: Company, next: number | null) {
    const previous = row.country;
    // An unchanged value is not a PATCH. `CountryCell` guards this too; keeping
    // it here means no caller can make the page write a no-op.
    if ((previous?.id ?? null) === next) return;

    /*
     * The optimistic embed is built from the MASTER row, because the master is
     * where the label comes from — the same source `countryLabels` renders
     * from, so the cell cannot flash a different spelling of the country the
     * reader just picked. The fallback keeps the shape rather than dropping to
     * `null`, which would read as "cleared" for the length of the request.
     */
    const chosen = next === null ? undefined : countries.find((record) => record.id === next);
    const optimistic: Company['country'] =
      next === null
        ? null
        : {
            id: next,
            name: chosen?.countryName ?? `Country #${next}`,
            code: chosen?.countryCode ?? '',
            billingRegion: chosen?.billingRegion ?? null,
          };

    setRowError(setCountryErrors, row.id, null);
    setWriteBanner(null);
    setWriteStatus('');
    setSavingCountryIds((current) => [...current, row.id]);
    patchRow(row.id, { country: optimistic });

    try {
      const { data } = await api.updateCompany(row.id, { countryId: next });

      const applied = data.country?.id ?? null;
      if (applied !== next) {
        patchRow(row.id, { country: previous });
        setRowError(
          setCountryErrors,
          row.id,
          'Country not saved — the server didn’t apply the change. Try again.',
        );
        setWriteStatus(`${row.name}: country not saved.`);
        return;
      }

      // The server's own embed, not the optimistic one — it carries the code
      // and the billing region the server resolved, which is what every other
      // reader of this row will see.
      patchRow(row.id, { country: data.country });
      setWriteStatus(
        `${row.name}: country is now ${
          applied === null ? 'not recorded' : (countryLabels.get(String(applied)) ?? `#${applied}`)
        }.`,
      );

      /*
       * ⚠️ **A refetch branch this column did not need before.** The old
       * free-text write could not change what the list matched, because
       * `billingCountry` has no list parameter at all. `?countryId=` IS one, so
       * a row can now stop matching the query it was listed under — refetch
       * rather than hide it locally, which would disagree with the total. It is
       * a `multi` column, so the param is a CSV.
       */
      const countryFilter = listFilters.countryId;
      if (countryFilter !== undefined) {
        const wanted = String(countryFilter).split(',');
        if (applied === null || !wanted.includes(String(applied))) void load();
      }
    } catch (caught) {
      patchRow(row.id, { country: previous });
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this can only ever be the page banner.
        setWriteBanner(
          denialReason(ability, 'update', 'companies') ??
            'Your account no longer has permission to change a company’s country.',
        );
        setWriteStatus(`${row.name}: country change refused.`);
      } else {
        // Ordered by specificity, the correction recorded on `changeStaff` and
        // `nameRefusal`: a 422 naming the field beats the form message, which
        // beats `message` — "Validation failed" on its own helps nobody.
        // `fieldErrors` is keyed on the REQUEST key, so `countryId`, never the
        // read key `country`.
        const message =
          (caught instanceof ApiError ? caught.fieldErrors.countryId : null) ??
          (caught instanceof ApiError ? caught.formMessage : null) ??
          (caught instanceof Error ? caught.message : null) ??
          'Country change failed';
        setRowError(setCountryErrors, row.id, message);
        setWriteStatus(`${row.name}: country not saved.`);
      }
    } finally {
      setSavingCountryIds((current) => current.filter((id) => id !== row.id));
    }
  }

  /**
   * Saves one row's REGIONS — the whole set, once, when the field is left.
   *
   * ⚠️ **`regionIds` is a REPLACE, the single most dangerous thing on this
   * page.** `[…]` replaces, `[]` clears, key OMITTED leaves the stored set
   * alone. Only ever reached from `InlineRegionsField`, which does not call
   * it when the selection is unchanged.
   *
   * Not optimistic, unlike the two selects — a replace-set has no single
   * value to put back, and the useful answer only exists once the server has
   * answered. The field shows its own "Saving…" instead.
   */
  const saveRegions = async (row: Company, next: string[]): Promise<InlineSetSaveResult> => {
    setWriteStatus('');
    try {
      const { data } = await api.updateCompany(row.id, { regionIds: next.map(Number) });

      // A 200 does not prove the change applied (`PATCH /companies` is not
      // `.strict()`). Ids come back exactly as sent or not at all, so the
      // read-back is conclusive here in a way it would not be for free text.
      const savedIds = new Set(data.regions.map((region) => String(region.id)));
      const applied = savedIds.size === next.length && next.every((id) => savedIds.has(id));
      if (!applied) {
        setWriteStatus(`${row.name}: regions not saved.`);
        return {
          status: 'warning',
          message:
            'Regions not saved — the server didn’t apply the change. Try again.',
        };
      }

      patchRow(row.id, { regions: data.regions });
      setWriteStatus(
        `${row.name}: regions saved — ${
          data.regions.length === 0
            ? 'none'
            : joinList(data.regions.map((region) => region.name))
        }.`,
      );

      // Same reasoning as the staff refetch above.
      const regionFilter = listFilters.regionId;
      if (regionFilter !== undefined) {
        const wanted = String(regionFilter).split(',');
        if (!wanted.some((id) => savedIds.has(id))) void load();
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        setWriteStatus(`${row.name}: regions not saved.`);
        if (caught.status === 403) {
          // Returned rather than raised as the page banner the two selects
          // use — `InlineRegionsField` reports success unless handed an
          // error, so it MUST be told. Not routed through `fieldErrors`,
          // which a 403 does not populate.
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'companies') ??
              'You no longer have permission to change this account.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This account no longer exists, so the change could not be saved.',
          };
        }
        // The 422 names EVERY offending id at once, so all bad chips are
        // marked in one pass instead of the user resubmitting to find the next.
        const message = caught.fieldErrors.regionIds ?? caught.formMessage ?? caught.message;
        return { status: 'error', message, invalidValues: refusedRegionIds(caught, next) };
      }
      setWriteStatus(`${row.name}: regions not saved.`);
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  /**
   * Saves one row's PROJECTS OF INTEREST — the whole set, once, when the field
   * is left.
   *
   * ⚠️ **`projectIdsOfInterest` is a REPLACE, exactly as `regionIds` is.**
   * `[…]` replaces, `[]` clears, key OMITTED leaves the stored set alone.
   * `InlineRegionsField` never calls this when the selection is unchanged,
   * which is what keeps an untouched field from sending anything — an empty
   * PATCH is a 422 in its own right.
   *
   * This writes the COMPANY's list of projects it is interested in and nothing
   * else: adding or clearing one creates or closes no sales pursuit (owner's
   * decision 2026-10-12).
   *
   * Not optimistic, unlike the two selects — a replace-set has no single value
   * to put back, and the useful answer only exists once the server has
   * answered. The field shows its own "Saving…" instead.
   */
  const saveProjectsOfInterest = async (
    row: Company,
    next: string[],
  ): Promise<InlineSetSaveResult> => {
    setWriteStatus('');
    try {
      const { data } = await api.updateCompany(row.id, {
        projectIdsOfInterest: next.map(Number),
      });

      // A 200 does not prove the change applied (`PATCH /companies` strips a
      // key it does not recognise and answers 200 having changed nothing). Ids
      // come back exactly as sent or not at all, so the read-back is
      // conclusive here in the way it is for regions and is not for free text.
      const savedIds = new Set(data.projectsOfInterest.map((project) => String(project.id)));
      const applied = savedIds.size === next.length && next.every((id) => savedIds.has(id));
      if (!applied) {
        setWriteStatus(`${row.name}: ${PROJECTS_OF_INTEREST_LABEL.toLowerCase()} not saved.`);
        return {
          status: 'warning',
          message: `${PROJECTS_OF_INTEREST_LABEL} not saved — the server didn’t apply the change. Try again.`,
        };
      }

      patchRow(row.id, { projectsOfInterest: data.projectsOfInterest });
      setWriteStatus(
        `${row.name}: ${PROJECTS_OF_INTEREST_LABEL.toLowerCase()} saved — ${
          data.projectsOfInterest.length === 0
            ? 'none'
            : joinList(data.projectsOfInterest.map((project) => project.name))
        }.`,
      );

      /*
       * No refetch branch, unlike Regions and the two person columns. Those
       * re-read the list because their column IS a server filter, so a row can
       * save itself out of the current query and has to leave. There is no
       * `?projectOfInterestId=` to filter on, so this set can never put the row
       * outside what was asked for.
       */
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        setWriteStatus(`${row.name}: ${PROJECTS_OF_INTEREST_LABEL.toLowerCase()} not saved.`);
        if (caught.status === 403) {
          // Returned rather than raised as the page banner the two selects use
          // — `InlineRegionsField` reports success unless handed an error, so
          // it MUST be told. Not routed through `fieldErrors`, which a 403 does
          // not populate.
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'companies') ??
              'You no longer have permission to change this account.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This account no longer exists, so the change could not be saved.',
          };
        }
        /*
         * ⚠️ **No `invalidValues`, deliberately.** Regions marks each refused
         * chip from `refusedRegionIds`, which reads the ids back out of the
         * server's SENTENCE. That is matching on wording, and it is only
         * tolerable there because the sentence is known and measured. The
         * refusal for this key has never been seen from here, so a parser for
         * it would be invented from a guess — and one that silently matched
         * nothing would mark no chips while looking as though it had.
         */
        const message =
          caught.fieldErrors.projectIdsOfInterest ?? caught.formMessage ?? caught.message;
        return { status: 'error', message };
      }
      setWriteStatus(`${row.name}: ${PROJECTS_OF_INTEREST_LABEL.toLowerCase()} not saved.`);
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  /**
   * A refusal from one of the two name writes, as one sentence for the one
   * control that caused it.
   *
   * ⚠️ Ordered by SPECIFICITY, not by which shape arrived first — the same
   * correction `changeStaff` records above. A 403 is handled by the caller: it
   * carries no `details`, so it is the page banner, never a field message.
   */
  function nameRefusal(
    caught: unknown,
    writeKey: 'name' | 'shortName' | 'billingName',
    fallback: string,
  ): string {
    if (caught instanceof ApiError) {
      // A length refusal DOES name the path here (`61 characters` comes back
      // with `details` naming `shortName`), so it marks the control the reader
      // just used. `formMessage` catches an unroutable key; `message` on a 422
      // is only "Validation failed", so it goes last.
      // No `?? fallback` after `message`: it is a non-nullable string, so the
      // fallback would be unreachable — it is for a throw that is not an
      // `Error` at all.
      return caught.fieldErrors[writeKey] ?? caught.formMessage ?? caught.message;
    }
    if (caught instanceof Error) return caught.message;
    return fallback;
  }

  /**
   * Saves one row's SHORT name — the same two defences, one different rule.
   *
   * ⚠️ **`''` is sent as `null`.** The API accepts both here (measured: each
   * answers 200 with the column back to `null`), which is the opposite of every
   * reference id on this endpoint, where `''` is a 422 and only `null` clears.
   * `null` is emitted from every clear path so this codebase never learns the
   * looser habit — and so the read-back has one shape to compare against.
   */
  const saveShortName = async (row: Company, next: string): Promise<InlineSaveResult> => {
    const previous = row.shortName;
    const wanted = next === '' ? null : next;
    setWriteBanner(null);
    setWriteStatus('');
    patchRow(row.id, { shortName: wanted });

    try {
      // ONE explicit key: PATCH strips an unknown key and still answers 200.
      const { data } = await api.updateCompany(row.id, { shortName: wanted });

      // Compared as the empty-equivalent, because the column comes back `null`
      // for a cleared value whichever of `''` and `null` was sent.
      if ((data.shortName?.trim() ?? '') !== next) {
        patchRow(row.id, { shortName: previous });
        setWriteStatus(`${row.name}: short name not saved.`);
        return {
          status: 'warning',
          message:
            'Short name not saved — the server didn’t apply the change. Try again.',
        };
      }

      patchRow(row.id, { shortName: data.shortName });
      setWriteStatus(
        data.shortName === null
          ? `${row.name}: short name cleared.`
          : `${row.name}: short name is now ${data.shortName}.`,
      );
      return { status: 'saved' };
    } catch (caught) {
      patchRow(row.id, { shortName: previous });
      setWriteStatus(`${row.name}: short name not saved.`);
      if (caught instanceof ApiError && caught.status === 403) {
        // Banner AND returned: a 403 has no `details`, and the field must be
        // told the write failed or `InlineField` reports "Saved.".
        const denial =
          denialReason(ability, 'update', 'companies') ??
          'Your account no longer has permission to change a company’s short name.';
        setWriteBanner(denial);
        return { status: 'error', message: `${denial} Nothing was saved.` };
      }
      if (caught instanceof ApiError && caught.status === 404) {
        return {
          status: 'error',
          message: 'This account no longer exists, so the change could not be saved.',
        };
      }
      return {
        status: 'error',
        message: nameRefusal(caught, 'shortName', 'Short name change failed'),
      };
    }
  };

  /**
   * Saves one row's BILLING name — the name its Xero Contact carries. The same
   * two defences as `saveShortName`, but NOT its `''` → `null` rule: the
   * billing name is required (owner 2026-10-25), so `validateCompanyBillingName`
   * refuses a blank box before this runs and the server refuses one with 422
   * `BILLING_NAME_REQUIRED`.
   *
   * ⚠️ **409 `COMPANY_BILLING_NAME_TAKEN` and 422 `BILLING_NAME_REQUIRED` are
   * matched on the CODE** and become the cell's error.
   */
  const saveBillingName = async (row: Company, next: string): Promise<InlineSaveResult> => {
    const previous = row.billingName;
    if (next.trim() === '') {
      return { status: 'error', message: 'Billing name is required.' };
    }
    const wanted = next;
    setWriteBanner(null);
    setWriteStatus('');
    patchRow(row.id, { billingName: wanted });

    try {
      // ONE explicit key: PATCH strips an unknown key and still answers 200.
      const { data } = await api.updateCompany(row.id, { billingName: wanted });

      // `undefined` back means the server does not know the key: it stripped
      // it and answered 200, which is a no-op and not a clear.
      if (data.billingName === undefined || (data.billingName?.trim() ?? '') !== next) {
        patchRow(row.id, { billingName: previous });
        setWriteStatus(`${row.name}: billing name not saved.`);
        return {
          status: 'warning',
          message:
            'Billing name not saved — the server didn’t apply the change. Try again.',
        };
      }

      patchRow(row.id, { billingName: data.billingName });
      setWriteStatus(`${row.name}: billing name is now ${data.billingName ?? ''}.`);
      return { status: 'saved' };
    } catch (caught) {
      patchRow(row.id, { billingName: previous });
      setWriteStatus(`${row.name}: billing name not saved.`);
      if (caught instanceof ApiError && caught.code === 'COMPANY_BILLING_NAME_TAKEN') {
        return {
          status: 'error',
          message: 'Another company already uses this name in Xero. Nothing was saved.',
        };
      }
      if (caught instanceof ApiError && caught.code === 'BILLING_NAME_REQUIRED') {
        return { status: 'error', message: 'Billing name is required. Nothing was saved.' };
      }
      if (caught instanceof ApiError && caught.status === 403) {
        // Banner AND returned: a 403 has no `details`, and the field must be
        // told the write failed or `InlineField` reports "Saved.".
        const denial =
          denialReason(ability, 'update', 'companies') ??
          'Your account no longer has permission to change a company’s billing name.';
        setWriteBanner(denial);
        return { status: 'error', message: `${denial} Nothing was saved.` };
      }
      if (caught instanceof ApiError && caught.status === 404) {
        return {
          status: 'error',
          message: 'This account no longer exists, so the change could not be saved.',
        };
      }
      return {
        status: 'error',
        message: nameRefusal(caught, 'billingName', 'Billing name change failed'),
      };
    }
  };

  /**
   * Handed to every `CompanyName`, so the refusal sentence and the write path
   * are assembled once rather than per row.
   *
   * Mounted UNCONDITIONALLY and self-gating, like `InlineRegionsField` and the
   * three inline field components on the revenue documents: `{canUpdate && …}`
   * would hide the company's NAME from a `viewer`, and every authenticated role
   * may read this page.
   */
  const nameEdit: CompanyNameEdit = {
    denial: canUpdate ? null : denialReason(ability, 'update', 'companies'),
    saveShortName,
    saveBillingName,
  };

  const stageCellProps = (row: Company) => ({
    row,
    stages,
    // Disabled, not hidden. A select offering nothing but "Unclassified" is a
    // one-click way to wipe a stage that is on screen right next to it.
    disabled: stagesLoading || stagesFailed,
    saving: savingStageIds.includes(row.id),
    error: stageErrors[row.id],
    onChange: (target: Company, stageId: number | null) => void changeStage(target, stageId),
  });

  /*
   * One per-stage status cell (owner 2026-10-25). Editable only in the column of
   * the row's OWN stage, offering only that stage's statuses; every other
   * column reads `—`. The row's saving flag and error belong to its own-stage
   * column alone, and the edit runs the page's one `changeStageStatus` flow
   * (date / reason dialog, verified on `data.stageStatus?.id`).
   *
   * The columns exist only once both masters have loaded (`stageStatusColumns`),
   * so there is no loading or failed state to disable the picker for here.
   */
  const stageStatusColumnCell = (
    row: Company,
    column: StageStatusColumn,
    variant: 'table' | 'card' = 'table',
  ) => {
    const ownStage = row.stage?.id === column.stageId;
    return (
      <StageStatusColumnCell
        company={row}
        column={column}
        allStatuses={stageStatuses}
        canEdit={canUpdate}
        saving={
          ownStage && (savingStageStatusIds.includes(row.id) || savingStageIds.includes(row.id))
        }
        error={ownStage ? (stageStatusErrors[row.id] ?? null) : null}
        onChange={(target, stageStatusId) => void changeStageStatus(target, stageStatusId)}
        variant={variant}
      />
    );
  };

  const sourceCellProps = (row: Company) => ({
    row,
    sources,
    // Disabled, not hidden — the same reasoning as the stage select. A select
    // offering nothing but "Not recorded" is a one-click way to wipe a source
    // that is on screen right next to it, and `companySourceOptions` keeps the
    // stored value in the list either way.
    disabled: sourcesLoading || sourcesFailed,
    saving: savingSourceIds.includes(row.id),
    error: sourceErrors[row.id],
    onChange: (target: Company, sourceId: number | null) => void changeSource(target, sourceId),
  });

  const companyTypeCellProps = (row: Company) => ({
    row,
    companyTypes,
    // Disabled, not hidden — the same reasoning as the source select beside it.
    // A select offering nothing but "Not recorded" is a one-click way to wipe a
    // type that is on screen right next to it, and `companyTypeOptions` keeps
    // the stored value in the list either way.
    disabled: companyTypesLoading || companyTypesFailed,
    saving: savingCompanyTypeIds.includes(row.id),
    error: companyTypeErrors[row.id],
    onChange: (target: Company, companyTypeId: number | null) =>
      void changeCompanyType(target, companyTypeId),
  });

  const staffCellProps = (row: Company, column: StaffColumn) => ({
    row,
    column,
    // The group's members, not the whole directory. The row's own holder is
    // injected back by `staffOptions` whether or not they are in the group.
    users: staffByColumn[column].users,
    outsideGroup: outsideGroupMarker(staffByColumn[column])?.name ?? null,
    // Disabled, not hidden — same reasoning as the stage select, and it
    // matters more here: a failed `/users` fetch leaves a picker whose only
    // option is "Unassigned", one click from clearing the name beside it.
    disabled: usersLoading || usersFailed,
    saving: (column === 'accountLead' ? savingLeadIds : savingCsmIds).includes(row.id),
    error: (column === 'accountLead' ? leadErrors : csmErrors)[row.id],
    onChange: (target: Company, key: StaffColumn, userId: number | null) =>
      requestStaffChange(target, key, userId),
  });

  const countryCellProps = (row: Company) => ({
    row,
    countries,
    // Disabled, not hidden — and `countryOptions` keeps the stored value in the
    // list either way, so this never becomes a one-click clear of a value the
    // reader can see.
    // Not usable yet: nothing read (or nothing to read), or the read failed.
    // See the note on the fetch for why there is no separate loading flag.
    disabled: countries.length === 0 || countriesFailed,
    saving: savingCountryIds.includes(row.id),
    error: countryErrors[row.id],
    onChange: (target: Company, next: number | null) => void changeCountry(target, next),
  });

  /**
   * The regions editor for one row.
   *
   * ⚠️ **The retired-region trap is closed here, and needs all three together:**
   *
   *  1. `regionOptions` INJECTS every stored id the active master no longer
   *     lists, so a retired region has a chip — otherwise the next unrelated
   *     save writes a set without it, a **200** silently destroying it.
   *  2. `InlineRegionsField` seeds its draft from the stored ids, so the
   *     retired id stays in the array unless someone removes that chip.
   *  3. `retiredRegionNames` says so BEFORE a save, since `regionIds` is
   *     validated as a set and one dead member refuses the lot.
   */
  const regionsField = (row: Company) => {
    const retired = retiredRegionNames(regionRecords, row.regions);
    return (
      <InlineRegionsField
        label={`Regions for ${row.name}`}
        values={row.regions.map((region) => String(region.id))}
        options={regionPickerOptions(regionRecords, row.regions)}
        optionsLoading={regionsLoading}
        disabled={!canUpdate}
        emptyLabel="No regions"
        placeholder={regionsLoading ? 'Loading regions…' : 'No regions'}
        searchPlaceholder="Search regions…"
        {...(regionsFailed
          ? { footnote: 'Region list unavailable. Reload to try again.' }
          : {})}
        {...(canUpdate && retired.length > 0
          ? {
              note: `${joinList(retired)} ${
                retired.length === 1 ? 'is' : 'are'
              } retired — remove ${retired.length === 1 ? 'it' : 'them'} to save this field.`,
            }
          : {})}
        onSave={(next) => saveRegions(row, next)}
      />
    );
  };

  /**
   * The projects-of-interest editor for one row — the same component, the same
   * contract and the same whole-set replace as Regions above, because it is the
   * same shape of value. Three differences, each deliberate:
   *
   *  1. **It COLLAPSES to a summary** (`display`), where Regions lists every
   *     chip. A company holds two to four regions and all of them are the
   *     answer; the projects list is open-ended and long-named, so an
   *     uncollapsed list turns one table row into a paragraph and stops the
   *     list being scannable. The full set is on the cell's `title`, and the
   *     EDITOR is still the whole multi-select — a collapsed display can never
   *     become a collapsed write.
   *  2. **No retired-value `note`.** Regions needs one because `regionIds` is
   *     validated as a set and one dead member refuses the lot. The injection
   *     of a stored value that the offered list no longer holds belongs to
   *     `projectOfInterestOptions`, which owns the cycle narrowing — this page
   *     must not second-guess it, and pinning the stored project is exactly
   *     what stops a narrowed picker rendering empty over a real value.
   *  3. **The truncation footnote**, because `/projects` caps at 100 and there
   *     are ~97 live. That is a fact about the data, not advice — the one
   *     footnote kind the no-helper-text rule keeps.
   *  4. **It holds state**, which is why the JSX is in `ProjectsOfInterestCell`
   *     rather than here: the Current/Previous pills are PER ROW, and a
   *     `useState` in this body would be one scope shared by every picker on
   *     the page. Read that component before changing anything about the pills.
   */
  const projectsField = (row: Company) => (
    <ProjectsOfInterestCell
      row={row}
      projects={projectRecords}
      scopes={projectCycleScopes}
      loading={projectsLoading}
      disabled={!canUpdate}
      footnote={
        projectsFailed
          ? 'Project list unavailable. Reload to try again.'
          : moreProjects
            ? 'Not all projects are listed — this picker shows the first page of them.'
            : undefined
      }
      onSave={(next) => saveProjectsOfInterest(row, next)}
    />
  );

  /**
   * ONE cell renderer for the desktop table body, keyed the same way the header
   * and the skeleton are — so a column that moves, hides or freezes moves in all
   * three at once. ⚠️ *SUPERSEDED: six `<TD>`s hand-written in source order*,
   * which is the arrangement that made reordering inexpressible.
   *
   * The actions lane is NOT here: it is the one cell that needs `numeric` and
   * `pinned` on the `<TD>` itself, so it stays at the call site where those
   * props live, and this switch is exhaustive over everything else.
   *
   * The per-column notes that stood beside the old cells and are still true:
   * Country sits beside the name because that is where the reader used to find
   * the city, and `null` reads as "Not recorded" — the honest reading, since
   * nobody has set one. Country, Stage and the two person columns each fall back
   * to a read-only cell for anyone who may not write companies. Regions is
   * edited in place and takes `disabled` itself, so it has no read-only branch
   * to pair with.
   */
  const bodyCellFor = (key: CompanyDataColumnKey, row: Company): ReactNode => {
    if (isStageStatusColumnKey(key)) {
      const column = stageStatusColumnById.get(key);
      return column ? stageStatusColumnCell(row, column) : null;
    }
    switch (key) {
      case 'name':
        // Approval is shown to every reader, not gated on update rights;
        // approved companies show nothing.
        return (
          <div className="min-w-0">
            <CompanyName row={row} edit={nameEdit} />
            {(row.approvalStatus === 'pending' || row.approvalStatus === 'rejected') && (
              <div className="mt-1">
                <ApprovalStatusBadge status={row.approvalStatus} />
              </div>
            )}
          </div>
        );
      // Self-gating like `CompanyName`: a reader who may not write gets the
      // value and the refusal, not an absent cell.
      case 'billingName':
        return <CompanyBillingName row={row} edit={nameEdit} />;
      case 'country':
        return canUpdate ? (
          <CountryCell {...countryCellProps(row)} />
        ) : (
          <CountryValue country={countryRowLabel(row)} />
        );
      case 'stage':
        return canUpdate ? <StageCell {...stageCellProps(row)} /> : <StageValue stage={row.stage} />;
      case 'followUpOn':
        return <FollowUpOnValue value={row.followUpOn} />;
      case 'source':
        return canUpdate ? (
          <SourceCell {...sourceCellProps(row)} />
        ) : (
          <SourceValue source={row.source} />
        );
      case 'companyType':
        return canUpdate ? (
          <CompanyTypeCell {...companyTypeCellProps(row)} />
        ) : (
          <CompanyTypeValue companyType={row.companyType} />
        );
      case 'accountLead':
        return canUpdate ? (
          <StaffCell {...staffCellProps(row, 'accountLead')} />
        ) : (
          <OwnerCell owner={row.accountLead} />
        );
      case 'clientSuccessManager':
        return canUpdate ? (
          <StaffCell {...staffCellProps(row, 'clientSuccessManager')} />
        ) : (
          <OwnerCell owner={row.clientSuccessManager} />
        );
      case 'regions':
        return regionsField(row);
      // Edited in place and takes `disabled` itself, like Regions — so it has
      // no read-only branch to pair with, and a `viewer` reads the same summary
      // a writer does.
      case 'projectOfInterest':
        return projectsField(row);
      // Read-only, from the `purchaseHistory` embed. `null` (not reported) and
      // `[]` (nothing bought) render differently — see `PurchaseHistoryCell`.
      // The highlighted cycle is the column's own cycle filter (`[]` when unset).
      case 'productsPurchased':
        return (
          <PurchaseHistoryCell
            history={row.purchaseHistory}
            kind="products"
            highlightCycleIds={purchasedCycleFilterIds}
            companyName={row.name}
          />
        );
      case 'projectsParticipated':
        return (
          <PurchaseHistoryCell
            history={row.purchaseHistory}
            kind="projects"
            highlightCycleIds={participatedCycleFilterIds}
            companyName={row.name}
          />
        );
      case 'creditBalance':
        return <CreditBalanceValue balances={row.creditBalances} />;
      // Read-only. `accountLastNote` is optional on the row: `undefined` is an
      // older server (an em dash, no claim), `null` is an account with no human
      // note — the cells keep those apart, so nothing here coalesces either.
      case 'lastNote':
        return (
          <OpenNotesButton companyName={row.name} onOpen={() => openActivity(row, 'notes')}>
            <AccountLastNoteCell note={row.accountLastNote} />
          </OpenNotesButton>
        );
      case 'lastNoteAt':
        return (
          <OpenNotesButton companyName={row.name} onOpen={() => openActivity(row, 'notes')}>
            <AccountLastNoteDateCell note={row.accountLastNote} />
          </OpenNotesButton>
        );
      // Read-only, three-state like the note pair. Both open the sheet on the
      // company, where all of the account's tasks and notes are.
      case 'lastTask':
        return (
          <OpenTasksButton companyName={row.name} onOpen={() => openActivity(row)}>
            <AccountLastTaskCell
              task={row.accountLastTask}
              viewerTimeZone={viewerZone(user?.timezone)}
            />
          </OpenTasksButton>
        );
      case 'lastTaskAt':
        return (
          <OpenTasksButton companyName={row.name} onOpen={() => openActivity(row)}>
            <AccountLastTaskDateCell task={row.accountLastTask} />
          </OpenTasksButton>
        );
    }
  };

  function openDelete(company: Company) {
    setDeleteError(null);
    setPendingDelete(company);
    /*
     * Reset FIRST, so a slow read for this account can never be shown against
     * the previous one — a stale figure here understates what is about to be
     * destroyed, which is the direction that costs somebody their history.
     *
     * ⚠️ Notes only. Tasks are still `ON DELETE RESTRICT` and REFUSE the delete
     * rather than being destroyed by it, so they belong in the server's refusal,
     * not in a sentence promising to delete them. See `company-delete.ts`.
     */
    setPendingNoteTotal(null);
    setPendingSystemNoteTotal(0);
    const seq = noteTotalSeq.current + 1;
    noteTotalSeq.current = seq;
    void api
      .noteSummary({ target: 'company', ids: String(company.id) })
      .then((notes) => {
        // Opened on another row meanwhile: that row's own read owns the figure.
        if (noteTotalSeq.current !== seq) return;
        const row = notes.data[0];
        setPendingNoteTotal(row?.count ?? 0);
        // ⚠️ `typeof`, NOT `?? 0` — an absent key and a real zero mean the same
        // thing here, and only the absent one would be a guess.
        setPendingSystemNoteTotal(typeof row?.systemCount === 'number' ? row.systemCount : 0);
      })
      .catch(() => {
        if (noteTotalSeq.current !== seq) return;
        setPendingNoteTotal(null);
        setPendingSystemNoteTotal(0);
      });
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCompany(pendingDelete.id);
      setPendingDelete(null);
      // Removing the only row on page 2+ would otherwise strand the user on an
      // empty final page; the page change refetches on its own.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the outcome the user wanted, so treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        // A 403 body carries no `details`, so this is a banner, never a field
        // error. `denialReason` returns null when the ability still believes
        // the delete was allowed — a role changed under the live token.
        setDeleteError(
          denialReason(ability, 'delete', 'companies') ??
            'Your account no longer has permission to delete companies.',
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved() {
    const wasCreate = !formState?.company;
    setFormState(null);
    // A new record lands at the top of the default sort, so send the user to
    // page 1 to see it rather than refreshing a page it isn't on.
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  /*
   * `countLabel` is GONE — owner's stricter helper-text ruling, 2026-09-05. It
   * built the panel's subtitle: "Loading…", or "{n} companies at stage {name}
   * matching “{term}”". Its only consumer was `panelDescription` below, which
   * went with it.
   *
   * ⚠️ **No count is lost from the screen.** The table footer still prints
   * "Showing {first}–{last} of {total}" and the page count beside it; those are
   * the pager's own values, not this sentence.
   */

  /*
   * What the BOARD was sent, and what it was not.
   *
   * Every server-scoped column filter is a real query param the board takes,
   * but their controls are header funnels, which board view does not render. An
   * applied filter with no visible control is exactly the "why is this empty?"
   * failure worth spelling out, so each gets a line. Everything else
   * `useTableFilters` holds — the page-scoped column filters and the sort — has
   * no board equivalent at all.
   */
  // Named from the columns rather than listed by hand, so a column that
  // starts or stops being carried to the board cannot drift out of this
  // sentence.
  //
  // ⚠️ Owner used to be excluded here BY NAME and had a banner sentence of its
  // own; both went on 2026-08-31 with the filter itself. No exclusion is needed
  // now — `ownerId` is not a `ServerFilterParam`, so no column can carry it.
  //
  // ⚠️ **Stage is excluded too, by the test rather than by name** —
  // `'stageId' in listFilters` is false since the hook doesn't hold its
  // value, the URL does.
  //
  // ⚠️ *SUPERSEDED 2026-09-05: "Stage has its OWN sentence in
  // `panelDescription`; naming it in both would say it twice."* That sentence is
  // gone with the stricter helper-text ruling, so stage is now mentioned
  // NOWHERE in board view. The exclusion still stands on its own terms — the
  // board DOES receive `stageId`, so listing it among the filters the board
  // cannot express would be a false statement — but the reason has changed from
  // "said elsewhere" to "not said at all".
  //
  // ⚠️ **AND COUNTRY IS EXCLUDED, BY NAME, BECAUSE THE BOARD 422s ON IT** —
  // which falsifies the paragraph above this block: *"Every server-scoped
  // column filter is a real query param the board takes"* was true of all four
  // until `countryId` shipped (2026-08-31). The companies kanban schema is
  // `.strict()` and per-board, so `boardParams` deliberately does not send it,
  // and listing it here would tell the reader a filter narrows this board when
  // it does not. It gets its own sentence below instead.
  const boardCarriedFilterLabels = columns
    .filter(
      (column) =>
        column.param !== undefined &&
        !BOARD_UNSUPPORTED_PARAMS.has(column.param) &&
        column.param in listFilters,
    )
    .map((column) => column.label);
  // The filters set on the table that the BOARD cannot express at all. Derived
  // the same way, from the columns, so a param moving between the two lists
  // cannot leave a stale sentence behind.
  //
  // ⚠️ A filtered table and an UNFILTERED board under one heading is the "it
  // looks like it worked" failure — the board would quietly show companies the
  // table had excluded. Saying so is the whole point of this line.
  const boardDroppedFilterLabels = columns
    .filter(
      (column) =>
        column.param !== undefined &&
        BOARD_UNSUPPORTED_PARAMS.has(column.param) &&
        column.param in listFilters,
    )
    .map((column) => column.label);
  /*
   * What is left once the filters the board DOES receive are taken out.
   *
   * ⚠️ `filters.activeCount`, NOT the page's `activeFilterCount`: that one adds
   * the URL's server-scoped selections, every one of which the board receives
   * except Country — and Country has its own sentence above. Counting them here
   * would report filters that reach the board as filters that do not.
   *
   * 🔴 **AND IT NO LONGER SUBTRACTS `Object.keys(listFilters).length`** —
   * *SUPERSEDED 2026-09-12, and leaving it would have printed a NEGATIVE count.*
   * That subtraction existed because the hook held the server columns'
   * selections, so `activeCount` double-counted what `listFilters` already
   * named. Every one of those now lives in the URL and is absent from
   * `activeCount`, so the hook holds nothing but page-scoped state: the
   * `name` column's selection, and the `emptiness` half of any column — which is
   * page-scoped everywhere here, since no endpoint exposes an is-null filter.
   * `activeCount` IS the page-scoped count now.
   */
  const pageScopedFilterCount = filters.activeCount;

  /*
   * `panelDescription` is GONE — owner's stricter helper-text ruling,
   * 2026-09-05. In board view it read "Headers count every match; up to 20 cards
   * shown." plus, when a stage was filtered, " Filtered to {name} — other
   * columns are empty."; in table view it was `countLabel`.
   *
   * ⚠️ **Both sentences were doing real work and the ACCEPTED COST should be
   * stated rather than buried**: a board column header counts every matching
   * record while the column renders at most `BOARD_CARDS_PER_COLUMN`, so a
   * header reading 47 over 20 cards now has nothing explaining it — and a
   * stage-filtered board is mostly empty columns, which reads as broken. The
   * `hasMore` footer inside each column is what is left of the first; the stage
   * funnel's own filled state is what is left of the second. Neither is a
   * replacement, and if the owner wants either fact back it is a sentence here,
   * not a new mechanism.
   */

  const newCompanyButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New company
    </Button>
  ) : null;

  /*
   * The live Google Sheet of ALL companies. `link` is null while loading, when
   * the mirror is not configured, and on any failed read — so the control is
   * simply absent then, never inert. Withheld from a reader who may not export,
   * because the sheet holds the same data an export would.
   */
  const companiesSheet = useTrackerSheetLink('companies');
  const canExportCompanies = ability.canExport('companies');
  const sheetLink = canExportCompanies && !companiesSheet.loading ? companiesSheet.link : null;

  /*
   * Labelled from `sm`, icon-only below it so the header row still fits at
   * 320px. The two copies sit in wrappers that carry the responsive hide —
   * `Button` bakes in its own display, so `hidden` on the button itself would
   * race the stylesheet rather than override it.
   */
  const exportButtons = canExportCompanies ? (
    <>
      <span className="sm:hidden">
        <Button
          variant="secondary"
          iconOnly
          aria-label="Export"
          leadingIcon={<DownloadIcon />}
          loading={exporting}
          onClick={() => void runExport()}
        />
      </span>
      <span className="hidden sm:inline-flex">
        <Button
          variant="secondary"
          leadingIcon={<DownloadIcon />}
          loading={exporting}
          onClick={() => void runExport()}
        >
          Export
        </Button>
      </span>
    </>
  ) : null;

  const sheetButtons =
    sheetLink !== null ? (
      <>
        <span className="sm:hidden">
          <Button
            variant="secondary"
            iconOnly
            aria-label="Open in Google Sheets"
            href={sheetLink}
            target="_blank"
            leadingIcon={<ExternalTabIcon />}
          />
        </span>
        <span className="hidden sm:inline-flex">
          <Button
            variant="secondary"
            href={sheetLink}
            target="_blank"
            trailingIcon={<ExternalTabIcon />}
          >
            Open in Google Sheets
            <span className="sr-only"> (opens in a new tab)</span>
          </Button>
        </span>
      </>
    ) : null;

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard —
   * every signed-in route is an ordinary `Shell` route, so a reader arriving by
   * URL must get a sentence rather than an error or a blank screen.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Records" title="Companies" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      {/* No `description` — the eyebrow, title and controls already say what
          the page is and how to use it. Errors, refusals and master-failure
          notices below stay. */}
      <PageHeader
        eyebrow="Records"
        title="Companies"
        actions={
          <>
            <ViewToggle view={view} onChange={setView} />
            {exportButtons}
            {sheetButtons}
            {newCompanyButton}
          </>
        }
      />

      {exportError && (
        <div className="mb-4">
          <Notice
            tone="danger"
            role="alert"
            title={<>The export didn&rsquo;t download.</>}
            action={
              <Button variant="secondary" size="sm" onClick={() => setExportError(null)}>
                Dismiss
              </Button>
            }
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">{exportError}</span>
          </Notice>
        </div>
      )}

      <Panel>
        <PanelHeader
          title="All companies"
          actions={
            // Stacked until `md`, not `sm`: `PanelHeader`'s actions slot is
            // `shrink-0`, so three controls side by side needed ~640px,
            // exactly the `sm` breakpoint, leaving the title nothing. Below
            // `md` they stack, which is what makes 320px work.
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
              {/* ⚠️ Not `md:hidden`, deliberately — a correctness fix, not a
                  layout preference. With no Created column and two of six
                  sorts having no header (`updatedAt`), hiding this above `md`
                  would leave "Newest first" (the page default) with no route
                  back to it after picking another sort — a dead end a user
                  can enter and not leave. Drives the same `useTableFilters`
                  state the Company header does. Board view drops it outright:
                  the board has no ordering parameter at all. */}
              <div
                className={cx('w-full md:w-40 lg:w-48', view === 'board' && 'hidden')}
              >
                {/* ✅ **A `Combobox`, not a `Select`, since 2026-09-13 —
                    owner's decision that every non-table-column FILTER dropdown
                    is searchable, regardless of how few options it has.** Six
                    rows do not need a filter box; a control that behaves
                    differently from the one beside it does need explaining, and
                    the consistency is the point rather than the search.
                    ⚠️ **The table-column funnels are NOT this mechanism** and
                    did not move: `FilterableTH` narrows a column and writes the
                    URL, this orders the whole list through `useTableFilters`.
                    ⚠️ **Options come from `sortSelectOptions`, never
                    `sortChoices` directly** — see that memo for why a narrowing
                    that can drop the ACTIVE value has to pin it back once the
                    control falls through to a placeholder. */}
                <Field label="Sort companies" hideLabel>
                  {({ id }) => (
                    <Combobox
                      id={id}
                      value={`${sortControlKey}:${order}`}
                      options={sortSelectOptions}
                      placeholder="Sort companies"
                      searchPlaceholder="Search sort orders…"
                      /* ⛔ **No `emptyOptionLabel`, deliberately.** The Country
                         cell above carries one because clearing a country is a
                         real choice the column is nullable for. There is no "no
                         sort" state here: `sort` and `order` are normalised to
                         a real pair before this renders, the list always comes
                         back in SOME order, and an empty row would be a choice
                         that means nothing — one that could only ever put `''`
                         through the `split(':')` below and drive a sort named
                         `undefined`. */
                      onChange={(next) => {
                        // Split on the LAST colon: a per-stage status column's
                        // key (`stageStatus:<stageId>`) carries one itself.
                        const cut = next.lastIndexOf(':');
                        if (cut <= 0) return;
                        applySortChoice(next.slice(0, cut), next.slice(cut + 1) as ListOrder);
                      }}
                    />
                  )}
                </Field>
              </div>
              {/* No standalone stage Select any more. Stage is a column funnel
                  like the other four, backed by the same `?stageId=` the URL
                  always carried — one mechanism, so the two can never disagree
                  about what the request is narrowed to. Below `md` the funnels
                  live in the FilterSheet, which is where it is reached there. */}
              <div className="w-full md:w-56 lg:w-64">
                <Field label="Search companies" hideLabel>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="search"
                      placeholder="Search name, email or city…"
                      leadingIcon={<SearchIcon />}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  )}
                </Field>
              </div>
              {/* ⚠️ **Hidden exactly where the TABLE is hidden, and nowhere
                  else.** Below `md` the rows are cards, which have no columns to
                  arrange — a control there would rearrange nothing. Board view
                  drops it for the same reason: its columns are the account
                  stages, and `KanbanBoard` carries its own show/hide.
                  ⚠️ The breakpoint is `md` and not `lg` deliberately — a feature
                  reachable only above a width is indistinguishable from one that
                  was removed, and `md` is where this table starts existing.
                  ⚠️ **The wrapper `<div>` is what carries the responsive hide.**
                  `hidden` and an inline-flex display utility are the same CSS
                  property and `Button` bakes one in, so passing the pair through
                  the trigger's own `className` would be racing the emitted
                  stylesheet's order rather than overriding anything. */}
              {view === 'table' && (
                <div className="hidden md:block">
                  <ColumnManager
                    layout={columnLayout}
                    labelOf={columnLabelOf}
                    lockedKeys={LOCKED_COLUMN_KEYS}
                    lockReasonOf={columnLockReasonOf}
                    pinnedKeys={PINNED_COLUMN_KEYS}
                  />
                </div>
              )}
            </div>
          }
        />

        {/* The live region that mirrored `panelDescription` went with it — it
            existed to announce that sentence to a screen reader, so with nothing
            to announce it would have been an empty region. The write-outcome
            region below is unrelated and stays. */}

        {/* A save is not a navigation and not a value change the select itself
            announces, so the outcome is spoken here. */}
        <p role="status" aria-live="polite" className="sr-only">
          {writeStatus}
        </p>

        {writeBanner && (
          <div className="border-b border-border px-4 py-2">
            <Notice
              tone="danger"
              role="alert"
              action={
                <Button variant="secondary" size="sm" onClick={() => setWriteBanner(null)}>
                  Dismiss
                </Button>
              }
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{writeBanner}</span>
            </Notice>
          </div>
        )}

        {/* Board view builds its columns from the board payload, not from this
            master, so a failed `/company-stages` fetch says nothing about it. */}
        {view === 'table' && stagesFailed && canUpdate && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="warning">
              Stage list unavailable &mdash; stages can&rsquo;t be changed here.
            </Notice>
          </div>
        )}

        {/* There is no faculty notice here any more — no Faculty column, no
            master fetch, so nothing to report. The regions field says its own
            on the field itself, via its `footnote`. */}

        {/* Same shape as the stage notice, for the two person columns. The wording says
            "couldn't be loaded" rather than "there are no staff", because the
            two are indistinguishable from the control and only one of them is
            ever true. */}
        {usersFailed && canUpdate && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="warning">
              Staff list unavailable &mdash; account lead and client success manager can&rsquo;t be
              changed here.
            </Notice>
          </div>
        )}

        {/* Same shape again, for the Country picker. "Couldn't be loaded" and
            not "there are no countries": the two are indistinguishable from a
            disabled control and only one of them has ever been true. Each
            company still shows what it holds — `countryOptions` injects the
            stored value whether or not the master was read. */}
        {countriesFailed && canUpdate && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="warning">
              Country list unavailable &mdash; countries can&rsquo;t be changed here.
            </Notice>
          </div>
        )}

        {/* "Pickers show the first 100 staff only." stood here and is GONE —
            owner's stricter helper-text ruling, 2026-09-05. It was a truncation
            notice, not a refusal.
            ⚠️ The three "list unavailable" banners ABOVE are a different species
            and stay: each sits over a control that is genuinely `disabled` when
            its flag is set (`usersLoading || usersFailed`, `countries.length ===
            0 || countriesFailed`), so each is a why-this-is-disabled sentence —
            an explicit keeper, and without it a permission denial, a derived
            value and a dead fetch are one greyed-out picker. */}

        {/* ⚠️ This is ENFORCEMENT, not a house convention: naming somebody
            outside the group is a 422 from `/companies`. Today each group holds
            ONE person, which reads as a broken dropdown unless something says
            otherwise.
            ⚠️ *SUPERSEDED 2026-09-13, the placement's REASON only: "Shown above
            the table since a native <select> has no footnote slot".* The two
            person pickers are `Combobox`-backed and `Combobox` takes a
            `footnote`, so no control here lacks the slot any more. **The
            sentence stays exactly where it is** — that pass converted the Sort
            and Cycle controls on the owner's instruction and nothing else, and
            where a notice belongs is a layout decision, not a side effect of a
            conversion. ⚠️ It is also NOT only a truncation notice: it explains a
            server refusal, so it would survive a helper-text sweep wherever it
            ends up.
            🔴 REMOVED 2026-10-02 on the owner's instruction ("remove the helper
            line"). The server's 422 still names the group when a non-member is
            picked, so the refusal is not lost — only the standing banner. */}

        {/* What the board does and does not take, said out loud — the funnels
            that set these live on the table headers, which board view does
            not render.
            ⚠️ `stageFilterIds.length > 0` is in the gate for a different
            reason: it is the CONTROL, not the sentence — the clear-all this
            block offers is the board's only route out of a stage filter, since
            a funnel exists on the table header alone.
            ⚠️ *SUPERSEDED 2026-09-05: "Stage's explanation lives in
            `panelDescription`."* That sentence went with the stricter
            helper-text ruling, so the board no longer explains WHY its columns
            are mostly empty under a stage filter — only offers the way out. */}
        {view === 'board' &&
          (pageScopedFilterCount > 0 ||
            Boolean(filters.sort) ||
            Boolean(search) ||
            stageFilterIds.length > 0 ||
            boardCarriedFilterLabels.length > 0 ||
            boardDroppedFilterLabels.length > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border bg-surface-sunken px-4 py-2">
              <div className="grid min-w-0 gap-1">
                {boardCarriedFilterLabels.length > 0 && (
                  <p className="m-0 text-xs text-fg-secondary">
                    {boardCarriedFilterLabels.join(', ')}{' '}
                    {boardCarriedFilterLabels.length === 1 ? 'filter' : 'filters'} from Table view{' '}
                    {boardCarriedFilterLabels.length === 1 ? 'narrows' : 'narrow'} this board.
                  </p>
                )}
                {/* ⚠️ The board does not accept these, so it is showing rows the
                    table excluded. Said plainly and pointed at the view that CAN
                    apply them — a filtered heading over an unfiltered board is
                    the failure this sentence exists to prevent. */}
                {boardDroppedFilterLabels.length > 0 && (
                  <p className="m-0 text-xs text-fg-secondary">
                    {boardDroppedFilterLabels.join(', ')}{' '}
                    {boardDroppedFilterLabels.length === 1 ? 'is' : 'are'} not applied here — this
                    board cannot filter on{' '}
                    {boardDroppedFilterLabels.length === 1 ? 'it' : 'them'}. Switch to Table.
                  </p>
                )}
                {search && (
                  <p className="m-0 text-xs text-fg-secondary">
                    &ldquo;{search}&rdquo; matches company name only here; Table also searches email
                    and city.
                  </p>
                )}
                {(pageScopedFilterCount > 0 || filters.sort) && (
                  <p className="m-0 text-xs text-fg-secondary">
                    {/* Plain "column filter" now: the alternative wording said
                        "OTHER column filter" to distinguish these from the owner
                        sentence that used to sit above, and that sentence went
                        with the owner filter on 2026-08-31. */}
                    {pageScopedFilterCount > 0
                      ? `${pluralise(pageScopedFilterCount, 'column filter')} ${
                          pageScopedFilterCount === 1 ? 'does' : 'do'
                        } not apply here. `
                      : ''}
                    {filters.sort
                      ? `The table’s sort doesn’t apply${
                          pageScopedFilterCount > 0 ? ' either' : ''
                        }. `
                      : ''}
                    Switch to Table to use {pageScopedFilterCount + (filters.sort ? 1 : 0) === 1 ? 'it' : 'them'}.
                  </p>
                )}
              </div>
              {activeFilterCount > 0 && (
                <Button variant="secondary" size="sm" onClick={clearAllFilters}>
                  Clear column filters
                </Button>
              )}
            </div>
          )}

        {view === 'table' && activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border bg-surface-sunken px-4 py-2">
            <div className="grid min-w-0 gap-1">
              <p className="m-0 text-xs text-fg-secondary">
                {pluralise(activeFilterCount, 'column filter')} applied.{' '}
                {hiddenByFilters > 0
                  ? `${hiddenByFilters} of the ${pluralise(rows.length, 'row')} loaded here are hidden.`
                  : 'Every loaded row still matches.'}
              </p>
              {/* ⚠️ **The funnel that set this is off screen, so the count above
                  cannot be acted on.** Naming the column is what turns "1 column
                  filter applied" into something a reader can do anything about —
                  either show the column again from Columns, or clear everything
                  here. See `hiddenFilteredColumnLabels`: a state the reader
                  cannot otherwise see, not a caption on a control. */}
              {hiddenFilteredColumnLabels.length > 0 && (
                <p className="m-0 text-xs text-fg-secondary">
                  {joinList(hiddenFilteredColumnLabels)}{' '}
                  {hiddenFilteredColumnLabels.length === 1
                    ? 'is filtered and its column is hidden.'
                    : 'are filtered and their columns are hidden.'}
                </p>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={clearAllFilters}>
              Clear column filters
            </Button>
          </div>
        )}

        {/* The list request's failure, not the board's — the board reports its
            own. Left on screen in board view it would blame a working board for
            a table load that failed before the switch. */}
        {view === 'table' && error && (
          <div className="border-b border-border px-4 py-2">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load companies.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {view === 'board' ? (
          // `min-w-0` so the board's own horizontal scroller is what scrolls —
          // a flex/grid child defaults to `min-width: auto` and would widen
          // the page instead, breaking 320px.
          <div className="min-w-0 px-4 py-3">
            <KanbanBoard
              board="companies"
              params={boardParams}
              hrefFor={(id) => `/companies/${id}`}
              // A viewer gets a 403 from the move endpoint; the note below says
              // why rather than letting a drag fail silently.
              canMove={canUpdate}
              // False keeps the board from fetching at all while the table is
              // the visible view; it is only mounted here, but the prop is the
              // contract and stating it keeps that true if this moves.
              enabled={view === 'board'}
              // The same "+" menu the table row carries, on the card — gated
              // identically: `createTargets` is already filtered per module
              // by `ability.canCreate`, one list, two mount points.
              // ⚠️ `card.title` is `string | null` on every board — the type
              // permits null even though this board's card is the company, so
              // an unnamed card is named by its id in the aria-label instead.
              cardAction={
                createTargets.length > 0
                  ? (card) => {
                      const company = {
                        id: card.id,
                        name: card.title ?? `Company #${card.id}`,
                      };
                      return (
                        <CreateForCompanyMenu
                          company={company}
                          targets={createTargets}
                          /*
                           * The same interception as both list mounts, and the
                           * reason the flow fetches: a card carries no
                           * `projectsOfInterest` to hand it.
                           */
                          onSelect={openProjectLines(company)}
                        />
                      );
                    }
                  : undefined
              }
            />
            {!canUpdate && moveDenial && (
              <p className="m-0 mt-3 text-xs text-fg-subtle">Cards can&rsquo;t be moved. {moveDenial}</p>
            )}
          </div>
        ) : (
        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below md a six-column table cannot fit 320px, so rows become
              cards — a different code path, with the same controls and the same
              state, not a narrower table. */}
          {!isDesktop && (
          <div className="md:hidden">
            {/* Cards have no headers, so the funnels have nowhere to live here.
                Same `headerFor` and `clearAll` as the desktop table — one filter
                store, so a filter set on a phone is still set on a laptop. */}
            <div className="border-b border-border px-4 py-2">
              <FilterSheet
                keys={filterKeys}
                headerFor={headerFor}
                activeCount={activeFilterCount}
                clearAll={clearAllFilters}
              />
            </div>
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              <CompaniesEmptyState
                search={search}
                onClear={clearSearch}
                hasError={Boolean(error)}
                action={newCompanyButton}
                columnFiltered={rows.length > 0}
                onClearFilters={clearAllFilters}
                stageLabel={stageFilterLabel}
                stageCount={stageFilterRefs.length}
                onClearStage={() => setStageFilter([])}
                countryCount={countryFilterCount}
                onClearCountry={clearCountryFilter}
              />
            ) : (
              <ul className="m-0 list-none p-0">
                {/* Spacers stand in for the cards outside the window, as in
                    the table below. */}
                <li ref={cardsTopSpacerRef} aria-hidden="true" style={{ height: cardsPadTop }} />
                {visibleRows.slice(cardsStart, cardsEnd).map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      {/* Editable here too: the card IS the list below `md`,
                          so read-only would make the feature desktop-only —
                          and the arrow rides in the same slot for the same
                          reason, since a control offered only above a
                          breakpoint is indistinguishable on a phone from one
                          that was never built. Mail is in the action row
                          below, matching the table's lane. */}
                      <CompanyName row={row} edit={nameEdit} />
                    </div>
                    {/* Each captioned — a card has no column header, and two
                        bare avatars side by side would be a guess. Editable
                        here too: the card IS the list under `md`, so
                        read-only would make the feature desktop-only. */}
                    <div className="mt-2 grid gap-1.5">
                      {/* ⚠️ The one card field that follows the column
                          layout: Billing name is opt-in (`OPT_IN_COLUMN_KEYS`),
                          so it shows here only for a reader who turned the
                          column on. */}
                      {columnLayout.isVisible('billingName') && (
                        <div className="grid gap-0.5">
                          <span className="text-2xs font-medium text-fg-muted">
                            {BILLING_NAME_LABEL}
                          </span>
                          <CompanyBillingName row={row} edit={nameEdit} />
                        </div>
                      )}
                      {/* ONE country caption now — the free-text "Billing
                          country" block that used to sit above this went with
                          its column. The card is the list below `md`, so the
                          country of record is editable here too: a column that
                          is read-only on a phone would be a desktop-only
                          feature. */}
                      <div className="grid gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          Country
                        </span>
                        {canUpdate ? (
                          <CountryCell {...countryCellProps(row)} />
                        ) : (
                          <CountryValue country={countryRowLabel(row)} />
                        )}
                      </div>
                      <div className="grid gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          Account lead
                        </span>
                        {canUpdate ? (
                          <StaffCell {...staffCellProps(row, 'accountLead')} />
                        ) : (
                          <OwnerCell owner={row.accountLead} />
                        )}
                      </div>
                      <div className="grid gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          Client success manager
                        </span>
                        {canUpdate ? (
                          <StaffCell {...staffCellProps(row, 'clientSuccessManager')} />
                        ) : (
                          <OwnerCell owner={row.clientSuccessManager} />
                        )}
                      </div>
                      {/* Same reasoning: captioned since a card has no column
                          header, editable here too since the card IS the list
                          below `md`. Its accessible name includes the company
                          — twenty fields all called "Regions" would be twenty
                          identical entries in a screen reader's list. */}
                      <div className="grid gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          Regions
                        </span>
                        {/* `InlineRegionsField` renders the same chips when
                            idle and takes `disabled` itself, so a reader sees
                            exactly what they saw before — no second branch. */}
                        {regionsField(row)}
                      </div>
                      {/* ⚠️ **Here as well as in the table, and that is not
                          optional.** The card IS the list below `md`, so a
                          field offered only in the desktop table would be
                          unreadable and uneditable on a phone — which is
                          indistinguishable from the feature having been
                          removed. Captioned like every other card field, since
                          a card has no column header to carry the meaning. */}
                      <div className="grid gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {PROJECTS_OF_INTEREST_LABEL}
                        </span>
                        {projectsField(row)}
                      </div>
                      {/* Here as well as in the table: the card IS the list
                          below `md` and is NOT driven by `COLUMN_KEYS`, so a
                          field only in the table would be gone on a phone.
                          Read-only; the full by-year breakdown is behind the
                          cell's own "View more". */}
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {PRODUCTS_PURCHASED_LABEL}
                        </span>
                        <PurchaseHistoryCell
                          history={row.purchaseHistory}
                          kind="products"
                          highlightCycleIds={purchasedCycleFilterIds}
                          companyName={row.name}
                        />
                      </div>
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {PROJECTS_PARTICIPATED_LABEL}
                        </span>
                        <PurchaseHistoryCell
                          history={row.purchaseHistory}
                          kind="projects"
                          highlightCycleIds={participatedCycleFilterIds}
                          companyName={row.name}
                        />
                      </div>
                      {/* Here as well as in the table: the card IS the list
                          below `md`. Read-only, one figure per currency. */}
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {CREDIT_BALANCE_LABEL}
                        </span>
                        <CreditBalanceValue balances={row.creditBalances} />
                      </div>
                      {/* Here as well as in the table: the card IS the list
                          below `md`, and this list is NOT driven by
                          `COLUMN_KEYS`. Two captioned fields, as the table has
                          two columns — the date is visible to every reader
                          where the text and author may not be. */}
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {ACCOUNT_LAST_NOTE_LABEL}
                        </span>
                        <OpenNotesButton
                          companyName={row.name}
                          onOpen={() => openActivity(row, 'notes')}
                        >
                          <AccountLastNoteCell note={row.accountLastNote} />
                        </OpenNotesButton>
                      </div>
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {ACCOUNT_LAST_NOTE_AT_LABEL}
                        </span>
                        <OpenNotesButton
                          companyName={row.name}
                          onOpen={() => openActivity(row, 'notes')}
                        >
                          <AccountLastNoteDateCell note={row.accountLastNote} />
                        </OpenNotesButton>
                      </div>
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {ACCOUNT_LAST_TASK_LABEL}
                        </span>
                        <OpenTasksButton companyName={row.name} onOpen={() => openActivity(row)}>
                          <AccountLastTaskCell
                            task={row.accountLastTask}
                            viewerTimeZone={viewerZone(user?.timezone)}
                          />
                        </OpenTasksButton>
                      </div>
                      <div className="grid min-w-0 gap-0.5">
                        <span className="text-2xs font-medium text-fg-muted">
                          {ACCOUNT_LAST_TASK_AT_LABEL}
                        </span>
                        <OpenTasksButton companyName={row.name} onOpen={() => openActivity(row)}>
                          <AccountLastTaskDateCell task={row.accountLastTask} />
                        </OpenTasksButton>
                      </div>
                    </div>
                    {/* A card has no column headers, so the caption carries the
                        meaning the table's TH carries. The control itself keeps
                        an accessible name that includes the company — twenty
                        selects all called "Stage" are twenty identical entries
                        in a screen reader's list. */}
                    <div className="mt-2.5 grid gap-1">
                      <span className="text-2xs font-medium text-fg-muted">
                        Stage
                      </span>
                      {canUpdate ? (
                        <StageCell {...stageCellProps(row)} />
                      ) : (
                        <StageValue stage={row.stage} />
                      )}
                    </div>
                    {/* The status of the company's OWN stage only, labelled
                        `{Stage} status` (owner 2026-10-25), directly under Stage
                        as in the column order — here as well as in the table,
                        because this list is NOT driven by `COLUMN_KEYS`. A
                        stage with no column (no active statuses) shows a held
                        status read-only, and nothing when there is none. */}
                    {(() => {
                      const ownColumn = stageStatusColumns.find(
                        (column) => column.stageId === row.stage?.id,
                      );
                      if (ownColumn) {
                        return (
                          <div className="mt-2.5 grid gap-1">
                            <span className="text-2xs font-medium text-fg-muted">
                              {ownColumn.label}
                            </span>
                            {stageStatusColumnCell(row, ownColumn, 'card')}
                          </div>
                        );
                      }
                      if (!row.stage || !row.stageStatus) return null;
                      return (
                        <div className="mt-2.5 grid gap-1">
                          <span className="text-2xs font-medium text-fg-muted">
                            {`${companyStageLabel(row.stage.id, row.stage.name)} status`}
                          </span>
                          <StageStatusValue status={row.stageStatus} />
                        </div>
                      );
                    })()}
                    {/* Opt-in like Billing name: shown only for a reader who
                        turned the column on. */}
                    {columnLayout.isVisible('followUpOn') && (
                      <div className="mt-2.5 grid gap-1">
                        <span className="text-2xs font-medium text-fg-muted">
                          {FOLLOW_UP_ON_LABEL}
                        </span>
                        <FollowUpOnValue value={row.followUpOn} />
                      </div>
                    )}
                    {/* ⚠️ **Here as well as in the table, and that is not
                        optional — this list is NOT driven by `COLUMN_KEYS`.**
                        The card IS the list below `md`, so a column added to
                        the table and not here would be readable and editable
                        only above a breakpoint, which on a phone is
                        indistinguishable from a feature that was never built.
                        Beside Stage for the same reason it sits beside Stage in
                        the column order. Captioned like every other card field,
                        since a card has no column header to carry the meaning;
                        the control keeps an accessible name naming the company,
                        or twenty selects called "Source" are twenty identical
                        entries in a screen reader's list. */}
                    <div className="mt-2.5 grid gap-1">
                      <span className="text-2xs font-medium text-fg-muted">
                        Source
                      </span>
                      {canUpdate ? (
                        <SourceCell {...sourceCellProps(row)} />
                      ) : (
                        <SourceValue source={row.source} />
                      )}
                    </div>
                    {/* ⚠️ **Here as well as in the table, and that is not
                        optional — this list is NOT driven by `COLUMN_KEYS`.**
                        A column added to the table and not here is readable and
                        editable only above a breakpoint, which on a phone is
                        indistinguishable from a feature that was never built.
                        Beside Source for the same reason it sits beside Source
                        in the column order, and captioned `Type` in full rather
                        than sharing Source's caption — the two are different
                        questions about the account and a card has no header row
                        to keep them apart. */}
                    <div className="mt-2.5 grid gap-1">
                      <span className="text-2xs font-medium text-fg-muted">
                        Type
                      </span>
                      {canUpdate ? (
                        <CompanyTypeCell {...companyTypeCellProps(row)} />
                      ) : (
                        <CompanyTypeValue companyType={row.companyType} />
                      )}
                    </div>
                    {/* ⚠️ Its OWN block, above the button row rather than inside
                        it. The card IS the list below `md`, so the action has to
                        be offered here — a desktop-only control would make it
                        unreachable on a phone — but it reports its outcome as
                        prose, and a multi-sentence result has no business being
                        right-aligned in a wrapping flex row beside two icons.
                        Captioned like every other field on the card, which has no
                        column header to carry the meaning. */}
                    {/* The badge is visible to every reader; only the resend
                        control is gated on update rights. */}
                    <div className="mt-2.5 grid gap-1">
                      <span className="text-2xs font-medium text-fg-muted">
                        Approval
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <ApprovalStatusBadge status={row.approvalStatus} />
                      </div>
                      {canResendApproval && (
                        <ResendApprovalControl company={row} variant="card" />
                      )}
                    </div>
                    {/* ⚠️ **Always rendered now, and the reason CHANGED on
                        2026-09-17 while the behaviour did not.** *SUPERSEDED,
                        earlier the same day: "this row carries the ONLY link to
                        the record from a phone since the arrow left the name
                        slot".* The arrow is back beside the name; what keeps
                        this group non-empty is the CONTACTS button, which is
                        ungated for the same reason. ⚠️ The gate is kept rather
                        than deleted: `actionCount` is the TABLE lane's count
                        and this row has never matched it (no activity button
                        here, and Resend has its own captioned block above), so
                        removing it would read as a claim that the two agree. */}
                    {actionCount > 0 && (
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                        {/* First, and labelled to match the secondary buttons
                            beside it rather than the icon lane on the desktop
                            table — the shape follows the neighbours, not the
                            viewport. ⚠️ **A real `Button`, where the control
                            that stood here for part of 2026-09-17 had to
                            restate `Button`'s own classes by hand**, because
                            that one was a router `<Link>` and this one is not.
                            Ungated for the same reason as the table's: reading
                            a company's contacts is open to every role; the
                            writes inside the manager are gated there. */}
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<UsersIcon />}
                          aria-label={`Contacts for ${row.name}`}
                          onClick={() => openContactsFor(row)}
                        >
                          Contacts
                          <span className="tabular-nums text-fg-muted">{row.contactCount}</span>
                        </Button>
                        {createTargets.length > 0 && (
                          <CreateForCompanyMenu
                            company={row}
                            targets={createTargets}
                            onSelect={openProjectLines(row)}
                          />
                        )}
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => setFormState({ company: row })}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => openDelete(row)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
                {cardsPadBottom > 0 && <li aria-hidden="true" style={{ height: cardsPadBottom }} />}
              </ul>
            )}
          </div>
          )}

          {isDesktop && (
          <div className="hidden md:block">
            {initialLoad ? (
              <TableWrap frozenHeader>
                {/* ⚠️ *SUPERSEDED: a hand-summed 1568px min-width class, written
                    twice, and then a spacer `<div>` carrying the computed floor
                    plus the head-row capture.* The floor is summed from the
                    columns actually on screen and goes straight on the table —
                    see `COLUMN_WIDTHS` for why it has to be an inline style. */}
                <Table style={{ minWidth: tableMinWidth }}>
                  <CompaniesTableHead
                    headerFor={headerFor}
                    actionCount={actionCount}
                    columnKeys={visibleColumnKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  <TableSkeleton
                    actionCount={actionCount}
                    columnKeys={visibleColumnKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </Table>
              </TableWrap>
            ) : visibleRows.length === 0 ? (
              <CompaniesEmptyState
                search={search}
                onClear={clearSearch}
                hasError={Boolean(error)}
                action={newCompanyButton}
                columnFiltered={rows.length > 0}
                onClearFilters={clearAllFilters}
                stageLabel={stageFilterLabel}
                stageCount={stageFilterRefs.length}
                onClearStage={() => setStageFilter([])}
                countryCount={countryFilterCount}
                onClearCountry={clearCountryFilter}
              />
            ) : (
              <TableWrap frozenHeader>
                {/* Same floor as the skeleton above, and literally the same
                    expression rather than a second copy of a hand-summed one.
                    Same head-row ref too: this branch and the skeleton are
                    different subtrees, so React points the ref at a different
                    `<tr>` on the swap and `useFrozenOffsets` — which compares the
                    live node against the one it is observing — re-measures. */}
                <Table style={{ minWidth: tableMinWidth }}>
                  <CompaniesTableHead
                    headerFor={headerFor}
                    actionCount={actionCount}
                    columnKeys={visibleColumnKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  <TBody>
                    {/* Spacers stand in for the rows outside the window so
                        the scrollbar keeps the full list's height. The top one
                        is always rendered — `useRowWindow` measures from it. */}
                    <tr ref={topSpacerRef} aria-hidden="true">
                      <td
                        colSpan={visibleColumnKeys.length}
                        style={{ height: windowPadTop, padding: 0, border: 0 }}
                      />
                    </tr>
                    {visibleRows.slice(windowStart, windowEnd).map((row) => (
                      <TR key={row.id} interactive>
                        {visibleColumnKeys.map((key) =>
                          key === ACTIONS_COLUMN_KEY ? (
                            <TD key={key} numeric pinned>
                              <div className="flex justify-end gap-1.5">
                                {/* ⚠️ **Owner's instruction, 2026-09-17: this
                                    slot is the MAIL button.** *SUPERSEDED,
                                    earlier the same day: "🔴 The ONLY link to
                                    the record from this view … it is FIRST in
                                    the group because the lane reads left to
                                    right as 'go there, then act here'".* The
                                    arrow went back beside the name in the same
                                    change, so nothing here is the only route to
                                    anything. It is still FIRST because Delete
                                    has to stay last and the destructive end of
                                    the lane is built from the right.

                                    ⚠️ **Ungated, and NOT on
                                    `ability.canUpdate`** — this opens a modal
                                    of the company's contacts and writes
                                    nothing; the addresses are read, and reading
                                    is open to every role. 2026-10-16: the mail
                                    button became the contacts manager (people
                                    icon + the row's `contactCount`); the email
                                    buttons are inside it, and its writes are
                                    gated there. Not `iconOnly`, because the
                                    count is a label. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leadingIcon={<UsersIcon />}
                                  tooltip="Contacts"
                                  aria-label={`Contacts for ${row.name}`}
                                  onClick={() => openContactsFor(row)}
                                >
                                  <span className="tabular-nums">{row.contactCount}</span>
                                </Button>
                                {/* Ungated: the task and note routers carry no
                                    role guard, so this is offered to everyone
                                    the row is offered to. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  leadingIcon={<ActivityIcon />}
                                  aria-label={`Tasks and notes for ${row.name}`}
                                  title={`Tasks and notes for ${row.name}`}
                                  onClick={() => openActivity(row)}
                                />
                                {createTargets.length > 0 && (
                                  <CreateForCompanyMenu
                                    company={row}
                                    targets={createTargets}
                                    onSelect={openProjectLines(row)}
                                  />
                                )}
                                {/* Per-row state, so one row sending never
                                    disables another — and a second press on
                                    the SAME row is impossible while its
                                    request is in flight, which matters because
                                    each press mails the whole approvers
                                    group. */}
                                {canResendApproval && (
                                  <ResendApprovalControl company={row} variant="row" />
                                )}
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    iconOnly
                                    leadingIcon={<PencilIcon />}
                                    aria-label={`Edit ${row.name}`}
                                    title={`Edit ${row.name}`}
                                    onClick={() => setFormState({ company: row })}
                                  />
                                )}
                                {canDelete && (
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    iconOnly
                                    leadingIcon={<TrashIcon />}
                                    aria-label={`Delete ${row.name}`}
                                    title={`Delete ${row.name}`}
                                    onClick={() => openDelete(row)}
                                  />
                                )}
                              </div>
                            </TD>
                          ) : (
                            <TD
                              key={key}
                              frozen={frozenSideOf(key)}
                              frozenOffset={frozenOffsets[key]}
                            >
                              {bodyCellFor(key, row)}
                            </TD>
                          ),
                        )}
                      </TR>
                    ))}
                    {windowPadBottom > 0 && (
                      <tr aria-hidden="true">
                        <td
                          colSpan={visibleColumnKeys.length}
                          style={{ height: windowPadBottom, padding: 0, border: 0 }}
                        />
                      </tr>
                    )}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
          )}
        </div>
        )}

        {view === 'table' && result && (
          // `ListPager` IS the `PanelFooter`. Column filters run over the
          // loaded rows, so whenever one is hiding some the server's range
          // would overstate what is on screen — the summary says so instead.
          <ListPager
            {...(hiddenByFilters > 0 && total > 0
              ? {
                  summary: showAll
                    ? `Showing ${visibleRows.length} of the ${rows.length} loaded · ${total} in total`
                    : `Showing ${visibleRows.length} of rows ${firstRow}–${lastRow} · ${total} in total`,
                }
              : {})}
            page={page}
            totalPages={totalPages}
            total={total}
            firstRow={firstRow}
            lastRow={lastRow}
            perPage={PER_PAGE}
            showAll={showAll}
            loading={loading}
            onPage={setPage}
            onToggleShowAll={setShowAll}
            noun="companies"
            truncated={showAll && showAllTruncated}
          />
        )}
      </Panel>

      {/* The Stage status follow-up / reason dialog — once per page, shared by
          the table cell and the cards. */}
      {stageStatusDialog}

      <CompanyFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        company={formState?.company}
        onSaved={handleSaved}
      />

      {/* One sheet for the whole table, not one per row — a row only says
          WHICH company it is for. `open` is derived from that, so closing
          keeps the company for the duration of the exit. */}
      <ActivityDrawer
        open={activityFor !== null}
        onClose={() => {
          setActivityFor(null);
          // A quiet refetch: `load()` keeps the current result on screen
          // (skeletons are only `loading && !result`) and reads the same
          // page, sort and filters. Board view has no list to refresh.
          if (activityChangedRef.current) {
            activityChangedRef.current = false;
            if (view !== 'board') void load();
          }
        }}
        onActivityChanged={() => {
          activityChangedRef.current = true;
        }}
        target={{ type: 'company', id: activityFor?.id ?? 0 }}
        recordLabel={activityFor?.name.trim() || `Company #${activityFor?.id ?? ''}`}
        {...(activityFocus ? { focus: activityFocus } : {})}
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
      />

      {/* ⛔ **ONE mount for the page, the same rule as the sheet above** — the
          row-level trigger only says WHICH company, and `null` is closed, so
          "which row" and "is it open" cannot come apart. Mounted OUTSIDE both
          list branches deliberately: it is reachable from the desktop lane and
          the phone card, and a mount inside either would unmount the dialog the
          moment the viewport crossed `md` with it open. */}
      <CompanyContactsManagerModal
        company={contactsFor}
        onClose={() => setContactsFor(null)}
        // The count is the row's own column, so it is patched in place rather
        // than re-reading the page for one number.
        onChanged={(companyId, contactCount) => patchRow(companyId, { contactCount })}
      />

      {/* ONE mount for the page: the inline Account lead cell (desktop and
          card) only names the change; `null` is closed. Cancel writes nothing. */}
      <AccountLeadChangeDialog
        open={leadChange !== null}
        companyName={leadChange?.row.name ?? ''}
        fromLead={leadChange?.row.accountLead ?? null}
        toLead={
          leadChange === null || leadChange.nextUserId === null
            ? null
            : (staffByColumn.accountLead.users.find((person) => person.id === leadChange.nextUserId) ??
              users.find((person) => person.id === leadChange.nextUserId) ??
              null)
        }
        onConfirm={(dateIso: string) => void confirmLeadChange(dateIso)}
        onClose={() => {
          if (leadChangeBusy) return;
          setLeadChange(null);
          setLeadChangeError(null);
        }}
        busy={leadChangeBusy}
        error={leadChangeError}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete company?'}
        description={
          <DeleteDescription
            company={pendingDelete}
            noteTotal={pendingNoteTotal}
            systemNoteTotal={pendingSystemNoteTotal}
          />
        }
        confirmLabel="Delete company"
        loading={deleting}
        error={deleteError}
      />

      {/* ONE flow for the page, mounted once — all three "+" mounts set the
          same request. `null` is closed, so there is no second `open` flag to
          disagree with it. */}
      <CompanyProjectLinesFlow
        request={projectLinesRequest}
        onClose={() => setProjectLinesRequest(null)}
        onSaved={() => {
          setProjectLinesRequest(null);
          // The list is the page's own state; a record raised elsewhere can
          // still move a row (stage, activity), so re-read rather than patch.
          void load();
        }}
      />
    </>
  );
}

/**
 * Deleting a company orphans its contacts rather than cascading, so the
 * consequence is spelled out in the backend's own wording. With no contacts
 * there is no consequence to state — "its 0 contacts" would be noise.
 *
 * ⚠️ THE NOTES ARE A SECOND, HEAVIER CONSEQUENCE AND IT IS NEW (backend
 * migration 111, 2026-08-30, on the owner's instruction). Contacts SURVIVE the
 * delete; notes do NOT, and nothing can restore them. Both sentences render, in
 * that order, because they say opposite things about two different things and
 * collapsing them would be read as covering both.
 *
 * ⚠️ NOT helper text, and it must survive any sweep of the no-helper-text rule:
 * `ConfirmDialog` body naming a destructive consequence is the rule's own
 * exception. `companyNotesConsequence` is the single copy of the wording.
 */
function DeleteDescription({
  company,
  noteTotal,
  systemNoteTotal,
}: {
  company: Company | null;
  noteTotal: number | null;
  /** Exact, and the same for every reader — unlike `noteTotal`, which is a floor. */
  systemNoteTotal: number;
}) {
  if (!company) return null;
  const count = company.contactCount;
  return (
    <>
      {count > 0 && (
        <>
          Its {count} {count === 1 ? 'contact' : 'contacts'} will be kept but unassigned.{' '}
        </>
      )}
      {/* Always rendered — the notes are destroyed whatever the count says, and
          the count is only ever a floor. See `company-delete.ts`. */}
      {companyNotesConsequence(noteTotal, systemNoteTotal)} If it has a Xero contact, that stays
      in Xero. This cannot be undone.
    </>
  );
}

function CompaniesEmptyState({
  search,
  onClear,
  hasError,
  action,
  columnFiltered,
  onClearFilters,
  stageLabel,
  stageCount,
  onClearStage,
  countryCount,
  onClearCountry,
}: {
  search: string;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
  /** True when the page did load rows and the column filters hid all of them. */
  columnFiltered: boolean;
  onClearFilters: () => void;
  /**
   * What the URL's stage filter narrows to — one stage's name, or a count above
   * that. Null when no stage is filtered. Never one of several names: printing
   * one as though it were the only one is a claim the reader would act on.
   */
  stageLabel: string | null;
  /** How many stages that label stands for, so the copy can agree with it. */
  stageCount: number;
  onClearStage: () => void;
  /** How many countries the funnel is narrowed to. `0` when it is not set. */
  countryCount: number;
  onClearCountry: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<BuildingIcon />}
        title="Nothing to show"
        description="The company list couldn’t be loaded. Try again."
      />
    );
  }

  if (columnFiltered) {
    return (
      <EmptyState
        icon={<BuildingIcon />}
        title="No rows on this page match the column filters"
        description="These filters cover this page only — a match may sit on another. Search to narrow the whole list."
        action={
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  /*
   * ✅ **COUNTRIES ARE POPULATED NOW, so this branch is an ORDINARY empty
   * result — corrected 2026-08-31.**
   *
   * *SUPERSEDED, both the heading and the copy under it: "🔴 A COUNTRY FILTER
   * RETURNS ZERO FOR EVERY COMPANY TODAY … all 148 companies read
   * `country: null` … 'No company has a country recorded yet, so this filter
   * matches nothing at all today. It is separate from the billing country in a
   * company's address, which is not filterable.'"* Measured 2026-08-31: 111 of
   * 149 companies carry a country of record, so the filter matches real rows
   * and the sentence promising it could not was **false in the discouraging
   * direction** — it told a reader their filter was broken by design.
   *
   * ⚠️ **The sentence it leant on ALSO lost its subject.** There is no longer a
   * billing country column to be "separate from": the two were merged on
   * 2026-08-31, and the surviving column is the one this filter narrows. A
   * sentence distinguishing a field from one that no longer exists sends
   * somebody hunting for it.
   *
   * ⚠️ **`?countryId=` still cannot express "no country recorded"** —
   * `country_id IN (...)` is UNKNOWN for a NULL, so the 38 companies with none
   * match no value of this filter. That is worth saying, and it is now the only
   * thing here worth saying: it is the reason a reader's own account can be
   * missing from a country they know it belongs to.
   *
   * ⚠️ This is an `EmptyState` description saying what to do next, which is the
   * no-helper-text rule's own structural exception — it names a state the
   * reader cannot otherwise see. Do not sweep it.
   *
   * ⚠️ **CHECKED BEFORE THE STAGE BRANCH, and the reason for the ORDER is now
   * spent.** It led because a country filter emptied the list unconditionally
   * and stage did not; neither dominates any more. The order is left alone
   * rather than reversed on a guess — swapping it would change which filter a
   * reader is sent to clear, which is a behaviour change and not a tidy-up.
   */
  if (countryCount > 0) {
    const one = countryCount === 1;
    return (
      <EmptyState
        icon={<BuildingIcon />}
        title={one ? 'No companies in that country' : 'No companies in those countries'}
        description={
          search
            ? `Nothing matching “${search}” is in ${one ? 'that country' : 'those countries'}. A company with no country recorded matches no country filter, so it will not appear here either.`
            : 'A company with no country recorded matches no country filter, so it will not appear here either.'
        }
        action={
          <Button variant="secondary" size="sm" onClick={onClearCountry}>
            {one ? 'Show all countries' : 'Clear country filter'}
          </Button>
        }
      />
    );
  }

  // The stage filter is a server query over the whole table, so an empty
  // result is a fact about the book of business, not about this page.
  if (stageLabel) {
    const one = stageCount === 1;
    return (
      <EmptyState
        icon={<BuildingIcon />}
        title={one ? `No companies at stage ${stageLabel}` : `No companies across ${stageLabel}`}
        description={
          search
            ? `Nothing matching “${search}” sits at ${one ? 'this stage' : 'these stages'}.`
            : `Nothing classified into ${one ? 'this stage' : 'these stages'} yet. Unclassified companies don’t appear here.`
        }
        action={
          <Button variant="secondary" size="sm" onClick={onClearStage}>
            Show all stages
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<BuildingIcon />}
      title={search ? 'No companies match that search' : 'No companies yet'}
      action={
        search ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search
          </Button>
        ) : (
          action
        )
      }
    />
  );
}
